import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Store, ArrowRight, Settings, Package, ShoppingCart, TrendingUp, 
  Edit, Edit2, Trash2, Plus, Eye, EyeOff, Clock, CheckCircle, XCircle, Loader2,
  BarChart3, Users, DollarSign, ArrowLeft, Building2, Hash, Car, 
  Wrench, MapPin, Phone, Home, Tag, Image, X, Upload, Palette,
  Layout, Type, Monitor, Smartphone, Save, MessageSquare, FileSpreadsheet,
  Ban, Archive, RotateCcw, Send, Sparkles, UserCircle, Video, 
  Info, Percent, Weight, PackageCheck, RefreshCw, Calendar, Bold, 
  Italic, Underline, Link as LinkIcon, List, ListOrdered, AlignLeft, AlignRight,
  MoreVertical, ArrowUpRight, Wand2, ChevronLeft, Sun, Crop, Eraser, Star, Languages,
  Search, LayoutGrid, Rows3, ArrowDownUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useLanguage } from '../context/LanguageContext';
import { useRegion } from '../context/RegionContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import AddPropertyPage from '../components/AddPropertyPage';
import AddCarPage from '../components/AddCarPage';
import AddProductPage from '../components/AddProductPage';
import AddPhonePage from '../components/AddPhonePage';
import AddPlatePage from '../components/AddPlatePage';
import UAEPlate, { hasPlateDesign } from '../components/plates/UAEPlate';
import GoogleMapPicker from '../components/GoogleMapPicker';

// Refactored imports from store-dashboard components
import { 
  storeTypeConfigs, 
  telecomLogos 
} from '../components/store-dashboard/storeTypeConfigs';
import { 
  UAEPlatePreview, 
  getAvailablePlateCodes, 
  validatePlateCodeCompatibility 
} from '../components/store-dashboard/UAEPlatePreview';
import { OrdersTab } from '../components/store-dashboard/OrdersTab';
import { ReviewsTab } from '../components/store-dashboard/ReviewsTab';
import { FinancialMovementTab } from '../components/store-dashboard/FinancialMovementTab';
import { RealEstateItemCard } from '../components/store-dashboard/ItemCards';
import { applyMask } from '../utils/numberMask';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// CGM AI Agent Icon Component
const CGMAIIcon = ({ className = "h-5 w-5" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_cloudgm-hub-1/artifacts/7nfnq4aq_freepik__adjust__56194.png" 
    alt="CGM AI" 
    className={`${className} object-contain`}
  />
);

// storeTypeConfigs is now imported from '../components/store-dashboard/storeTypeConfigs'
// Keeping this comment for reference - original definition was here (lines 40-413)

// UAEPlatePreview, getAvailablePlateCodes, validatePlateCodeCompatibility 
// are now imported from '../components/store-dashboard/UAEPlatePreview'
// Original definitions were here (lines 416-746)

// Note: The original inline definitions have been moved to separate files for better maintainability
// This reduces the file size by approximately 700 lines


export default function StoreDashboard() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { formatPrice, currency } = useRegion();
  const { isDarkMode } = useTheme();
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditModal, setShowEditModal] = useState(false);
  // iter-227: translation state — 'ar-en' or 'en-ar' while a request is in flight, else null
  const [translatingDesc, setTranslatingDesc] = useState(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [editData, setEditData] = useState({ name: '', nameEn: '', description: '', descriptionEn: '', contact_phone: '', contact_whatsapp: '', contact_email: '' });
  // Contact info inline edit state (own separate section in Settings)
  const [editingContact, setEditingContact] = useState(false);
  const [contactDraft, setContactDraft] = useState({ contact_phone: '', contact_whatsapp: '', contact_email: '' });
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  // Product list view controls (grid vs list) + search
  const [productsViewMode, setProductsViewMode] = useState('grid'); // 'grid' | 'list'
  const [productsSearch, setProductsSearch] = useState('');
  const [newItem, setNewItem] = useState({});
  const [itemImage, setItemImage] = useState(null);
  const [itemImagePreview, setItemImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  
  // Item form state
  const [itemName, setItemName] = useState('');
  const [itemNameEn, setItemNameEn] = useState('');
  const [itemPrice, setItemPrice] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemQuantity, setItemQuantity] = useState('');
  
  // Multiple images state for auto/vehicles
  const [vehicleImages, setVehicleImages] = useState([null, null, null, null, null]); // [main, extra1, extra2, extra3, extra4]
  const [vehicleImagePreviews, setVehicleImagePreviews] = useState([null, null, null, null, null]);
  const vehicleImageRef0 = useRef(null);
  const vehicleImageRef1 = useRef(null);
  const vehicleImageRef2 = useRef(null);
  const vehicleImageRef3 = useRef(null);
  const vehicleImageRef4 = useRef(null);
  const vehicleImageRefs = [vehicleImageRef0, vehicleImageRef1, vehicleImageRef2, vehicleImageRef3, vehicleImageRef4];
  
  // Theme customization state
  const [storeTheme, setStoreTheme] = useState({
    primaryColor: '#1E9BD9',
    secondaryColor: '#F5A962',
    backgroundColor: '#FFFFFF',
    textColor: '#0F172A',
    cardStyle: 'rounded', // rounded, sharp, soft
    layout: 'grid', // grid, list
    fontStyle: 'modern' // modern, classic, arabic
  });
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop, mobile
  const [savingTheme, setSavingTheme] = useState(false);
  
  // Logo and Banner state
  const [newLogo, setNewLogo] = useState(null);
  const [newLogoPreview, setNewLogoPreview] = useState(null);
  const [newBanner, setNewBanner] = useState(null);
  const [newBannerPreview, setNewBannerPreview] = useState(null);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const csvInputRef = useRef(null);

  // Negotiations state
  const [negotiations, setNegotiations] = useState([]);
  const [loadingNegotiations, setLoadingNegotiations] = useState(false);

  // Bulk upload state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadProgress, setBulkUploadProgress] = useState(0);
  const [bulkUploading, setBulkUploading] = useState(false);
  
  // Remove sold items state
  const [removingSold, setRemovingSold] = useState(false);

  // Archive state
  const [archivedItems, setArchivedItems] = useState([]);
  
  // Plate code validation state
  const [plateCodeError, setPlateCodeError] = useState('');
  const [loadingArchive, setLoadingArchive] = useState(false);

  // AI Assistant Chat state
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const chatMessagesRef = useRef(null);
  const [myStoreSubTab, setMyStoreSubTab] = useState('overview'); // 'overview' or 'assistant'

  // Team members state
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [newTeamMember, setNewTeamMember] = useState({ name: '', email: '', role: 'member' });
  const [loadingTeam, setLoadingTeam] = useState(false);

  // Phone number details modal state
  const [selectedPhoneItem, setSelectedPhoneItem] = useState(null);
  const [showPhoneDetailsModal, setShowPhoneDetailsModal] = useState(false);

  // Car details/edit modal state
  const [selectedCarItem, setSelectedCarItem] = useState(null);
  const [showCarDetailsModal, setShowCarDetailsModal] = useState(false);
  const [isEditingCar, setIsEditingCar] = useState(false);
  const [editCarData, setEditCarData] = useState({});

  // Real Estate details/edit modal state
  const [selectedRealEstateItem, setSelectedRealEstateItem] = useState(null);
  const [showRealEstateDetailsModal, setShowRealEstateDetailsModal] = useState(false);
  const [isEditingRealEstate, setIsEditingRealEstate] = useState(false);
  const [editRealEstateData, setEditRealEstateData] = useState({});

  // E-commerce Product details/edit modal state
  const [selectedProductItem, setSelectedProductItem] = useState(null);
  const [showProductDetailsModal, setShowProductDetailsModal] = useState(false);
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const [editProductData, setEditProductData] = useState({});
  const [editProductImages, setEditProductImages] = useState([]);
  const [savingProduct, setSavingProduct] = useState(false);

  // Full-page add forms state
  const [showAddPropertyPage, setShowAddPropertyPage] = useState(false);
  const [showAddCarPage, setShowAddCarPage] = useState(false);
  
  // Full-page edit forms state
  const [showEditPropertyPage, setShowEditPropertyPage] = useState(false);
  const [showEditCarPage, setShowEditCarPage] = useState(false);
  const [showAddProductPage, setShowAddProductPage] = useState(false);
  const [showEditProductPage, setShowEditProductPage] = useState(false);
  const [showAddPhonePage, setShowAddPhonePage] = useState(false);
  const [showAddPlatePage, setShowAddPlatePage] = useState(false);
  const [showEditPlatePage, setShowEditPlatePage] = useState(false);
  const [showEditPhonePage, setShowEditPhonePage] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Discount modal state (for all item types)
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountItem, setDiscountItem] = useState(null);
  const [discountType, setDiscountType] = useState('percentage'); // 'percentage' or 'fixed'
  const [discountPercentage, setDiscountPercentage] = useState('');
  const [discountNewPrice, setDiscountNewPrice] = useState('');
  const [discountError, setDiscountError] = useState('');
  const [savingDiscount, setSavingDiscount] = useState(false);

  // Phone number form state
  const [phonePrefix, setPhonePrefix] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneCategory, setPhoneCategory] = useState('');
  const [phonePrice, setPhonePrice] = useState('');
  const [phoneDescription, setPhoneDescription] = useState('');
  const [phoneNegotiable, setPhoneNegotiable] = useState(null); // true or false
  const [phonePaymentType, setPhonePaymentType] = useState(''); // 'prepaid' or 'postpaid'
  const [phoneContract, setPhoneContract] = useState(null); // true or false

  // Categories state (for e-commerce stores only)
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [productsSubTab, setProductsSubTab] = useState('list'); // 'list' or 'add'
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [productFilter, setProductFilter] = useState('all'); // 'all' or category id
  const [selectedFilterCategory, setSelectedFilterCategory] = useState(null); // For showing subcategories
  const [selectedFilterSubCategory, setSelectedFilterSubCategory] = useState('all'); // Subcategory filter

  // Advanced Product Form State (E-commerce)
  const [productImages, setProductImages] = useState([]); // Max 4 images: [{file, preview, isMain, enhanced}]
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const [pendingImage, setPendingImage] = useState(null); // Temporary image before confirmation
  const [isNewImage, setIsNewImage] = useState(false); // Track if editing new or existing image
  const [imageEnhancements, setImageEnhancements] = useState({ brightness: 100, contrast: 100, saturation: 100 });
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiProductInfo, setAiProductInfo] = useState(null); // {name, description, category}
  const [aiSuggestions, setAiSuggestions] = useState([]); // [{id, text, action}]
  const [applyingSuggestion, setApplyingSuggestion] = useState(null);
  const [productRibbon, setProductRibbon] = useState('');
  const [productOnSale, setProductOnSale] = useState(false);
  const [productSalePrice, setProductSalePrice] = useState('');
  const [productDiscountPercent, setProductDiscountPercent] = useState('');
  const [productCost, setProductCost] = useState('');
  const [productSKU, setProductSKU] = useState('');
  const [productWeight, setProductWeight] = useState('');
  const [productStatus, setProductStatus] = useState('in_stock');
  const [productTrackInventory, setProductTrackInventory] = useState(false);
  const [productPreOrder, setProductPreOrder] = useState(false);
  const [productShippingBy, setProductShippingBy] = useState('merchant');
  const [productShippingCostType, setProductShippingCostType] = useState('free');
  const [productShippingFee, setProductShippingFee] = useState('');
  const [productOptions, setProductOptions] = useState([]);
  const [showAddOptionModal, setShowAddOptionModal] = useState(false);
  const [selectedOptionType, setSelectedOptionType] = useState(null);
  const [customOptionValues, setCustomOptionValues] = useState('');
  const [newOptionName, setNewOptionName] = useState('');
  const [newOptionType, setNewOptionType] = useState('text');
  const [newOptionChoices, setNewOptionChoices] = useState('');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionName, setSubscriptionName] = useState('');
  const [subscriptionTagline, setSubscriptionTagline] = useState('');
  const [subscriptionRepeatValue, setSubscriptionRepeatValue] = useState('1');
  const [subscriptionRepeatUnit, setSubscriptionRepeatUnit] = useState('month');
  const [subscriptionExpiresAfter, setSubscriptionExpiresAfter] = useState('');
  const [subscriptionDiscount, setSubscriptionDiscount] = useState('');
  const [subscriptionDiscountType, setSubscriptionDiscountType] = useState('AED');
  const [productSubscription, setProductSubscription] = useState(null);
  const [additionalInfoSections, setAdditionalInfoSections] = useState([]);
  const productImagesInputRef = useRef(null);

  // Phone prefixes configuration with operators
  const phonePrefixes = [
    { value: '050', operator: 'etisalat', operatorName: 'اتصالات', operatorNameEn: 'Etisalat' },
    { value: '054', operator: 'etisalat', operatorName: 'اتصالات', operatorNameEn: 'Etisalat' },
    { value: '056', operator: 'etisalat', operatorName: 'اتصالات', operatorNameEn: 'Etisalat' },
    { value: '055', operator: 'du', operatorName: 'دو', operatorNameEn: 'Du' },
    { value: '052', operator: 'du', operatorName: 'دو', operatorNameEn: 'Du' },
    { value: '058', operator: 'du', operatorName: 'دو', operatorNameEn: 'Du' }
  ];

  // telecomLogos is now imported from '../components/store-dashboard/storeTypeConfigs'

  // Get current operator based on selected prefix
  const getCurrentOperator = () => {
    const prefix = phonePrefixes.find(p => p.value === phonePrefix);
    return prefix ? prefix.operator : null;
  };

  // Predefined color palettes
  const colorPalettes = [
    { name: 'كلاود جي إم', nameEn: 'CloudGM', primary: '#1E9BD9', secondary: '#F5A962' },
    { name: 'أخضر زمردي', nameEn: 'Emerald', primary: '#10B981', secondary: '#34D399' },
    { name: 'بنفسجي ملكي', nameEn: 'Royal Purple', primary: '#8B5CF6', secondary: '#A78BFA' },
    { name: 'أحمر أنيق', nameEn: 'Elegant Red', primary: '#EF4444', secondary: '#F87171' },
    { name: 'ذهبي فاخر', nameEn: 'Luxury Gold', primary: '#F59E0B', secondary: '#FBBF24' },
    { name: 'وردي عصري', nameEn: 'Modern Pink', primary: '#EC4899', secondary: '#F472B6' },
    { name: 'أزرق داكن', nameEn: 'Dark Blue', primary: '#1E3A8A', secondary: '#3B82F6' },
    { name: 'رمادي أنيق', nameEn: 'Elegant Gray', primary: '#475569', secondary: '#94A3B8' }
  ];

  useEffect(() => {
    fetchStoreDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  // Fetch negotiations when on negotiations tab
  useEffect(() => {
    if (activeTab === 'negotiations') {
      fetchNegotiations();
    }
    if (activeTab === 'archive') {
      fetchArchivedItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchNegotiations = async () => {
    setLoadingNegotiations(true);
    const token = localStorage.getItem('business_token');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/stores/${storeId}/negotiations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNegotiations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch negotiations:', error);
      setNegotiations([]);
    } finally {
      setLoadingNegotiations(false);
    }
  };

  const fetchStoreDetails = async () => {
    const token = localStorage.getItem('business_token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/stores/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Normalize store type field (API returns storeType, but frontend uses type)
      const storeData = {
        ...response.data,
        type: response.data.storeType || response.data.type
      };
      setStore(storeData);
      setEditData({
        name: storeData.name,
        nameEn: storeData.nameEn || '',
        description: storeData.description || '',
        descriptionEn: storeData.descriptionEn || '',
        contact_phone: storeData.contact_phone || '',
        contact_whatsapp: storeData.contact_whatsapp || '',
        contact_email: storeData.contact_email || ''
      });
      // Fetch items for this store
      fetchItems();
      
      // Fetch categories if e-commerce store
      console.log('Store type check:', { storeType: response.data.storeType, type: response.data.type });
      if (response.data.storeType === 'ecommerce' || response.data.type === 'ecommerce') {
        console.log('Fetching categories for e-commerce store...');
        fetchCategories();
      } else {
        console.log('Not an e-commerce store, skipping categories fetch');
      }
    } catch (error) {
      console.error('Failed to fetch store:', error);
      toast.error(language === 'ar' ? 'فشل في تحميل بيانات المتجر' : 'Failed to load store data');
      navigate('/stores');
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    const token = localStorage.getItem('business_token');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/stores/${storeId}/items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // المؤرشفة لها تبويب منفصل — لا تظهر في القائمة الرئيسية
      setItems((response.data || []).filter(i => i.status !== 'archived'));
    } catch (error) {
      console.error('Failed to fetch items:', error);
      setItems([]);
    }
  };

  const updateStore = async () => {
    const token = localStorage.getItem('business_token');
    setSaving(true);

    try {
      await axios.put(`${BACKEND_URL}/api/stores/${storeId}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStore({ ...store, ...editData });
      setShowEditModal(false);
      toast.success(language === 'ar' ? 'تم تحديث المتجر' : 'Store updated');
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل في تحديث المتجر' : 'Failed to update store');
    } finally {
      setSaving(false);
    }
  };

  const saveContactInfo = async () => {
    const token = localStorage.getItem('business_token');
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/stores/${storeId}`, contactDraft, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStore({ ...store, ...contactDraft });
      setEditingContact(false);
      toast.success(language === 'ar' ? 'تم حفظ بيانات التواصل' : 'Contact info saved');
    } catch (e) {
      toast.error(language === 'ar' ? 'فشل الحفظ' : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // iter-227: One-tap AR ↔ EN auto-translate for store description.
  // Uses the existing /api/translate endpoint (Gemini-backed).
  const translateStoreDescription = async (sourceLang, targetLang) => {
    const sourceText = sourceLang === 'ar' ? editData.description : editData.descriptionEn;
    if (!sourceText || !sourceText.trim()) return;
    const key = `${sourceLang}-${targetLang}`;
    setTranslatingDesc(key);
    try {
      const token = localStorage.getItem('business_token');
      const fd = new FormData();
      fd.append('text', sourceText);
      fd.append('source_lang', sourceLang);
      fd.append('target_lang', targetLang);
      const res = await axios.post(`${BACKEND_URL}/api/translate`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const translated = (res.data && res.data.translated) ? res.data.translated : '';
      if (!translated) {
        toast.error(language === 'ar' ? 'تعذّرت الترجمة' : 'Translation failed');
        return;
      }
      if (targetLang === 'en') setEditData(prev => ({ ...prev, descriptionEn: translated }));
      else setEditData(prev => ({ ...prev, description: translated }));
      toast.success(language === 'ar' ? 'تمت الترجمة' : 'Translated');
    } catch (err) {
      toast.error(language === 'ar' ? 'تعذّرت الترجمة' : 'Translation failed');
    } finally {
      setTranslatingDesc(null);
    }
  };

  const deleteStore = async () => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من حذف هذا المتجر؟ لا يمكن التراجع عن هذا الإجراء.' : 'Are you sure you want to delete this store? This action cannot be undone.')) {
      return;
    }

    const token = localStorage.getItem('business_token');
    try {
      await axios.delete(`${BACKEND_URL}/api/stores/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'ar' ? 'تم حذف المتجر' : 'Store deleted');
      navigate('/stores');
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل حذف المتجر' : 'Failed to delete store');
    }
  };

  // Fetch categories (for e-commerce stores only)
  const fetchCategories = async () => {
    console.log('fetchCategories called');
    setLoadingCategories(true);
    const token = localStorage.getItem('business_token');
    try {
      console.log('Fetching categories from API...');
      const response = await axios.get(`${BACKEND_URL}/api/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Categories API response:', response.data);
      // API returns { success: true, data: [...] } or direct array
      const categoriesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setCategories(categoriesData);
      console.log('Categories loaded:', categoriesData?.length, categoriesData);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(language === 'ar' ? 'حجم الصورة يجب أن يكون أقل من 5 ميجابايت' : 'Image size must be less than 5MB');
        return;
      }
      setItemImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setItemImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle multiple vehicle images
  const handleVehicleImageUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(language === 'ar' ? 'حجم الصورة يجب أن يكون أقل من 5 ميجابايت' : 'Image size must be less than 5MB');
        return;
      }
      const newImages = [...vehicleImages];
      newImages[index] = file;
      setVehicleImages(newImages);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const newPreviews = [...vehicleImagePreviews];
        newPreviews[index] = reader.result;
        setVehicleImagePreviews(newPreviews);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove vehicle image
  const removeVehicleImage = (index) => {
    const newImages = [...vehicleImages];
    newImages[index] = null;
    setVehicleImages(newImages);
    
    const newPreviews = [...vehicleImagePreviews];
    newPreviews[index] = null;
    setVehicleImagePreviews(newPreviews);
  };

  const addItem = async () => {
    const token = localStorage.getItem('business_token');
    
    // Validation for phone numbers
    if (store.type === 'phone_numbers' || store.storeType === 'phone_numbers') {
      if (!phonePrefix || !phoneNumber || phoneNumber.length < 7) {
        toast.error(language === 'ar' ? 'يرجى إدخال رقم الهاتف كاملاً' : 'Please enter complete phone number');
        return;
      }
      if (!phoneCategory) {
        toast.error(language === 'ar' ? 'يرجى اختيار الفئة' : 'Please select category');
        return;
      }
      if (!phonePaymentType) {
        toast.error(language === 'ar' ? 'يرجى اختيار نوع الفاتورة' : 'Please select invoice type');
        return;
      }
      if (phoneContract === null || phoneContract === undefined) {
        toast.error(language === 'ar' ? 'يرجى تحديد العقد' : 'Please select contract option');
        return;
      }
      if (phoneNegotiable === null || phoneNegotiable === undefined) {
        toast.error(language === 'ar' ? 'يرجى تحديد قابلية التفاوض' : 'Please select negotiable option');
        return;
      }
      if (!phonePrice || parseFloat(phonePrice) <= 0) {
        toast.error(language === 'ar' ? 'يرجى إدخال السعر' : 'Please enter price');
        return;
      }
    }
    
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append('storeId', storeId);
      formData.append('storeType', store.type || store.storeType);
      
      // Handle phone numbers custom form
      if (store.type === 'phone_numbers' || store.storeType === 'phone_numbers') {
        const fullNumber = `${phonePrefix}-${phoneNumber}`;
        const operator = getCurrentOperator();
        formData.append('number', fullNumber);
        formData.append('prefix', phonePrefix);
        formData.append('digits', phoneNumber);
        formData.append('operator', operator);
        formData.append('category', phoneCategory);
        formData.append('price', phonePrice);
        formData.append('description', phoneDescription);
        formData.append('negotiable', phoneNegotiable);
        formData.append('paymentType', phonePaymentType);
        formData.append('hasContract', phoneContract);
      } else if (store.type === 'ecommerce') {
        // Handle e-commerce products with categories
        formData.append('name', itemName);
        formData.append('nameEn', itemNameEn || '');
        formData.append('price', itemPrice);
        formData.append('description', itemDescription || '');
        formData.append('quantity', itemQuantity || '0');
        formData.append('category_id', selectedMainCategory);
        formData.append('subcategory_id', selectedSubCategory);
        
        // Get category names for display
        const mainCat = categories.find(c => c.id === selectedMainCategory);
        const subCat = mainCat?.subcategories?.find(s => s.id === selectedSubCategory);
        formData.append('category_name', language === 'ar' ? mainCat?.name_ar : mainCat?.name_en);
        formData.append('subcategory_name', language === 'ar' ? subCat?.name_ar : subCat?.name_en);
        
        // Handle product images (from new E-commerce form)
        if (productImages && productImages.length > 0) {
          // Use main image (based on mainImageIndex)
          const mainImg = productImages[mainImageIndex] || productImages[0];
          
          if (mainImg && mainImg.file) {
            formData.append('image', mainImg.file);
          }
          // Add extra images
          productImages.forEach((img, idx) => {
            if (img && img.file && idx !== mainImageIndex) {
              formData.append(`extraImage${idx}`, img.file);
            }
          });
        } else if (itemImage) {
          // Fallback to old single image method
          formData.append('image', itemImage);
        }
      } else {
        Object.keys(newItem).forEach(key => {
          formData.append(key, newItem[key]);
        });
        
        // Handle multiple images for auto/vehicles
        if (store.type === 'auto' && storeConfig.multipleImages) {
          vehicleImages.forEach((img, idx) => {
            if (img) {
              formData.append(idx === 0 ? 'mainImage' : `extraImage${idx}`, img);
            }
          });
        } else if (itemImage) {
          formData.append('image', itemImage);
        }
      }

      const response = await axios.post(`${BACKEND_URL}/api/stores/${storeId}/items`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setItems([...items, response.data]);
      setShowAddItemModal(false);
      setNewItem({});
      setItemImage(null);
      setItemImagePreview(null);
      
      // Reset e-commerce form
      if (store.type === 'ecommerce') {
        setItemName('');
        setItemNameEn('');
        setItemPrice('');
        setItemDescription('');
        setItemQuantity('');
        setSelectedMainCategory('');
        setSelectedSubCategory('');
        setProductImages([]); // Reset product images
        setMainImageIndex(0);
        setProductsSubTab('list');
      }
      
      // Reset phone number form
      setPhonePrefix('');
      setPhoneNumber('');
      setPhoneCategory('');
      setPhonePrice('');
      setPhoneDescription('');
      setPhoneNegotiable(null);
      setPhonePaymentType('');
      setPhoneContract(null);
      toast.success(language === 'ar' ? 'تمت الإضافة بنجاح' : 'Item added successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل في الإضافة' : 'Failed to add item'));
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت متأكد من الحذف؟' : 'Are you sure you want to delete?')) {
      return;
    }

    const token = localStorage.getItem('business_token');
    try {
      await axios.delete(`${BACKEND_URL}/api/stores/${storeId}/items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(items.filter(item => item.id !== itemId));
      toast.success(language === 'ar' ? 'تم الحذف' : 'Deleted successfully');
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل الحذف' : 'Failed to delete');
    }
  };

  // Mark item as sold
  const markAsSold = async (itemId) => {
    const token = localStorage.getItem('business_token');
    try {
      await axios.put(`${BACKEND_URL}/api/stores/${storeId}/items/${itemId}/sold`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(items.map(item => 
        item.id === itemId ? { ...item, status: 'sold' } : item
      ));
      toast.success(language === 'ar' ? 'تم تعليم الرقم كمباع' : 'Marked as sold');
      fetchStoreDetails();
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل في تعليم كمباع' : 'Failed to mark as sold');
    }
  };

  // Return sold item back to active (إعادة للبيع)
  const returnToSale = async (itemId) => {
    const token = localStorage.getItem('business_token');
    try {
      await axios.put(`${BACKEND_URL}/api/stores/${storeId}/items/${itemId}/return-to-sale`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(items.map(item => 
        item.id === itemId ? { ...item, status: 'active' } : item
      ));
      toast.success(language === 'ar' ? 'تم إعادة الرقم للبيع' : 'Returned to sale');
      fetchStoreDetails();
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل في إعادة للبيع' : 'Failed to return to sale');
    }
  };

  // Archive item (أرشفة)
  const archiveItem = async (itemId) => {
    const token = localStorage.getItem('business_token');
    try {
      await axios.put(`${BACKEND_URL}/api/stores/${storeId}/items/${itemId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(items.filter(item => item.id !== itemId));
      toast.success(language === 'ar' ? 'تم أرشفة الرقم' : 'Item archived');
      fetchStoreDetails();
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل في الأرشفة' : 'Failed to archive');
    }
  };

  // ========== Discount Functions ==========
  
  // Open discount modal
  const openDiscountModal = (item) => {
    setDiscountItem(item);
    setDiscountType('percentage');
    setDiscountPercentage('');
    setDiscountNewPrice('');
    setDiscountError('');
    setShowDiscountModal(true);
  };

  // Calculate discount based on type
  const calculateDiscount = (type, value) => {
    if (!discountItem || !value) {
      setDiscountError('');
      return;
    }

    const originalPrice = discountItem.price;
    const numValue = parseFloat(value);

    if (type === 'percentage') {
      // Calculate new price from percentage
      if (numValue <= 0 || numValue > 100) {
        setDiscountError(language === 'ar' ? 'النسبة يجب أن تكون بين 1 و 100' : 'Percentage must be between 1 and 100');
        return;
      }
      const newPrice = originalPrice - (originalPrice * numValue / 100);
      setDiscountNewPrice(Math.round(newPrice).toString());
      setDiscountError('');
    } else {
      // Calculate percentage from new price
      if (numValue >= originalPrice) {
        if (numValue === originalPrice) {
          setDiscountError(language === 'ar' ? 'هذا نفس المبلغ المعروض!' : 'This is the same as the original price!');
        } else {
          setDiscountError(language === 'ar' ? 'لا يمكن إدخال سعر أعلى من السعر الأصلي!' : 'Cannot enter a price higher than the original!');
        }
        return;
      }
      if (numValue <= 0) {
        setDiscountError(language === 'ar' ? 'السعر يجب أن يكون أكبر من صفر' : 'Price must be greater than zero');
        return;
      }
      const percentage = ((originalPrice - numValue) / originalPrice) * 100;
      setDiscountPercentage(Math.round(percentage).toString());
      setDiscountError('');
    }
  };

  // Handle percentage change
  const handleDiscountPercentageChange = (value) => {
    setDiscountPercentage(value);
    if (discountType === 'percentage') {
      calculateDiscount('percentage', value);
    }
  };

  // Handle new price change
  const handleDiscountNewPriceChange = (value) => {
    setDiscountNewPrice(value);
    if (discountType === 'fixed') {
      calculateDiscount('fixed', value);
    }
  };

  // Save discount
  const saveDiscount = async () => {
    if (!discountItem || discountError) return;

    const token = localStorage.getItem('business_token');
    setSavingDiscount(true);

    try {
      const originalPrice = discountItem.price;
      const salePrice = parseFloat(discountNewPrice);
      const discountPercent = parseFloat(discountPercentage);

      if (!salePrice || salePrice >= originalPrice || salePrice <= 0) {
        toast.error(language === 'ar' ? 'يرجى إدخال قيمة صحيحة' : 'Please enter a valid value');
        setSavingDiscount(false);
        return;
      }

      await axios.put(`${BACKEND_URL}/api/stores/${storeId}/items/${discountItem.id}/discount`, {
        salePrice: salePrice,
        discountPercentage: discountPercent,
        originalPrice: originalPrice,
        hasDiscount: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      setItems(items.map(item => 
        item.id === discountItem.id 
          ? { 
              ...item, 
              salePrice: salePrice, 
              discountPercentage: discountPercent,
              originalPrice: originalPrice,
              hasDiscount: true
            } 
          : item
      ));

      toast.success(language === 'ar' ? 'تم إضافة الخصم بنجاح!' : 'Discount added successfully!');
      setShowDiscountModal(false);
      setDiscountItem(null);
    } catch (error) {
      console.error('Error saving discount:', error);
      toast.error(language === 'ar' ? 'فشل في إضافة الخصم' : 'Failed to add discount');
    } finally {
      setSavingDiscount(false);
    }
  };

  // Remove discount
  const removeDiscount = async (itemId) => {
    const token = localStorage.getItem('business_token');
    try {
      await axios.put(`${BACKEND_URL}/api/stores/${storeId}/items/${itemId}/remove-discount`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setItems(items.map(item => 
        item.id === itemId 
          ? { 
              ...item, 
              salePrice: null, 
              discountPercentage: null,
              hasDiscount: false
            } 
          : item
      ));

      toast.success(language === 'ar' ? 'تم إزالة الخصم' : 'Discount removed');
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل في إزالة الخصم' : 'Failed to remove discount');
    }
  };

  // Restore item from archive (إعادة من الأرشيف)
  const restoreFromArchive = async (itemId) => {
    const token = localStorage.getItem('business_token');
    try {
      await axios.put(`${BACKEND_URL}/api/stores/${storeId}/items/${itemId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchivedItems(archivedItems.filter(item => item.id !== itemId));
      toast.success(language === 'ar' ? 'تم استعادة الرقم' : 'Item restored');
      fetchStoreDetails();
      fetchItems();
    } catch (error) {
      toast.error(language === 'ar' ? 'فشل في الاستعادة' : 'Failed to restore');
    }
  };

  // Fetch archived items
  const fetchArchivedItems = async () => {
    setLoadingArchive(true);
    const token = localStorage.getItem('business_token');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/stores/${storeId}/items/archived`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchivedItems(response.data || []);
    } catch (error) {
      console.error('Failed to fetch archived items:', error);
      setArchivedItems([]);
    } finally {
      setLoadingArchive(false);
    }
  };

  // AI Assistant Chat - Send message
  const sendAiMessage = async () => {
    if (!aiChatInput.trim() || aiChatLoading) return;
    
    const userMessage = aiChatInput.trim();
    setAiChatInput('');
    setAiChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setAiChatLoading(true);
    
    try {
      const token = localStorage.getItem('business_token');
      const response = await axios.post(`${BACKEND_URL}/api/ai/chat`, {
        message: userMessage,
        context: {
          store_name: store.name,
          store_type: store.type,
          items_count: items.length,
          store_id: storeId
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAiChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: response.data.response || response.data.message || 'عذراً، حدث خطأ في المعالجة'
      }]);
    } catch (error) {
      console.error('AI Chat error:', error);
      setAiChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: language === 'ar' 
          ? 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.' 
          : 'Sorry, an error occurred. Please try again.'
      }]);
    } finally {
      setAiChatLoading(false);
    }
  };

  // Scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [aiChatMessages]);

  // Bulk upload CSV/Excel
  const handleBulkUpload = async () => {
    if (!bulkUploadFile) return;
    
    setBulkUploading(true);
    setBulkUploadProgress(0);
    const token = localStorage.getItem('business_token');
    
    try {
      const formData = new FormData();
      formData.append('file', bulkUploadFile);
      formData.append('storeType', store.type);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/stores/${storeId}/items/bulk`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setBulkUploadProgress(progress);
          }
        }
      );
      
      toast.success(language === 'ar' 
        ? `تم إضافة ${response.data.added} رقم بنجاح` 
        : `Successfully added ${response.data.added} numbers`
      );
      setShowBulkUploadModal(false);
      setBulkUploadFile(null);
      fetchItems();
      fetchStoreDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل في رفع الملف' : 'Failed to upload file'));
    } finally {
      setBulkUploading(false);
      setBulkUploadProgress(0);
    }
  };

  const getStatusBadge = (status, verified) => {
    if (status === 'active' && verified) {
      return {
        icon: CheckCircle,
        text: language === 'ar' ? 'فعال' : 'Active',
        className: 'bg-green-100 text-green-700'
      };
    } else if (status === 'suspended') {
      return {
        icon: XCircle,
        text: language === 'ar' ? 'معلق' : 'Suspended',
        className: 'bg-red-100 text-red-700'
      };
    } else {
      return {
        icon: Clock,
        text: language === 'ar' ? 'قيد المراجعة' : 'Pending Review',
        className: 'bg-orange-100 text-orange-700'
      };
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center cgm-page`}>
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-[#38BDF8]/20 border-t-[#38BDF8] animate-spin" />
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className={`min-h-screen flex items-center justify-center cgm-page`}>
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl gradient-primary flex items-center justify-center">
            <Store className="h-10 w-10 text-white" />
          </div>
          <p className={`text-xl mb-4 ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>{language === 'ar' ? 'المتجر غير موجود' : 'Store not found'}</p>
          <Link to="/stores">
            <Button className="gradient-primary text-white">
              {language === 'ar' ? 'العودة للمتاجر' : 'Back to Stores'}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const storeConfig = storeTypeConfigs[store.type] || storeTypeConfigs.ecommerce;
  const statusBadge = getStatusBadge(store.status, store.verified);
  const StatusIcon = statusBadge.icon;
  const StoreIcon = storeConfig.icon;

  // Types that DON'T have orders (only ecommerce and spare_parts have orders)
  const typesWithoutOrders = ['phone_numbers', 'car_plates', 'auto', 'real_estate', 'numbers'];
  const showOrders = !typesWithoutOrders.includes(store.type) && !typesWithoutOrders.includes(store.storeType);

  const tabs = [
    { id: 'overview', label: language === 'ar' ? 'متجري' : 'My Store', icon: Store },
    { id: 'items', label: language === 'ar' ? storeConfig.itemsName : storeConfig.itemsNameEn, icon: storeConfig.icon },
    // Only show orders for ecommerce and spare_parts
    ...(showOrders ? [{ id: 'orders', label: language === 'ar' ? 'الطلبات' : 'Orders', icon: ShoppingCart }] : []),
    // iter-292 — Financial Movement: same visibility rule as Orders (only
    // store types that actually sell: ecommerce + spare_parts).
    ...(showOrders ? [{ id: 'financial', label: language === 'ar' ? 'الحركة المالية' : 'Financial Movement', icon: ArrowDownUp }] : []),
    { id: 'reviews', label: language === 'ar' ? 'التقييمات' : 'Reviews', icon: Star },
    { id: 'settings', label: language === 'ar' ? 'الإعدادات' : 'Settings', icon: Settings },
    { id: 'negotiations', label: language === 'ar' ? 'التفاوض' : 'Negotiations', icon: MessageSquare },
    { id: 'archive', label: language === 'ar' ? 'الأرشيف' : 'Archive', icon: Archive },
    { id: 'team', label: language === 'ar' ? 'فريق العمل' : 'Team', icon: Users }
  ];

  // Show full-page Add Property form
  if (showAddPropertyPage) {
    return (
      <AddPropertyPage
        store={store}
        language={language}
        isDarkMode={isDarkMode}
        onBack={() => setShowAddPropertyPage(false)}
        onSuccess={() => {
          fetchItems();
          setShowAddPropertyPage(false);
        }}
      />
    );
  }

  // Show full-page Add Car form
  if (showAddCarPage) {
    return (
      <AddCarPage
        store={store}
        language={language}
        isDarkMode={isDarkMode}
        onBack={() => setShowAddCarPage(false)}
        onSuccess={() => {
          fetchItems();
          setShowAddCarPage(false);
        }}
      />
    );
  }

  // Show full-page Edit Property form
  if (showEditPropertyPage && editingItem) {
    return (
      <AddPropertyPage
        store={store}
        language={language}
        isDarkMode={isDarkMode}
        editItem={editingItem}
        isEditMode={true}
        onBack={() => {
          setShowEditPropertyPage(false);
          setEditingItem(null);
        }}
        onSuccess={() => {
          fetchItems();
          setShowEditPropertyPage(false);
          setEditingItem(null);
        }}
      />
    );
  }

  // Show full-page Edit Car form
  if (showEditCarPage && editingItem) {
    return (
      <AddCarPage
        store={store}
        language={language}
        isDarkMode={isDarkMode}
        editItem={editingItem}
        isEditMode={true}
        onBack={() => {
          setShowEditCarPage(false);
          setEditingItem(null);
        }}
        onSuccess={() => {
          fetchItems();
          setShowEditCarPage(false);
          setEditingItem(null);
        }}
      />
    );
  }

  // Show full-page Add Product form (E-commerce)
  if (showAddProductPage) {
    return (
      <AddProductPage
        store={store}
        language={language}
        isDarkMode={isDarkMode}
        categories={categories}
        onBack={() => setShowAddProductPage(false)}
        onSuccess={() => {
          fetchItems();
          setShowAddProductPage(false);
        }}
      />
    );
  }

  // Show full-page Edit Product form (E-commerce)
  if (showEditProductPage && editingItem) {
    return (
      <AddProductPage
        store={store}
        language={language}
        isDarkMode={isDarkMode}
        categories={categories}
        editItem={editingItem}
        isEditMode={true}
        onBack={() => {
          setShowEditProductPage(false);
          setEditingItem(null);
        }}
        onSuccess={() => {
          fetchItems();
          setShowEditProductPage(false);
          setEditingItem(null);
        }}
      />
    );
  }

  // Show full-page Add Phone form
  if (showAddPhonePage) {
    return (
      <AddPhonePage
        store={store}
        language={language}
        isDarkMode={isDarkMode}
        onBack={() => setShowAddPhonePage(false)}
        onSuccess={() => {
          fetchItems();
          setShowAddPhonePage(false);
        }}
      />
    );
  }

  // Show full-page Edit Phone form
  if (showEditPhonePage && editingItem) {
    return (
      <AddPhonePage
        store={store}
        language={language}
        isDarkMode={isDarkMode}
        editItem={editingItem}
        isEditMode={true}
        onBack={() => {
          setShowEditPhonePage(false);
          setEditingItem(null);
        }}
        onSuccess={() => {
          fetchItems();
          setShowEditPhonePage(false);
          setEditingItem(null);
        }}
      />
    );
  }

  // Show full-page Add/Edit Plate form
  if (showAddPlatePage) {
    return (
      <AddPlatePage
        store={store}
        language={language}
        isDarkMode={isDarkMode}
        onBack={() => setShowAddPlatePage(false)}
        onSuccess={() => {
          fetchItems();
          setShowAddPlatePage(false);
        }}
      />
    );
  }

  if (showEditPlatePage && editingItem) {
    return (
      <AddPlatePage
        store={store}
        language={language}
        isDarkMode={isDarkMode}
        editItem={editingItem}
        onBack={() => {
          setShowEditPlatePage(false);
          setEditingItem(null);
        }}
        onSuccess={() => {
          fetchItems();
          setShowEditPlatePage(false);
          setEditingItem(null);
        }}
      />
    );
  }

  return (
    <div className={`min-h-screen cgm-page`}>
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className={`absolute top-20 start-10 w-[300px] h-[300px] rounded-full blur-[100px] ${isDarkMode ? 'bg-[#38BDF8]/10' : 'bg-[#38BDF8]/5'}`} />
        <div className={`absolute bottom-20 end-10 w-[250px] h-[250px] rounded-full blur-[80px] ${isDarkMode ? 'bg-[#F97316]/10' : 'bg-[#F97316]/5'}`} />
      </div>
      
      {/* Header — AI-Grade with store-type color */}
      <section className="relative overflow-hidden py-6 sm:py-8">
        {/* Gradient Background with store color */}
        <div className={`absolute inset-0 bg-gradient-to-br ${storeConfig.color} cgm-flow pointer-events-none`} style={{ backgroundSize: '200% 200%' }} />
        {/* Grid pattern overlay — visible */}
        <div className="absolute inset-0 opacity-80 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.18) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          maskImage: 'radial-gradient(ellipse at center, black 15%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 15%, transparent 75%)'
        }} />
        {/* Soft radial overlay for depth */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(circle at 25% 30%, rgba(255,255,255,0.15) 0%, transparent 40%), radial-gradient(circle at 75% 70%, rgba(249,115,22,0.2) 0%, transparent 50%)'
        }} />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/stores" className="inline-flex items-center text-white/80 hover:text-white mb-3 text-sm transition-colors cgm-reveal">
            <ArrowLeft className="h-4 w-4 me-1 rtl:rotate-180" />
            {language === 'ar' ? 'العودة للمتاجر' : 'Back to Stores'}
          </Link>
          
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-white cgm-reveal" style={{ animationDelay: '100ms' }}>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/15 backdrop-blur-md rounded-2xl border border-white/20 shadow-xl">
                {store.logo ? (
                  <img src={store.logo} alt={store.name} className="h-9 w-9 rounded-lg object-cover" />
                ) : (
                  <StoreIcon className="h-7 w-7" />
                )}
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black tracking-tight" data-testid="store-name" style={{ fontFamily: 'Cairo, sans-serif' }}>
                  {language === 'ar' ? store.name : (store.nameEn || store.name)}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-white/85 text-sm font-semibold">
                    {language === 'ar' ? storeConfig.name : storeConfig.nameEn}
                  </span>
                  <span className="text-white/50">•</span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${statusBadge.className}`}>
                    <StatusIcon className="h-2.5 w-2.5" />
                    {statusBadge.text}
                  </span>
                </div>
                {store.store_uid && (
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(store.store_uid);
                      toast.success(language === 'ar' ? 'تم نسخ الرقم التعريفي' : 'Store ID copied');
                    }}
                    className="inline-flex items-center gap-1.5 mt-2 px-2 py-0.5 rounded-md bg-white/15 hover:bg-white/25 border border-white/20 text-[11px] font-mono tracking-wider text-white/95 transition-colors backdrop-blur-sm"
                    data-testid="store-uid-badge"
                    title={language === 'ar' ? 'انقر للنسخ' : 'Click to copy'}
                  >
                    <span className="text-white/70">
                      {language === 'ar' ? 'الرقم التعريفي:' : 'ID:'}
                    </span>
                    <span dir="ltr">{store.store_uid}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs Navigation - Premium */}
      <div className={`border-b sticky top-0 z-10 ${
        isDarkMode 
          ? 'border-white/10 bg-[#131B2C]/90 backdrop-blur-xl' 
          : 'border-[#E2E8F0] bg-white/90 backdrop-blur-xl'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    isActive
                      ? 'border-[#38BDF8]'
                      : isDarkMode
                        ? 'border-transparent text-gray-400 hover:text-[#38BDF8]'
                        : 'border-transparent text-[#475569] hover:text-[#38BDF8]'
                  }`}
                  data-testid={`tab-${tab.id}`}
                >
                  {/* iter-227: keep icon opaque even when the label is
                      gradient-clipped (text-transparent would otherwise hide
                      Lucide icons that stroke with currentColor). */}
                  <tab.icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-[#38BDF8]' : ''}`} />
                  <span className={isActive ? 'text-gradient' : ''}>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Sub-tabs Switch */}
            <div className="flex justify-center">
              <div className={`p-1.5 rounded-2xl inline-flex gap-1 ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-100'}`}>
                <button
                  onClick={() => setMyStoreSubTab('overview')}
                  className={`px-6 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 flex items-center gap-2 ${
                    myStoreSubTab === 'overview'
                      ? isDarkMode ? 'bg-[#1E293B] text-white shadow-md' : 'bg-white text-[#0F172A] shadow-md'
                      : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <BarChart3 className="h-4 w-4" />
                  {language === 'ar' ? 'نظرة عامة' : 'Overview'}
                </button>
                <button
                  onClick={() => setMyStoreSubTab('assistant')}
                  className={`px-6 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 flex items-center gap-2 ${
                    myStoreSubTab === 'assistant'
                      ? 'bg-gradient-to-r from-[#1E9BD9] to-[#38BDF8] text-white shadow-md'
                      : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <CGMAIIcon className="h-5 w-5" />
                  {language === 'ar' ? 'CGM AI AGENT' : 'CGM AI AGENT'}
                  <span className={`w-2 h-2 rounded-full ${myStoreSubTab === 'assistant' ? 'bg-green-400' : 'bg-green-500'} animate-pulse`}></span>
                </button>
              </div>
            </div>

            {/* Overview Content */}
            {myStoreSubTab === 'overview' && (
              <div className="space-y-6 animate-in fade-in duration-300">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 cgm-reveal-stagger">
                  {storeConfig.stats.map((stat, index) => {
                    let statValue = 0;
                    if (stat.id === 'available' || stat.id === 'listings' || stat.id === 'vehicles' || stat.id === 'products') {
                      statValue = items.filter(item => item.status !== 'sold').length;
                    } else if (stat.id === 'sold') {
                      statValue = items.filter(item => item.status === 'sold').length;
                    } else if (stat.id === 'archived') {
                      // Live source of truth — same list the Archive tab renders.
                      // Prevents drift from a stale cached counter on the store doc (deployer RCA iter-231).
                      statValue = archivedItems.length;
                    } else if (stat.id === 'views') {
                      statValue = store.views || 0;
                    } else if (stat.id === 'inquiries') {
                      statValue = store.inquiries || negotiations.length || 0;
                    } else {
                      statValue = store[stat.id] || 0;
                    }
                    
                    return (
                      <div key={stat.id} className="cgm-ai-card group">
                        <div className="flex items-center justify-between mb-3">
                          <div className={`cgm-icon-glow !p-2.5 bg-gradient-to-br ${storeConfig.color} group-hover:scale-110 transition-transform duration-500`}>
                            <stat.icon className="h-5 w-5 text-white" />
                          </div>
                        </div>
                        <p className="cgm-stat-value-ai">{statValue}</p>
                        <p className={`text-xs sm:text-sm font-semibold mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                          {language === 'ar' ? stat.label : stat.labelEn}
                        </p>
                      </div>
                    );
                  })}
                </div>

                {/* Store Info */}
                <div className="cgm-ai-card-elevated">
                  <h2 className={`text-lg font-black mb-4 ${isDarkMode ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Cairo, sans-serif' }}>
                    {language === 'ar' ? 'معلومات المتجر' : 'Store Information'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>{language === 'ar' ? 'اسم المتجر (عربي)' : 'Store Name (Arabic)'}</p>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{store.name}</p>
                    </div>
                    <div>
                      <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>{language === 'ar' ? 'اسم المتجر (إنجليزي)' : 'Store Name (English)'}</p>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{store.nameEn || '-'}</p>
                    </div>
                    <div>
                      <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>{language === 'ar' ? 'نوع المتجر' : 'Store Type'}</p>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{language === 'ar' ? storeConfig.name : storeConfig.nameEn}</p>
                    </div>
                    <div>
                      <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>{language === 'ar' ? 'تاريخ الإنشاء' : 'Created At'}</p>
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {store.created_at ? new Date(store.created_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US') : (language === 'ar' ? 'غير محدد' : 'Not specified')}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>{language === 'ar' ? 'الوصف' : 'Description'}</p>
                      <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-[#0F172A]'}`}>{store.description || (language === 'ar' ? 'لا يوجد وصف' : 'No description')}</p>
                    </div>
                  </div>
                </div>

                {/* Pending Review Notice */}
                {store.status === 'pending' && !store.verified && (
                  <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-5">
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Clock className="h-6 w-6 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-bold text-orange-800 mb-1">
                          {language === 'ar' ? 'متجرك قيد المراجعة' : 'Your store is under review'}
                        </h3>
                        <p className="text-sm text-orange-600">
                          {language === 'ar' 
                            ? 'سيتم مراجعة طلبك من قبل فريق الإدارة. قد يستغرق ذلك من 1-3 أيام عمل.'
                            : 'Your request will be reviewed by the admin team. This may take 1-3 business days.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* AI Assistant Content */}
            {myStoreSubTab === 'assistant' && (
              <div className="animate-in slide-in-from-bottom-4 duration-500">
                {/* Animated AI Chat Box */}
                <div className="relative max-w-3xl mx-auto">
                  {/* Animated Background Glow */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#38BDF8] via-[#1E9BD9] to-[#F97316] rounded-3xl blur-xl opacity-20 animate-pulse"></div>
                  
                  {/* Main Chat Container */}
                  <div className={`relative rounded-3xl shadow-2xl overflow-hidden border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-gray-100'}`}>
                    {/* Animated Header */}
                    <div className="relative overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-r from-[#1E293B] via-[#0F172A] to-[#1E293B] bg-[length:200%_100%] animate-gradient pointer-events-none"></div>
                      <div className="relative px-6 py-5 flex items-center gap-4">
                        {/* CGM AI Icon */}
                        <div className="relative">
                          <div className="absolute inset-0 bg-[#38BDF8]/30 rounded-2xl animate-ping"></div>
                          <div className="relative p-2 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                            <CGMAIIcon className="h-10 w-10" />
                          </div>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-white text-lg flex items-center gap-2">
                            CGM AI AGENT
                            <Sparkles className="h-5 w-5 text-[#F97316] animate-bounce" />
                          </h3>
                          <p className="text-white/80 text-sm">
                            {language === 'ar' ? 'مدعوم بالذكاء الاصطناعي • متاح 24/7' : 'AI Powered • Available 24/7'}
                          </p>
                        </div>
                        {/* Status Indicator */}
                        <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full">
                          <span className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse"></span>
                          <span className="text-white/90 text-xs font-medium">
                            {language === 'ar' ? 'متصل' : 'Online'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Chat Messages Area */}
                    <div 
                      ref={chatMessagesRef}
                      className={`h-[400px] overflow-y-auto p-6 ${isDarkMode ? 'bg-[#131B2C]' : 'bg-gradient-to-b from-gray-50/50 to-white'}`}
                      style={{
                        backgroundImage: isDarkMode ? 'none' : 'radial-gradient(circle at 50% 0%, rgba(30, 155, 217, 0.03) 0%, transparent 50%)'
                      }}
                    >
                      {aiChatMessages.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-center px-4">
                          {/* Animated Welcome */}
                          <div className="relative mb-6">
                            <div className="absolute inset-0 bg-gradient-to-r from-[#38BDF8] to-[#F97316] rounded-full blur-2xl opacity-20 animate-pulse"></div>
                            <div className="relative w-28 h-28 bg-gradient-to-br from-[#1E293B] to-[#0F172A] rounded-full flex items-center justify-center border-2 border-[#38BDF8]/30 shadow-xl">
                              <CGMAIIcon className="h-16 w-16" />
                            </div>
                          </div>
                          <h4 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                            {language === 'ar' ? 'مرحباً بك!' : 'Welcome!'}
                          </h4>
                          <p className={`mb-6 max-w-sm ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>
                            {language === 'ar' 
                              ? 'أنا CGM AI AGENT، يمكنني مساعدتك في إدارة متجرك وتقديم النصائح لزيادة مبيعاتك' 
                              : "I'm CGM AI AGENT, I can help you manage your store and provide tips to increase your sales"}
                          </p>
                          
                          {/* Suggestion Cards */}
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-lg">
                            {[
                              { text: language === 'ar' ? 'كيف أزيد مبيعاتي؟' : 'Increase sales?', icon: TrendingUp, color: 'from-green-400 to-emerald-500' },
                              { text: language === 'ar' ? 'نصائح للتسعير' : 'Pricing tips', icon: DollarSign, color: 'from-amber-400 to-orange-500' },
                              { text: language === 'ar' ? 'تحسين الوصف' : 'Better descriptions', icon: Type, color: 'from-[#1E9BD9] to-[#0D7AB5]' }
                            ].map((suggestion, idx) => (
                              <button
                                key={idx}
                                onClick={() => {
                                  setAiChatInput(suggestion.text);
                                  sendAiMessage();
                                }}
                                className={`group p-4 rounded-2xl border-2 hover:border-[#1E9BD9]/30 hover:shadow-lg transition-all duration-300 text-start ${isDarkMode ? 'bg-[#0F172A] border-white/10' : 'bg-white border-gray-100'}`}
                              >
                                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${suggestion.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                                  <suggestion.icon className="h-5 w-5 text-white" />
                                </div>
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{suggestion.text}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {aiChatMessages.map((msg, idx) => (
                            <div 
                              key={idx} 
                              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2 duration-300`}
                              style={{ animationDelay: `${idx * 50}ms` }}
                            >
                              {msg.role === 'assistant' && (
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1E293B] to-[#0F172A] flex items-center justify-center me-3 flex-shrink-0 border border-[#38BDF8]/30 overflow-hidden">
                                  <CGMAIIcon className="h-6 w-6" />
                                </div>
                              )}
                              <div className={`max-w-[75%] rounded-2xl px-5 py-3 ${
                                msg.role === 'user' 
                                  ? 'bg-gradient-to-r from-[#1E9BD9] to-blue-600 text-white rounded-br-md' 
                                  : isDarkMode ? 'bg-[#0F172A] border border-white/10 text-white rounded-bl-md' : 'bg-white border border-gray-100 text-[#0F172A] rounded-bl-md shadow-sm'
                              }`}>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                              </div>
                              {msg.role === 'user' && (
                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ms-3 flex-shrink-0 ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-100'}`}>
                                  <UserCircle className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                                </div>
                              )}
                            </div>
                          ))}
                          {aiChatLoading && (
                            <div className="flex justify-start animate-in fade-in duration-300">
                              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1E293B] to-[#0F172A] flex items-center justify-center me-3 border border-[#38BDF8]/30 overflow-hidden">
                                <CGMAIIcon className="h-6 w-6" />
                              </div>
                              <div className={`rounded-2xl rounded-bl-md px-5 py-4 ${isDarkMode ? 'bg-[#0F172A] border border-white/10' : 'bg-white border border-gray-100 shadow-sm'}`}>
                                <div className="flex items-center gap-1.5">
                                  <span className="w-2 h-2 bg-[#1E9BD9] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                                  <span className="w-2 h-2 bg-[#1E9BD9] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                                  <span className="w-2 h-2 bg-[#1E9BD9] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Chat Input Area */}
                    <div className={`p-4 backdrop-blur-sm border-t ${isDarkMode ? 'bg-[#0F172A]/80 border-white/10' : 'bg-gray-50/80 border-gray-100'}`}>
                      <div className="flex gap-3 items-center">
                        <div className="flex-1 relative">
                          <Input
                            value={aiChatInput}
                            onChange={(e) => setAiChatInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && sendAiMessage()}
                            placeholder={language === 'ar' ? 'اكتب رسالتك هنا...' : 'Type your message...'}
                            className={`w-full py-3 px-5 border-2 rounded-2xl focus:border-[#1E9BD9] focus:ring-2 focus:ring-[#1E9BD9]/20 transition-all text-base ${isDarkMode ? 'bg-[#1E293B] border-white/10 text-white placeholder:text-gray-500' : 'bg-white border-gray-100'}`}
                            disabled={aiChatLoading}
                          />
                        </div>
                        <Button
                          onClick={sendAiMessage}
                          disabled={!aiChatInput.trim() || aiChatLoading}
                          className="h-12 w-12 rounded-2xl bg-gradient-to-r from-[#1E9BD9] to-[#0D7AB5] hover:opacity-90 transition-all disabled:opacity-50 p-0 flex items-center justify-center"
                        >
                          {aiChatLoading ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                          ) : (
                            <Send className="h-5 w-5" />
                          )}
                        </Button>
                      </div>
                      <p className="text-center text-xs text-gray-400 mt-3">
                        {language === 'ar' ? 'مدعوم بتقنية الذكاء الاصطناعي من CloudGM' : 'Powered by CloudGM AI Technology'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Items Tab */}
        {activeTab === 'items' && (
          <div className="space-y-6">
            {/* E-commerce Store - Show products list directly with Add button */}
            {(store.type === 'ecommerce' || store.storeType === 'ecommerce') ? (
              <>
                {/* Header with Add Product button */}
                <div className="flex items-center justify-between mb-4">
                  <h2 className={`text-xl font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    <Package className="h-6 w-6 text-[#1E9BD9]" />
                    {language === 'ar' ? 'المنتجات' : 'Products'}
                    {items.length > 0 && (
                      <span className="px-2 py-0.5 bg-[#1E9BD9] text-white text-sm rounded-full ms-2">{items.length}</span>
                    )}
                  </h2>
                  <Button
                    onClick={() => setShowAddProductPage(true)}
                    className="bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
                  >
                    <Plus className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'إضافة منتج' : 'Add Product'}
                  </Button>
                </div>

                {/* Products List directly */}
                <div className="animate-in fade-in duration-300 space-y-6">
                    {/* Categories Grid with Images */}
                    <div className={`rounded-xl p-6 shadow-lg border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-[#E2E8F0]'}`}>
                      <h3 className={`text-lg font-bold mb-6 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        <Package className="h-5 w-5 text-[#1E9BD9]" />
                        {language === 'ar' ? 'التصنيفات الرئيسية' : 'Main Categories'}
                      </h3>
                      
                      {loadingCategories ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" />
                        </div>
                      ) : (
                        <>
                          {/* Main Categories Grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                            {/* All Products Card */}
                            <button
                              onClick={() => {
                                setSelectedFilterCategory(null);
                                setSelectedFilterSubCategory('all');
                                setProductFilter('all');
                              }}
                              className={`relative group rounded-2xl overflow-hidden transition-all duration-300 aspect-square ${
                                !selectedFilterCategory
                                  ? 'ring-4 ring-[#1E9BD9] ring-offset-2 scale-[1.02]'
                                  : 'hover:scale-[1.02] hover:shadow-lg'
                              }`}
                            >
                              <div className="absolute inset-0 bg-gradient-to-br from-[#1E9BD9] to-[#0D7AB5]" />
                              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-3">
                                <Package className="h-10 w-10 mb-2 opacity-90" />
                                <span className="font-bold text-sm text-center">
                                  {language === 'ar' ? 'الكل' : 'All'}
                                </span>
                                <span className="mt-1 px-3 py-1 bg-white/20 rounded-full text-xs font-medium">
                                  {items.length} {language === 'ar' ? 'منتج' : 'items'}
                                </span>
                              </div>
                              {!selectedFilterCategory && (
                                <div className="absolute top-2 right-2 bg-white rounded-full p-1">
                                  <CheckCircle className="h-4 w-4 text-[#1E9BD9]" />
                                </div>
                              )}
                            </button>
                            
                            {/* Main Category Cards */}
                            {categories.map((cat) => {
                              const count = items.filter(item => item.category_id === cat.id).length;
                              const isSelected = selectedFilterCategory?.id === cat.id;
                              return (
                                <button
                                  key={cat.id}
                                  onClick={() => {
                                    setSelectedFilterCategory(cat);
                                    setSelectedFilterSubCategory('all');
                                    setProductFilter(cat.id);
                                  }}
                                  className={`relative group rounded-2xl overflow-hidden transition-all duration-300 aspect-square ${
                                    isSelected
                                      ? 'ring-4 ring-[#1E9BD9] ring-offset-2 scale-[1.02]'
                                      : 'hover:scale-[1.02] hover:shadow-lg'
                                  }`}
                                >
                                  {/* Category Image */}
                                  {cat.image ? (
                                    <img 
                                      src={cat.image} 
                                      alt={cat.name_ar || cat.name_en} 
                                      className="absolute inset-0 w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div 
                                      className="absolute inset-0 bg-gradient-to-br from-gray-400 to-gray-600" 
                                    />
                                  )}
                                  
                                  {/* Gradient Overlay */}
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                                  
                                  {/* Category Info */}
                                  <div className="absolute inset-0 flex flex-col items-center justify-end p-3 text-white">
                                    <span className="font-bold text-sm text-center line-clamp-2 mb-1">
                                      {language === 'ar' ? (cat.name_ar || cat.name) : (cat.name_en || cat.name)}
                                    </span>
                                    <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium">
                                      {count} {language === 'ar' ? 'منتج' : 'items'}
                                    </span>
                                  </div>
                                  
                                  {/* Selected Indicator */}
                                  {isSelected && (
                                    <div className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-lg">
                                      <CheckCircle className="h-4 w-4 text-[#1E9BD9]" />
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                          
                          {/* Subcategories - Show when a main category is selected */}
                          {selectedFilterCategory && selectedFilterCategory.subcategories && selectedFilterCategory.subcategories.length > 0 && (
                            <div className={`mt-6 pt-6 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
                              <h4 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-gray-300' : 'text-[#475569]'}`}>
                                <Tag className="h-4 w-4" />
                                {language === 'ar' ? 'التصنيفات الفرعية' : 'Subcategories'}
                                <span className={`font-normal ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>
                                  ({language === 'ar' ? (selectedFilterCategory.name_ar || selectedFilterCategory.name) : (selectedFilterCategory.name_en || selectedFilterCategory.name)})
                                </span>
                              </h4>
                              <div className="flex flex-wrap gap-2">
                                {/* All in this category */}
                                <button
                                  onClick={() => {
                                    setSelectedFilterSubCategory('all');
                                    setProductFilter(selectedFilterCategory.id);
                                  }}
                                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                                    selectedFilterSubCategory === 'all'
                                      ? 'bg-[#1E9BD9] text-white shadow-md'
                                      : isDarkMode ? 'bg-[#0F172A] text-gray-300 hover:bg-[#1E293B]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  {language === 'ar' ? 'الكل' : 'All'}
                                  <span className={`ms-2 px-2 py-0.5 rounded-full text-xs ${
                                    selectedFilterSubCategory === 'all' ? 'bg-white/20' : isDarkMode ? 'bg-white/10' : 'bg-gray-200'
                                  }`}>
                                    {items.filter(item => item.category_id === selectedFilterCategory.id).length}
                                  </span>
                                </button>
                                
                                {/* Subcategory buttons */}
                                {selectedFilterCategory.subcategories
                                  .filter(sub => !sub.is_default) // Filter out default "All" subcategory
                                  .map((sub) => {
                                    const subCount = items.filter(item => item.subcategory_id === sub.id).length;
                                    const isSubSelected = selectedFilterSubCategory === sub.id;
                                    return (
                                      <button
                                        key={sub.id}
                                        onClick={() => {
                                          setSelectedFilterSubCategory(sub.id);
                                          setProductFilter(`${selectedFilterCategory.id}:${sub.id}`);
                                        }}
                                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                                          isSubSelected
                                            ? 'bg-[#1E9BD9] text-white shadow-md'
                                            : isDarkMode ? 'bg-[#0F172A] text-gray-300 hover:bg-[#1E293B]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                        }`}
                                      >
                                        {language === 'ar' ? sub.name_ar : sub.name_en}
                                        <span className={`ms-2 px-2 py-0.5 rounded-full text-xs ${
                                          isSubSelected ? 'bg-white/20' : isDarkMode ? 'bg-white/10' : 'bg-gray-200'
                                        }`}>
                                          {subCount}
                                        </span>
                                      </button>
                                    );
                                  })}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Products List */}
                    <div className={`rounded-xl p-6 shadow-lg border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-[#E2E8F0]'}`}>
                      <div className="flex items-center justify-between mb-6">
                        <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {language === 'ar' ? 'المنتجات' : 'Products'}
                          <span className={`text-sm font-normal ms-2 ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>
                            ({productFilter === 'all' ? items.length : items.filter(i => i.category_id === productFilter).length})
                          </span>
                        </h3>
                      </div>
                      
                      {items.length === 0 ? (
                        <div className="text-center py-12">
                          <ShoppingCart className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`} />
                          <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>
                            {language === 'ar' ? 'لا يوجد منتجات بعد' : 'No products yet'}
                          </p>
                          <Button 
                            onClick={() => setProductsSubTab('add')}
                            className="bg-gradient-to-r from-[#1E9BD9] to-[#0D7AB5] hover:opacity-90"
                          >
                            <Plus className="h-4 w-4 me-2" />
                            {language === 'ar' ? 'إضافة أول منتج' : 'Add First Product'}
                          </Button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {items
                            .filter(item => productFilter === 'all' || item.category_id === productFilter)
                            .map((item) => (
                              <div 
                                key={item.id} 
                                className={`border rounded-xl p-4 hover:shadow-md transition-all cursor-pointer ${isDarkMode ? 'border-white/10 bg-[#0F172A] hover:border-[#1E9BD9]/50' : 'hover:border-[#1E9BD9]'}`}
                                onClick={() => {
                                  setSelectedProductItem(item);
                                  setEditProductData({
                                    name: item.name || '',
                                    nameEn: item.nameEn || '',
                                    price: item.price || '',
                                    description: item.description || '',
                                    quantity: item.quantity || '',
                                    category_id: item.category_id || '',
                                    subcategory_id: item.subcategory_id || '',
                                    status: item.status || 'active'
                                  });
                                  setEditProductImages(item.images || (item.image ? [item.image] : []));
                                  setShowProductDetailsModal(true);
                                }}
                              >
                                {item.image ? (
                                  <img src={item.image} alt={item.name} className="w-full h-40 object-cover rounded-lg mb-3" />
                                ) : (
                                  <div className={`w-full h-40 rounded-lg mb-3 flex items-center justify-center ${isDarkMode ? 'bg-[#1E293B]' : 'bg-gray-100'}`}>
                                    <Package className={`h-12 w-12 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                                  </div>
                                )}
                                <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{item.name}</h4>
                                {item.category_name && (
                                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>{item.category_name}</p>
                                )}
                                <div className="flex items-center justify-between mt-2">
                                  <div>
                                    {item.hasDiscount ? (
                                      <>
                                        <span className={`text-sm line-through ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>AED {item.price}</span>
                                        <span className="text-red-500 font-bold ms-2">AED {item.salePrice}</span>
                                      </>
                                    ) : (
                                      <span className="text-[#1E9BD9] font-bold">AED {item.price}</span>
                                    )}
                                  </div>
                                  {item.quantity !== undefined && (
                                    <span className={`text-xs px-2 py-1 rounded ${item.quantity > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                      {item.quantity > 0 ? `${item.quantity} ${language === 'ar' ? 'متوفر' : 'in stock'}` : (language === 'ar' ? 'نفذ' : 'Out')}
                                    </span>
                                  )}
                                </div>
                                <div className="flex gap-2 mt-3" onClick={(e) => e.stopPropagation()}>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="flex-1 text-[#1E9BD9] border-[#1E9BD9]"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Go to full-page edit mode
                                      setEditingItem(item);
                                      setShowEditProductPage(true);
                                    }}
                                  >
                                    <Edit2 className="h-3 w-3 me-1" />
                                    {language === 'ar' ? 'تعديل' : 'Edit'}
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    className="text-red-500 border-red-200 hover:bg-red-50"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteItem(item.id);
                                    }}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>
              </>
            ) : (
              /* Non E-commerce Stores - Original Layout */
              <div className={`rounded-xl p-6 shadow-lg border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-[#E2E8F0]'}`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {language === 'ar' ? storeConfig.itemsName : storeConfig.itemsNameEn}
              </h2>
              <div className="flex flex-wrap gap-2">
                {/* Bulk Upload Button - Only for phone_numbers */}
                {(store.type === 'phone_numbers' || store.storeType === 'phone_numbers') && (
                  <Button 
                    onClick={() => setShowBulkUploadModal(true)}
                    variant="outline"
                    className="border-[#1E9BD9] text-[#1E9BD9] hover:bg-[#1E9BD9]/10"
                  >
                    <FileSpreadsheet className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'رفع ملف CSV/Excel' : 'Upload CSV/Excel'}
                  </Button>
                )}
                <Button 
                  onClick={() => {
                    if (store.type === 'real_estate' || store.storeType === 'real_estate') {
                      setShowAddPropertyPage(true);
                    } else if (store.type === 'auto' || store.storeType === 'auto') {
                      setShowAddCarPage(true);
                    } else if (store.type === 'phone_numbers' || store.storeType === 'phone_numbers') {
                      setShowAddPhonePage(true);
                    } else if (store.type === 'car_plates' || store.storeType === 'car_plates') {
                      setShowAddPlatePage(true);
                    } else {
                      setShowAddProductPage(true);
                    }
                  }}
                  className={`bg-gradient-to-r ${storeConfig.color} hover:opacity-90`}
                >
                  <Plus className="h-4 w-4 me-2" />
                  {language === 'ar' ? `إضافة ${storeConfig.itemName}` : `Add ${storeConfig.itemNameEn}`}
                </Button>
              </div>
            </div>

            {/* Pending Notice */}
            {store.status === 'pending' && (
              <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-xl flex items-center gap-3">
                <Clock className="h-5 w-5 text-orange-500 flex-shrink-0" />
                <p className="text-sm text-orange-700">
                  {language === 'ar' 
                    ? 'المتجر قيد المراجعة - يمكنك إضافة العناصر الآن وستظهر بعد الموافقة'
                    : 'Store is pending review - you can add items now and they will appear after approval'
                  }
                </p>
              </div>
            )}
            
            {items.length === 0 ? (
              <div className="text-center py-12">
                <storeConfig.icon className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`} />
                <p className={`mb-4 ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>
                  {language === 'ar' ? `لا يوجد ${storeConfig.itemsName} بعد` : `No ${storeConfig.itemsNameEn.toLowerCase()} yet`}
                </p>
                <Button 
                  onClick={() => {
                    if (store.type === 'real_estate' || store.storeType === 'real_estate') {
                      setShowAddPropertyPage(true);
                    } else if (store.type === 'auto' || store.storeType === 'auto') {
                      setShowAddCarPage(true);
                    } else if (store.type === 'phone_numbers' || store.storeType === 'phone_numbers') {
                      setShowAddPhonePage(true);
                    } else if (store.type === 'car_plates' || store.storeType === 'car_plates') {
                      setShowAddPlatePage(true);
                    } else {
                      setShowAddProductPage(true);
                    }
                  }}
                  className={`bg-gradient-to-r ${storeConfig.color} hover:opacity-90`}
                >
                  <Plus className="h-4 w-4 me-2" />
                  {language === 'ar' ? `إضافة ${storeConfig.itemName}` : `Add ${storeConfig.itemNameEn}`}
                </Button>
              </div>
            ) : (
              <>
                {/* Toolbar: search + view toggle */}
                <div className={`mb-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-3 rounded-xl border ${isDarkMode ? 'bg-[#0F172A]/60 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="relative flex-1">
                    <Search className={`absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    <Input
                      value={productsSearch}
                      onChange={(e) => setProductsSearch(e.target.value)}
                      placeholder={language === 'ar' ? 'ابحث بالاسم أو الرقم أو السعر...' : 'Search by name, number, price...'}
                      className={`ps-10 h-10 ${isDarkMode ? 'bg-[#131B2C] border-white/10 text-white' : ''}`}
                      data-testid="products-search-input"
                    />
                  </div>
                  <div className={`inline-flex rounded-lg border overflow-hidden self-end sm:self-auto ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                    <button
                      type="button"
                      onClick={() => setProductsViewMode('grid')}
                      className={`px-3 h-10 inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
                        productsViewMode === 'grid'
                          ? 'bg-[#1E9BD9] text-white'
                          : (isDarkMode ? 'bg-[#131B2C] text-gray-300 hover:bg-white/5' : 'bg-white text-slate-700 hover:bg-slate-100')
                      }`}
                      data-testid="products-view-grid-btn"
                    >
                      <LayoutGrid className="h-4 w-4" />
                      <span className="hidden sm:inline">{language === 'ar' ? 'بطاقات' : 'Grid'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setProductsViewMode('list')}
                      className={`px-3 h-10 inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${
                        productsViewMode === 'list'
                          ? 'bg-[#1E9BD9] text-white'
                          : (isDarkMode ? 'bg-[#131B2C] text-gray-300 hover:bg-white/5' : 'bg-white text-slate-700 hover:bg-slate-100')
                      }`}
                      data-testid="products-view-list-btn"
                    >
                      <Rows3 className="h-4 w-4" />
                      <span className="hidden sm:inline">{language === 'ar' ? 'جدول' : 'Table'}</span>
                    </button>
                  </div>
                </div>
                {(() => {
                  const q = (productsSearch || '').trim().toLowerCase();
                  const filteredItems = !q ? items : items.filter(it => {
                    const hay = [
                      it.name, it.title, it.number, it.plateNumber, it.plateCode,
                      it.make, it.model, it.description,
                      String(it.price ?? ''), String(it.salePrice ?? '')
                    ].filter(Boolean).join(' ').toLowerCase();
                    return hay.includes(q);
                  });
                  if (filteredItems.length === 0) {
                    return (
                      <div className={`text-center py-10 rounded-xl border ${isDarkMode ? 'border-white/10 bg-[#0F172A]/40 text-gray-400' : 'border-slate-200 bg-slate-50 text-slate-500'}`}>
                        {language === 'ar' ? 'لا توجد نتائج مطابقة لبحثك' : 'No items match your search'}
                      </div>
                    );
                  }
                  if (productsViewMode === 'list') {
                    const type = store.type || store.storeType;
                    const isPhones = type === 'phone_numbers';
                    const isPlates = type === 'car_plates';

                    // Operator label map for phone stores
                    const opLabel = (op) => {
                      const k = String(op || '').toLowerCase();
                      if (k === 'etisalat' || k === 'e&' || k === 'e&and') return language === 'ar' ? 'اتصالات' : 'Etisalat';
                      if (k === 'du') return 'du';
                      if (k === 'virgin') return 'Virgin';
                      return op || '—';
                    };

                    return (
                      <div className={`overflow-x-auto rounded-xl border ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
                        <table className={`w-full text-sm ${isDarkMode ? 'text-gray-200' : 'text-slate-800'}`} data-testid="products-list-table">
                          <thead className={isDarkMode ? 'bg-[#131B2C] text-gray-400' : 'bg-slate-50 text-slate-500'}>
                            <tr>
                              {isPhones ? (
                                <>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'المشغل' : 'Operator'}</th>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'الرقم' : 'Number'}</th>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'كيف يظهر للعملاء' : 'Customer View'}</th>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'السعر' : 'Price'}</th>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'الحالة' : 'Status'}</th>
                                </>
                              ) : isPlates ? (
                                <>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'الرقم' : 'Number'}</th>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'الإمارة' : 'Emirate'}</th>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'الكود' : 'Code'}</th>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'كيف يظهر للعملاء' : 'Customer View'}</th>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'السعر' : 'Price'}</th>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'الحالة' : 'Status'}</th>
                                </>
                              ) : (
                                <>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'الصورة' : 'Image'}</th>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'الاسم' : 'Name'}</th>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'السعر' : 'Price'}</th>
                                  <th className="text-start px-4 py-3 font-semibold">{language === 'ar' ? 'الحالة' : 'Status'}</th>
                                </>
                              )}
                              <th className="text-end px-4 py-3 font-semibold">{language === 'ar' ? 'إجراءات' : 'Actions'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredItems.map((item) => {
                              const displayName = item.name || item.title || item.number || item.plateNumber || `${item.make || ''} ${item.model || ''}`.trim() || '—';
                              const isSold = item.status === 'sold';
                              const isArchived = item.status === 'archived';
                              const priceCell = item.priceOnRequest
                                ? <span className={`text-xs ${isDarkMode ? 'text-white/80' : 'text-slate-600'}`}>{language === 'ar' ? 'اتصل لمعرفة السعر' : 'Call for price'}</span>
                                : <span className="text-[#1E9BD9] font-bold" dir="ltr">{(item.salePrice ?? item.price)?.toLocaleString('en-US')} AED</span>;
                              const statusCell = (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  isSold ? 'bg-red-100 text-red-700'
                                    : isArchived ? 'bg-slate-200 text-slate-600'
                                    : 'bg-emerald-100 text-emerald-700'
                                }`}>
                                  {isSold ? (language === 'ar' ? 'مباع' : 'Sold')
                                    : isArchived ? (language === 'ar' ? 'مؤرشف' : 'Archived')
                                    : (language === 'ar' ? 'نشط' : 'Active')}
                                </span>
                              );
                              return (
                                <tr key={item.id} className={`border-t ${isDarkMode ? 'border-white/5 hover:bg-white/5' : 'border-slate-100 hover:bg-slate-50'}`}>
                                  {isPhones ? (
                                    <>
                                      <td className="px-4 py-3 whitespace-nowrap font-semibold">{opLabel(item.operator)}</td>
                                      <td className="px-4 py-3 whitespace-nowrap font-mono font-bold" dir="ltr">{item.number}</td>
                                      <td className="px-4 py-3 whitespace-nowrap font-mono text-amber-500" dir="ltr" title={language === 'ar' ? 'الشكل الذي يراه العملاء' : 'Customer-facing masked view'}>{applyMask(item.number, item.hiddenDigits)}</td>
                                      <td className="px-4 py-3 whitespace-nowrap">{priceCell}</td>
                                      <td className="px-4 py-3 whitespace-nowrap">{statusCell}</td>
                                    </>
                                  ) : isPlates ? (
                                    <>
                                      <td className="px-4 py-3 whitespace-nowrap font-mono font-bold" dir="ltr">{item.plateNumber}</td>
                                      <td className="px-4 py-3 whitespace-nowrap">{item.emirate || '—'}</td>
                                      <td className="px-4 py-3 whitespace-nowrap font-semibold" dir="ltr">{item.plateCode || '—'}</td>
                                      <td className="px-4 py-3 whitespace-nowrap font-mono text-amber-500" dir="ltr" title={language === 'ar' ? 'الشكل الذي يراه العملاء' : 'Customer-facing masked view'}>{(item.plateCode ? item.plateCode + ' ' : '') + applyMask(item.plateNumber, item.hiddenDigits)}</td>
                                      <td className="px-4 py-3 whitespace-nowrap">{priceCell}</td>
                                      <td className="px-4 py-3 whitespace-nowrap">{statusCell}</td>
                                    </>
                                  ) : (
                                    <>
                                      <td className="px-4 py-3">
                                        <div className={`h-12 w-12 rounded-lg overflow-hidden flex items-center justify-center ${isDarkMode ? 'bg-white/5' : 'bg-slate-100'}`}>
                                          {item.image || (Array.isArray(item.images) && item.images[0])
                                            ? <img src={item.image || item.images[0]} alt="" className="h-full w-full object-cover" />
                                            : <Package className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`} />}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3 max-w-[280px]">
                                        <div className="font-semibold truncate" title={displayName}>{displayName}</div>
                                      </td>
                                      <td className="px-4 py-3 whitespace-nowrap">{priceCell}</td>
                                      <td className="px-4 py-3 whitespace-nowrap">{statusCell}</td>
                                    </>
                                  )}
                                  <td className="px-4 py-3 text-end whitespace-nowrap">
                                    <div className="inline-flex gap-1">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingItem(item);
                                          if (store.type === 'phone_numbers' || store.storeType === 'phone_numbers') setShowEditPhonePage(true);
                                          else if (store.type === 'car_plates' || store.storeType === 'car_plates') setShowEditPlatePage(true);
                                          else if (store.type === 'auto' || store.storeType === 'auto') setShowAddCarPage(true);
                                          else if (store.type === 'real_estate' || store.storeType === 'real_estate') setShowAddPropertyPage(true);
                                          else {
                                            setSelectedProductItem(item);
                                            setEditProductData({
                                              name: item.name || '',
                                              nameEn: item.nameEn || '',
                                              price: item.price || '',
                                              description: item.description || '',
                                              quantity: item.quantity || '',
                                              category_id: item.category_id || '',
                                              subcategory_id: item.subcategory_id || '',
                                              status: item.status || 'active'
                                            });
                                            setEditProductImages(item.images || (item.image ? [item.image] : []));
                                            setShowProductDetailsModal(true);
                                          }
                                        }}
                                        data-testid={`list-edit-${item.id}`}
                                      >
                                        <Edit2 className="h-3.5 w-3.5" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (window.confirm(language === 'ar' ? 'حذف هذا العنصر نهائياً؟' : 'Delete this item permanently?')) {
                                            deleteItem(item.id);
                                          }
                                        }}
                                        data-testid={`list-delete-${item.id}`}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  }
                  // GRID (default) — original rendering (uses `filteredItems` variable name below)
                  return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredItems.map((item) => (
                  <div key={item.id} className={`border rounded-xl p-4 hover:shadow-md transition-shadow relative ${isDarkMode ? 'border-white/10 bg-[#0F172A]' : ''}`}>
                    {/* SOLD OUT Badge - Top right corner only */}
                    {item.status === 'sold' && (
                      <div className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1 rounded-lg transform rotate-0 shadow-lg z-10">
                        <span className="text-sm font-bold tracking-wider">SOLD OUT</span>
                      </div>
                    )}
                    
                    {/* Special display for phone numbers */}
                    {(store.type === 'phone_numbers' || store.storeType === 'phone_numbers') ? (
                      <div className="mb-3">
                        <div 
                          className="flex items-center p-4 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 shadow-lg"
                        >
                          {/* Telecom Logo - Always on the left */}
                          {item.operator && (
                            <div className="flex-shrink-0 order-first">
                              <img 
                                src={telecomLogos[item.operator]} 
                                alt={item.operator}
                                className="h-12 w-12 object-contain rounded-lg bg-white/10 p-1"
                              />
                            </div>
                          )}
                          {/* Number Display — merchant sees the REAL number + a masked preview of how customers see it (iter-214/219) */}
                          <div className="flex-1 text-center relative min-h-[90px] flex flex-col items-center justify-center">
                            <span 
                              className="font-bold text-2xl md:text-3xl tracking-widest text-white"
                              dir="ltr"
                            >
                              {item.number}
                            </span>
                            {/* Masked preview — fixed slot so cards keep the SAME height with/without hiding */}
                            <div className="mt-2 min-h-[40px] flex flex-col items-center justify-center gap-1">
                              {Array.isArray(item.hiddenDigits) && item.hiddenDigits.length > 0 ? (
                                <div data-testid={`phone-mask-preview-${item.id}`} className="flex flex-col items-center gap-1">
                                  <span className={`text-[11px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {language === 'ar' ? 'كما يراه العملاء' : 'As customers see it'}
                                  </span>
                                  <span className="font-mono text-lg tracking-widest text-amber-400" dir="ltr">
                                    {applyMask(item.number, item.hiddenDigits)}
                                  </span>
                                </div>
                              ) : (
                                <span className={`text-[11px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  {language === 'ar' ? 'الرقم يظهر كاملاً للعملاء' : 'Fully visible to customers'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {/* Meta chips row — always rendered so cards keep the same height */}
                        <div className="flex flex-wrap gap-1.5 mt-2 justify-center min-h-[26px]">
                          {item.category && (
                            <span className={`inline-block px-2 py-1 text-xs rounded-full ${isDarkMode ? 'bg-[#1E293B] text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                              {item.category === 'vip' ? 'VIP' :
                               item.category === 'golden' ? (language === 'ar' ? 'ذهبي' : 'Golden') :
                               item.category === 'silver' ? (language === 'ar' ? 'فضي' : 'Silver') :
                               (language === 'ar' ? 'عادي' : 'Normal')}
                            </span>
                          )}
                          {/* iter-228: explicit hidden-state chip */}
                          {Array.isArray(item.hiddenDigits) && item.hiddenDigits.length > 0 ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full font-medium ${isDarkMode ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40' : 'bg-amber-100 text-amber-700 border border-amber-300'}`}
                                  data-testid={`phone-hidden-chip-${item.id}`}>
                              <EyeOff className="h-3 w-3" />
                              {language === 'ar' ? 'مخفي' : 'Hidden'}
                            </span>
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${isDarkMode ? 'bg-white/5 text-gray-400 border border-white/10' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}
                                  data-testid={`phone-visible-chip-${item.id}`}>
                              {language === 'ar' ? 'غير مخفي' : 'Visible'}
                            </span>
                          )}
                          {item.negotiable && (
                            <span className={`inline-block px-2 py-1 text-xs rounded-full font-medium ${isDarkMode ? 'bg-green-500/15 text-green-300 border border-green-500/40' : 'bg-green-100 text-green-700'}`}>
                              {language === 'ar' ? 'قابل للتفاوض' : 'Negotiable'}
                            </span>
                          )}
                        </div>
                        {/* Direct Edit Button for Phone */}
                        <Button
                          size="sm"
                          className="w-full mt-3 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingItem(item);
                            setShowEditPhonePage(true);
                          }}
                        >
                          <Edit2 className="h-4 w-4 me-2" />
                          {language === 'ar' ? 'تعديل' : 'Edit'}
                        </Button>
                      </div>
                    ) : (store.type === 'car_plates' || store.storeType === 'car_plates') && item.plateNumber ? (
                      /* Special display for car plates — merchant sees REAL plate + masked preview (iter-214/219) */
                      <div className="mb-3 relative">
                        {hasPlateDesign(item.emirate, item.plateType || 'private') ? (
                          <div className="flex justify-center">
                            <UAEPlate
                              emirate={item.emirate}
                              category={item.plateType || 'private'}
                              code={item.show_on_plate === false ? '' : item.plateCode}
                              number={item.plateNumber}
                              width={250}
                            />
                          </div>
                        ) : (
                          <UAEPlatePreview 
                            emirate={item.emirate}
                            plateCode={item.plateCode}
                            plateNumber={item.plateNumber}
                            plateType={item.plateType || 'private'}
                            language={language}
                          />
                        )}
                        {/* Masked preview — fixed slot so cards keep the SAME height */}
                        <div className="mt-2 min-h-[40px] flex flex-col items-center justify-center gap-1">
                          {Array.isArray(item.hiddenDigits) && item.hiddenDigits.length > 0 ? (
                            <div data-testid={`plate-mask-preview-${item.id}`} className="flex flex-col items-center gap-1">
                              <span className={`text-[11px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {language === 'ar' ? 'كما يراها العملاء' : 'As customers see it'}
                              </span>
                              <span className="font-mono text-lg tracking-widest text-amber-500" dir="ltr">
                                {(item.plateCode ? item.plateCode + ' ' : '') + applyMask(item.plateNumber, item.hiddenDigits)}
                              </span>
                            </div>
                          ) : (
                            <span className={`text-[11px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {language === 'ar' ? 'اللوحة تظهر كاملة للعملاء' : 'Fully visible to customers'}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2 justify-center min-h-[26px]">
                          {item.plateType && (
                            <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                              {item.plateType === 'private' ? (language === 'ar' ? 'خصوصي' : 'Private') :
                               item.plateType === 'motorcycle' ? (language === 'ar' ? 'دراجة' : 'Motorcycle') :
                               (language === 'ar' ? 'كلاسيك' : 'Classic')}
                            </span>
                          )}
                          {/* iter-228: hidden-state chip (same as phones) */}
                          {Array.isArray(item.hiddenDigits) && item.hiddenDigits.length > 0 ? (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full font-medium ${isDarkMode ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40' : 'bg-amber-100 text-amber-700 border border-amber-300'}`}
                                  data-testid={`plate-hidden-chip-${item.id}`}>
                              <EyeOff className="h-3 w-3" />
                              {language === 'ar' ? 'مخفي' : 'Hidden'}
                            </span>
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${isDarkMode ? 'bg-white/5 text-gray-400 border border-white/10' : 'bg-slate-100 text-slate-500 border border-slate-200'}`}
                                  data-testid={`plate-visible-chip-${item.id}`}>
                              {language === 'ar' ? 'غير مخفي' : 'Visible'}
                            </span>
                          )}
                          {item.negotiable && (
                            <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                              {language === 'ar' ? 'قابل للتفاوض' : 'Negotiable'}
                            </span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          className="w-full mt-3 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingItem(item);
                            setShowEditPlatePage(true);
                          }}
                          data-testid={`edit-plate-${item.id}`}
                        >
                          <Edit2 className="h-4 w-4 me-2" />
                          {language === 'ar' ? 'تعديل' : 'Edit'}
                        </Button>
                      </div>
                    ) : (
                      <>
                        {/* For auto/vehicles - show detailed card with Edit button */}
                        {(store.type === 'auto' || store.storeType === 'auto') ? (
                          <div className="space-y-2">
                            {/* Car Image */}
                            {(item.image || item.images?.[0]) && (
                              <img 
                                src={item.images?.[0] || item.image} 
                                alt={item.make + ' ' + item.model} 
                                className="w-full h-40 object-cover rounded-lg"
                              />
                            )}
                            {/* Car Name */}
                            <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                              {item.make} {item.model}
                            </h3>
                            {/* Car Details Grid */}
                            <div className={`grid grid-cols-2 gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {item.year && (
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-500">📅</span>
                                  <span>{item.year}</span>
                                </div>
                              )}
                              {item.mileage && (
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-500">🛣️</span>
                                  <span>{item.mileage?.toLocaleString()} km</span>
                                </div>
                              )}
                              {item.color && (
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-500">🎨</span>
                                  <span>{item.color}</span>
                                </div>
                              )}
                              {item.condition && (
                                <div className="flex items-center gap-1">
                                  <span className="text-gray-500">⭐</span>
                                  <span>{item.condition === 'new' ? (language === 'ar' ? 'جديد' : 'New') : (language === 'ar' ? 'مستعمل' : 'Used')}</span>
                                </div>
                              )}
                            </div>
                            {/* Direct Edit + Delete buttons */}
                            <div className="flex gap-2 mt-2">
                              <Button
                                size="sm"
                                className="flex-1 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingItem(item);
                                  setShowEditCarPage(true);
                                }}
                                data-testid={`car-card-edit-${item.id}`}
                              >
                                <Edit2 className="h-4 w-4 me-2" />
                                {language === 'ar' ? 'تعديل' : 'Edit'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(language === 'ar' ? 'حذف هذه السيارة نهائياً؟' : 'Delete this car permanently?')) {
                                    deleteItem(item.id);
                                  }
                                }}
                                data-testid={`car-card-delete-${item.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (store.type === 'real_estate' || store.storeType === 'real_estate') ? (
                          /* Real Estate Card — unified fixed layout */
                          <RealEstateItemCard
                            item={item}
                            language={language}
                            isDarkMode={isDarkMode}
                            onEdit={(it) => {
                              setEditingItem(it);
                              setShowEditPropertyPage(true);
                            }}
                            onDelete={(id) => deleteItem(id)}
                          />
                        ) : (
                          <>
                            {item.image && (
                              <img src={item.image} alt={item.name || item.title || item.number} className="w-full h-40 object-cover rounded-lg mb-3" />
                            )}
                            <h3 className={`font-semibold mb-1 line-clamp-2 min-h-[2.75rem] text-center ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`} title={item.name || item.title || item.number || item.plateNumber}>
                              {item.name || item.title || item.number || item.plateNumber || (item.make + ' ' + item.model)}
                            </h3>
                          </>
                        )}
                      </>
                    )}
                    {/* Price row — centered, uniform height/layout regardless of price mode (iter-228). */}
                    <div className="mt-auto pt-2 min-h-[36px] flex items-center justify-center">
                      {item.priceOnRequest ? (
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border ${
                            isDarkMode
                              ? 'bg-white/5 text-white/90 border-white/15'
                              : 'bg-slate-50 text-slate-700 border-slate-300'
                          }`}
                          data-testid={`price-on-request-${item.id}`}
                        >
                          <Phone className="h-3.5 w-3.5" />
                          {language === 'ar' ? 'اتصل لمعرفة السعر' : 'Call for price'}
                        </span>
                      ) : item.hasDiscount && item.salePrice ? (
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded">
                              -{item.discountPercentage}%
                            </span>
                            <span className={`text-xs line-through ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                              {item.price?.toLocaleString('en-US')}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-red-500 font-bold text-lg leading-none">{item.salePrice?.toLocaleString('en-US')}</span>
                            <img
                              src={isDarkMode ? '/currency-aed-white.png' : '/currency-aed-black.png'}
                              alt="AED"
                              className="h-4 w-auto"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <span className="text-[#1E9BD9] font-bold text-lg leading-none">{item.price?.toLocaleString('en-US')}</span>
                          <img
                            src={isDarkMode ? '/currency-aed-white.png' : '/currency-aed-black.png'}
                            alt="AED"
                            className="h-5 w-auto"
                          />
                        </div>
                      )}
                    </div>
                    {/* Action Buttons - Always at bottom */}
                    <div className="flex flex-wrap justify-center items-center mt-3 gap-2 pt-2 border-t border-white/10">
                      {/* Three action buttons based on item status */}
                      <div className="flex gap-1 flex-wrap">
                        {/* Mark as Sold - Only if active */}
                        {item.status === 'active' && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-orange-600 border-orange-300 hover:bg-orange-50 text-xs px-2"
                            onClick={() => markAsSold(item.id)}
                          >
                            <CheckCircle className="h-3 w-3 me-1" />
                            {language === 'ar' ? 'تم البيع' : 'Sold'}
                          </Button>
                        )}
                        
                        {/* Return to Sale - Only if sold */}
                        {item.status === 'sold' && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-green-600 border-green-300 hover:bg-green-50 text-xs px-2"
                            onClick={() => returnToSale(item.id)}
                          >
                            <RotateCcw className="h-3 w-3 me-1" />
                            {language === 'ar' ? 'إعادة للبيع' : 'Return'}
                          </Button>
                        )}
                        
                        {/* Archive button - Only after item is sold */}
                        {item.status === 'sold' && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className={`text-xs px-2 ${isDarkMode ? 'text-gray-400 border-gray-600 hover:bg-white/10' : 'text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                            onClick={() => archiveItem(item.id)}
                          >
                            <Archive className="h-3 w-3 me-1" />
                            {language === 'ar' ? 'أرشفة' : 'Archive'}
                          </Button>
                        )}

                        {/* Discount button - Only for active items */}
                        {item.status === 'active' && (
                          item.hasDiscount ? (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-red-500 border-red-300 hover:bg-red-50 text-xs px-2"
                              onClick={() => removeDiscount(item.id)}
                            >
                              <X className="h-3 w-3 me-1" />
                              {language === 'ar' ? 'إلغاء الخصم' : 'Remove'}
                            </Button>
                          ) : (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-[#1E9BD9] border-[#1E9BD9]/50 hover:bg-[#1E9BD9]/10 text-xs px-2"
                              onClick={() => openDiscountModal(item)}
                            >
                              <Percent className="h-3 w-3 me-1" />
                              {language === 'ar' ? 'خصم' : 'Discount'}
                            </Button>
                          )
                        )}

                        {/* Delete button — always available (iter-231) */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-300 hover:bg-red-50 text-xs px-2"
                          onClick={() => {
                            if (window.confirm(language === 'ar' ? 'حذف هذا العنصر نهائياً؟' : 'Delete this item permanently?')) {
                              deleteItem(item.id);
                            }
                          }}
                          data-testid={`card-delete-${item.id}`}
                        >
                          <Trash2 className="h-3 w-3 me-1" />
                          {language === 'ar' ? 'حذف' : 'Delete'}
                        </Button>
                      </div>
                      
                      {/* Status badge */}
                      {item.status === 'sold' && (
                        <span className="text-xs text-red-600 font-medium bg-red-50 px-2 py-1 rounded">
                          {language === 'ar' ? 'مباع' : 'Sold'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
                  );
                })()}
              </>
            )}
          </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <OrdersTab 
            storeId={storeId}
            language={language}
            isDarkMode={isDarkMode}
            BACKEND_URL={BACKEND_URL}
          />
        )}

        {/* iter-292 — Financial Movement Tab */}
        {activeTab === 'financial' && (
          <FinancialMovementTab
            storeId={storeId}
            language={language}
            isDarkMode={isDarkMode}
            BACKEND_URL={BACKEND_URL}
          />
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <ReviewsTab 
            storeId={storeId}
            language={language}
            isDarkMode={isDarkMode}
            BACKEND_URL={BACKEND_URL}
          />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Store Details */}
            <div className={`rounded-xl p-6 shadow-lg border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-[#E2E8F0]'}`}>
              <h2 className={`text-lg font-bold mb-6 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                <Settings className="h-5 w-5 text-[#1E9BD9]" />
                {language === 'ar' ? 'إعدادات المتجر' : 'Store Settings'}
              </h2>
              
              <div className="space-y-6">
                {/* Edit Store Info */}
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{language === 'ar' ? 'تعديل بيانات المتجر' : 'Edit Store Details'}</h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>{language === 'ar' ? 'تعديل الاسم والوصف' : 'Edit name and description'}</p>
                    </div>
                    <Button onClick={() => setShowEditModal(true)} variant="outline">
                      <Edit className="h-4 w-4 me-2" />
                      {language === 'ar' ? 'تعديل' : 'Edit'}
                    </Button>
                  </div>
                </div>

                {/* Store Contact Info — separate inline section */}
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`} data-testid="store-contact-section">
                  <div className="flex items-start justify-between mb-4 gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className={`font-medium flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        <Phone className="h-4 w-4 text-[#1E9BD9]" />
                        {language === 'ar' ? 'بيانات التواصل للمتجر' : 'Store Contact Info'}
                      </h3>
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>
                        {language === 'ar'
                          ? 'خاصة بهذا المتجر فقط، مختلفة عن بيانات حسابك الشخصي'
                          : 'Specific to this store, separate from your personal profile'}
                      </p>
                    </div>
                    {!editingContact ? (
                      <Button variant="outline" size="sm" onClick={() => {
                        setContactDraft({
                          contact_phone: store?.contact_phone || '',
                          contact_whatsapp: store?.contact_whatsapp || '',
                          contact_email: store?.contact_email || ''
                        });
                        setEditingContact(true);
                      }} data-testid="edit-contact-btn">
                        <Edit className="h-4 w-4 me-2" />
                        {language === 'ar' ? 'تعديل' : 'Edit'}
                      </Button>
                    ) : (
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setEditingContact(false)} data-testid="cancel-contact-btn">
                          {language === 'ar' ? 'إلغاء' : 'Cancel'}
                        </Button>
                        <Button size="sm" className="bg-[#1E9BD9]" onClick={saveContactInfo} disabled={saving} data-testid="save-contact-btn">
                          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (language === 'ar' ? 'حفظ' : 'Save')}
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className={`text-xs ${isDarkMode ? 'text-white/70' : ''}`}>{language === 'ar' ? 'رقم الاتصال' : 'Phone'}</Label>
                      <Input
                        value={editingContact ? contactDraft.contact_phone : (store?.contact_phone || '')}
                        onChange={(e) => setContactDraft({ ...contactDraft, contact_phone: e.target.value })}
                        readOnly={!editingContact}
                        dir="ltr"
                        className={`mt-1 ${!editingContact ? (isDarkMode ? 'bg-[#0F172A] border-white/5' : 'bg-slate-100') : ''}`}
                        placeholder="+971..."
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <Label className={`text-xs ${isDarkMode ? 'text-white/70' : ''}`}>{language === 'ar' ? 'واتساب' : 'WhatsApp'}</Label>
                      <Input
                        value={editingContact ? contactDraft.contact_whatsapp : (store?.contact_whatsapp || '')}
                        onChange={(e) => setContactDraft({ ...contactDraft, contact_whatsapp: e.target.value })}
                        readOnly={!editingContact}
                        dir="ltr"
                        className={`mt-1 ${!editingContact ? (isDarkMode ? 'bg-[#0F172A] border-white/5' : 'bg-slate-100') : ''}`}
                        placeholder="+971..."
                        data-testid="contact-whatsapp-input"
                      />
                    </div>
                    <div>
                      <Label className={`text-xs ${isDarkMode ? 'text-white/70' : ''}`}>{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label>
                      <Input
                        value={editingContact ? contactDraft.contact_email : (store?.contact_email || '')}
                        onChange={(e) => setContactDraft({ ...contactDraft, contact_email: e.target.value })}
                        readOnly={!editingContact}
                        dir="ltr"
                        type="email"
                        className={`mt-1 ${!editingContact ? (isDarkMode ? 'bg-[#0F172A] border-white/5' : 'bg-slate-100') : ''}`}
                        placeholder="store@example.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Logo & Banner Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Logo Upload */}
                  <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`}>
                    <h3 className={`font-medium mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      <Image className="h-4 w-4 text-[#1E9BD9]" />
                      {language === 'ar' ? 'شعار المتجر' : 'Store Logo'}
                    </h3>
                    <div className="flex items-center gap-4">
                      <div 
                        onClick={() => logoInputRef.current?.click()}
                        className={`w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center cursor-pointer hover:border-[#1E9BD9] transition-colors overflow-hidden ${isDarkMode ? 'border-gray-600 bg-[#1E293B]' : 'border-gray-300 bg-white'}`}
                      >
                        {newLogoPreview || store?.logo ? (
                          <img src={newLogoPreview || store?.logo} alt="Logo" className="w-full h-full object-cover" />
                        ) : (
                          <div className="text-center">
                            <Upload className="h-6 w-6 mx-auto text-gray-400" />
                            <p className="text-xs text-gray-400 mt-1">{language === 'ar' ? 'رفع' : 'Upload'}</p>
                          </div>
                        )}
                      </div>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              toast.error(language === 'ar' ? 'حجم الصورة يجب أن يكون أقل من 5 ميجابايت' : 'Image size must be less than 5MB');
                              return;
                            }
                            setNewLogo(file);
                            const reader = new FileReader();
                            reader.onloadend = () => setNewLogoPreview(reader.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="hidden"
                      />
                      <div className="flex-1">
                        <p className="text-sm text-gray-600 mb-2">
                          {language === 'ar' ? 'اضغط لتغيير الشعار' : 'Click to change logo'}
                        </p>
                        {newLogoPreview && (
                          <Button 
                            size="sm"
                            onClick={async () => {
                              const token = localStorage.getItem('business_token');
                              const formData = new FormData();
                              formData.append('logo', newLogo);
                              try {
                                await axios.put(`${BACKEND_URL}/api/stores/${storeId}/logo`, formData, {
                                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                                });
                                setStore({ ...store, logo: newLogoPreview });
                                setNewLogo(null);
                                setNewLogoPreview(null);
                                toast.success(language === 'ar' ? 'تم تحديث الشعار' : 'Logo updated');
                              } catch (error) {
                                toast.error(language === 'ar' ? 'فشل تحديث الشعار' : 'Failed to update logo');
                              }
                            }}
                            className="bg-[#1E9BD9]"
                          >
                            <Save className="h-3 w-3 me-1" />
                            {language === 'ar' ? 'حفظ' : 'Save'}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Banner Upload */}
                  <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-[#F8FAFC]'}`}>
                    <h3 className={`font-medium mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      <Layout className="h-4 w-4 text-[#F5A962]" />
                      {language === 'ar' ? 'صورة البانر' : 'Banner Image'}
                    </h3>
                    <div 
                      onClick={() => bannerInputRef.current?.click()}
                      className={`w-full h-24 rounded-xl border-2 border-dashed flex items-center justify-center cursor-pointer hover:border-[#F5A962] transition-colors overflow-hidden ${isDarkMode ? 'border-gray-600 bg-[#1E293B]' : 'border-gray-300 bg-white'}`}
                    >
                      {newBannerPreview || store?.banner ? (
                        <img src={newBannerPreview || store?.banner} alt="Banner" className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-center">
                          <Upload className="h-6 w-6 mx-auto text-gray-400" />
                          <p className="text-xs text-gray-400 mt-1">{language === 'ar' ? 'رفع صورة البانر' : 'Upload banner image'}</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={bannerInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          if (file.size > 10 * 1024 * 1024) {
                            toast.error(language === 'ar' ? 'حجم الصورة يجب أن يكون أقل من 10 ميجابايت' : 'Image size must be less than 10MB');
                            return;
                          }
                          setNewBanner(file);
                          const reader = new FileReader();
                          reader.onloadend = () => setNewBannerPreview(reader.result);
                          reader.readAsDataURL(file);
                        }
                      }}
                      className="hidden"
                    />
                    {newBannerPreview && (
                      <Button 
                        size="sm"
                        className="mt-3 bg-[#F5A962] hover:bg-[#E5994F]"
                        onClick={async () => {
                          const token = localStorage.getItem('business_token');
                          const formData = new FormData();
                          formData.append('banner', newBanner);
                          try {
                            await axios.put(`${BACKEND_URL}/api/stores/${storeId}/banner`, formData, {
                              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                            });
                            setStore({ ...store, banner: newBannerPreview });
                            setNewBanner(null);
                            setNewBannerPreview(null);
                            toast.success(language === 'ar' ? 'تم تحديث البانر' : 'Banner updated');
                          } catch (error) {
                            toast.error(language === 'ar' ? 'فشل تحديث البانر' : 'Failed to update banner');
                          }
                        }}
                      >
                        <Save className="h-3 w-3 me-1" />
                        {language === 'ar' ? 'حفظ البانر' : 'Save Banner'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Theme Customization */}
            <div className={`rounded-xl p-6 shadow-lg border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-[#E2E8F0]'}`}>
              <h2 className={`text-lg font-bold mb-6 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                <Palette className="h-5 w-5 text-[#F5A962]" />
                {language === 'ar' ? 'ثيم المتجر' : 'Store Theme'}
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Theme Controls */}
                <div className="space-y-6">
                  {/* Color Palettes */}
                  <div>
                    <Label className={`font-medium mb-3 block ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {language === 'ar' ? 'اختر ألوان جاهزة' : 'Choose Preset Colors'}
                    </Label>
                    <div className="grid grid-cols-4 gap-2">
                      {colorPalettes.map((palette) => (
                        <button
                          key={palette.name}
                          onClick={() => setStoreTheme({ ...storeTheme, primaryColor: palette.primary, secondaryColor: palette.secondary })}
                          className={`p-2 rounded-lg border-2 transition-all ${
                            storeTheme.primaryColor === palette.primary 
                              ? 'border-[#1E9BD9] ring-2 ring-[#1E9BD9]/30' 
                              : isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                          }`}
                          title={language === 'ar' ? palette.name : palette.nameEn}
                        >
                          <div className="flex gap-1">
                            <div className="w-6 h-6 rounded-full" style={{ backgroundColor: palette.primary }} />
                            <div className="w-6 h-6 rounded-full" style={{ backgroundColor: palette.secondary }} />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Colors */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className={`font-medium mb-2 block ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {language === 'ar' ? 'اللون الرئيسي' : 'Primary Color'}
                      </Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={storeTheme.primaryColor}
                          onChange={(e) => setStoreTheme({ ...storeTheme, primaryColor: e.target.value })}
                          className={`w-12 h-10 rounded-lg cursor-pointer border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}
                        />
                        <Input 
                          value={storeTheme.primaryColor}
                          onChange={(e) => setStoreTheme({ ...storeTheme, primaryColor: e.target.value })}
                          className="flex-1 font-mono text-sm"
                          dir="ltr"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className={`font-medium mb-2 block ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {language === 'ar' ? 'اللون الثانوي' : 'Secondary Color'}
                      </Label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={storeTheme.secondaryColor}
                          onChange={(e) => setStoreTheme({ ...storeTheme, secondaryColor: e.target.value })}
                          className={`w-12 h-10 rounded-lg cursor-pointer border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}
                        />
                        <Input 
                          value={storeTheme.secondaryColor}
                          onChange={(e) => setStoreTheme({ ...storeTheme, secondaryColor: e.target.value })}
                          className="flex-1 font-mono text-sm"
                          dir="ltr"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Card Style */}
                  <div>
                    <Label className={`font-medium mb-3 block ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {language === 'ar' ? 'شكل البطاقات' : 'Card Style'}
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: 'rounded', label: 'دائري', labelEn: 'Rounded' },
                        { id: 'sharp', label: 'حاد', labelEn: 'Sharp' },
                        { id: 'soft', label: 'ناعم', labelEn: 'Soft' }
                      ].map((style) => (
                        <button
                          key={style.id}
                          onClick={() => setStoreTheme({ ...storeTheme, cardStyle: style.id })}
                          className={`p-3 border-2 transition-all text-center ${
                            storeTheme.cardStyle === style.id 
                              ? 'border-[#1E9BD9] bg-[#1E9BD9]/5' 
                              : isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                          } ${style.id === 'rounded' ? 'rounded-xl' : style.id === 'sharp' ? 'rounded-none' : 'rounded-lg'}`}
                        >
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? style.label : style.labelEn}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Layout */}
                  <div>
                    <Label className={`font-medium mb-3 block ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {language === 'ar' ? 'طريقة العرض' : 'Layout'}
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => setStoreTheme({ ...storeTheme, layout: 'grid' })}
                        className={`p-4 border-2 rounded-xl transition-all flex flex-col items-center gap-2 ${
                          storeTheme.layout === 'grid' 
                            ? 'border-[#1E9BD9] bg-[#1E9BD9]/5' 
                            : isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="grid grid-cols-2 gap-1">
                          <div className={`w-4 h-4 rounded ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`} />
                          <div className={`w-4 h-4 rounded ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`} />
                          <div className={`w-4 h-4 rounded ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`} />
                          <div className={`w-4 h-4 rounded ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`} />
                        </div>
                        <span className={`text-xs ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'شبكة' : 'Grid'}</span>
                      </button>
                      <button
                        onClick={() => setStoreTheme({ ...storeTheme, layout: 'list' })}
                        className={`p-4 border-2 rounded-xl transition-all flex flex-col items-center gap-2 ${
                          storeTheme.layout === 'list' 
                            ? 'border-[#1E9BD9] bg-[#1E9BD9]/5' 
                            : isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="space-y-1">
                          <div className={`w-10 h-2 rounded ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`} />
                          <div className={`w-10 h-2 rounded ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`} />
                          <div className={`w-10 h-2 rounded ${isDarkMode ? 'bg-gray-500' : 'bg-gray-300'}`} />
                        </div>
                        <span className={`text-xs ${isDarkMode ? 'text-white' : ''}`}>{language === 'ar' ? 'قائمة' : 'List'}</span>
                      </button>
                    </div>
                  </div>

                  {/* Save Button */}
                  <Button 
                    onClick={async () => {
                      setSavingTheme(true);
                      const token = localStorage.getItem('business_token');
                      try {
                        await axios.put(`${BACKEND_URL}/api/stores/${storeId}/theme`, storeTheme, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success(language === 'ar' ? 'تم حفظ الثيم بنجاح' : 'Theme saved successfully');
                      } catch (error) {
                        toast.error(language === 'ar' ? 'فشل حفظ الثيم' : 'Failed to save theme');
                      } finally {
                        setSavingTheme(false);
                      }
                    }}
                    disabled={savingTheme}
                    className="w-full bg-[#1E9BD9] hover:bg-[#0369A1]"
                  >
                    {savingTheme ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 me-2" />}
                    {language === 'ar' ? 'حفظ الثيم' : 'Save Theme'}
                  </Button>
                </div>

                {/* Live Preview */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <Label className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {language === 'ar' ? 'معاينة المتجر' : 'Store Preview'}
                    </Label>
                    <div className={`flex gap-1 p-1 rounded-lg ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-100'}`}>
                      <button
                        onClick={() => setPreviewMode('desktop')}
                        className={`p-2 rounded-md transition-colors ${previewMode === 'desktop' ? isDarkMode ? 'bg-[#1E293B] shadow-sm' : 'bg-white shadow-sm' : ''}`}
                      >
                        <Monitor className={`h-4 w-4 ${isDarkMode ? 'text-white' : ''}`} />
                      </button>
                      <button
                        onClick={() => setPreviewMode('mobile')}
                        className={`p-2 rounded-md transition-colors ${previewMode === 'mobile' ? isDarkMode ? 'bg-[#1E293B] shadow-sm' : 'bg-white shadow-sm' : ''}`}
                      >
                        <Smartphone className={`h-4 w-4 ${isDarkMode ? 'text-white' : ''}`} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Preview Container */}
                  <div className={`border-2 rounded-xl overflow-hidden ${isDarkMode ? 'border-gray-600 bg-[#0F172A]' : 'border-gray-200 bg-gray-50'} ${
                    previewMode === 'mobile' ? 'max-w-[320px] mx-auto' : ''
                  }`}>
                    {/* Preview Header */}
                    <div 
                      className="p-4 text-white"
                      style={{ background: `linear-gradient(135deg, ${storeTheme.primaryColor}, ${storeTheme.secondaryColor})` }}
                    >
                      <div className="flex items-center gap-3">
                        {store?.logo ? (
                          <img src={store.logo} alt="" className="w-10 h-10 rounded-lg bg-white/20" />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                            <Store className="h-5 w-5" />
                          </div>
                        )}
                        <div>
                          <h3 className="font-bold text-sm">{store?.name || 'اسم المتجر'}</h3>
                          <p className="text-xs opacity-80">{storeConfig.name}</p>
                        </div>
                      </div>
                    </div>

                    {/* Preview Content */}
                    <div className={`p-4 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'}`}>
                      <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {language === 'ar' ? 'عرض المنتجات' : 'Products Display'}
                      </p>
                      <div className={storeTheme.layout === 'grid' ? 'grid grid-cols-2 gap-2' : 'space-y-2'}>
                        {[1, 2, 3, 4].map((i) => (
                          <div 
                            key={i}
                            className={`border p-2 ${isDarkMode ? 'border-white/10' : ''} ${
                              storeTheme.cardStyle === 'rounded' ? 'rounded-xl' : 
                              storeTheme.cardStyle === 'sharp' ? 'rounded-none' : 'rounded-lg'
                            } ${storeTheme.layout === 'list' ? 'flex items-center gap-3' : ''}`}
                          >
                            <div 
                              className={`${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-100'} ${storeTheme.layout === 'grid' ? 'h-16 mb-2' : 'w-12 h-12'} ${
                                storeTheme.cardStyle === 'rounded' ? 'rounded-lg' : 
                                storeTheme.cardStyle === 'sharp' ? 'rounded-none' : 'rounded-md'
                              }`}
                            />
                            <div className="flex-1">
                              <div className={`h-2 rounded w-3/4 mb-1 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`} />
                              <div 
                                className="h-2 rounded w-1/2"
                                style={{ backgroundColor: storeTheme.primaryColor + '40' }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Delete Store */}
            <div className={`rounded-xl p-6 shadow-lg border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-[#E2E8F0]'}`}>
              <div className="p-4 bg-red-50 rounded-xl border border-red-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-red-800">{language === 'ar' ? 'حذف المتجر' : 'Delete Store'}</h3>
                    <p className="text-sm text-red-600">{language === 'ar' ? 'حذف المتجر نهائياً - لا يمكن التراجع' : 'Permanently delete this store - cannot be undone'}</p>
                  </div>
                  <Button onClick={deleteStore} variant="outline" className="border-red-300 text-red-600 hover:bg-red-100">
                    <Trash2 className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'حذف' : 'Delete'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Negotiations Tab */}
        {activeTab === 'negotiations' && (
          <div className={`rounded-xl p-6 shadow-lg border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-[#E2E8F0]'}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                <MessageSquare className="h-5 w-5 text-[#1E9BD9]" />
                {language === 'ar' ? 'طلبات التفاوض' : 'Negotiation Requests'}
              </h2>
            </div>

            {/* Info Banner */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-sm text-blue-700">
                {language === 'ar' 
                  ? 'هنا ستستقبل استفسارات التفاوض من العملاء على منصة CloudGM. سيتمكن العملاء من إرسال عروض أسعار على الأرقام المعروضة.'
                  : 'Here you will receive negotiation inquiries from customers on the CloudGM platform. Customers can send price offers on displayed numbers.'
                }
              </p>
            </div>

            {loadingNegotiations ? (
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#1E9BD9]" />
              </div>
            ) : negotiations.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`} />
                <p className={`mb-2 ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>
                  {language === 'ar' ? 'لا توجد طلبات تفاوض حالياً' : 'No negotiation requests yet'}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>
                  {language === 'ar' 
                    ? 'عندما يرسل عميل عرض سعر، سيظهر هنا'
                    : 'When a customer sends a price offer, it will appear here'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {negotiations.map((neg) => (
                  <div key={neg.id} className={`border rounded-xl p-4 hover:shadow-md transition-shadow ${isDarkMode ? 'border-white/10 bg-[#0F172A]' : ''}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{neg.customer_name || 'عميل'}</p>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>{neg.customer_phone || neg.customer_email}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        neg.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        neg.status === 'accepted' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {neg.status === 'pending' ? (language === 'ar' ? 'قيد الانتظار' : 'Pending') :
                         neg.status === 'accepted' ? (language === 'ar' ? 'مقبول' : 'Accepted') :
                         (language === 'ar' ? 'مرفوض' : 'Rejected')}
                      </span>
                    </div>
                    <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? 'bg-[#1E293B]' : 'bg-gray-50'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-[#475569]'}`}>
                        <span className="font-medium">{language === 'ar' ? 'الرقم:' : 'Number:'}</span> {neg.item_number}
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-[#475569]'}`}>
                        <span className="font-medium">{language === 'ar' ? 'السعر المعروض:' : 'Original Price:'}</span> {formatPrice(neg.original_price)}
                      </p>
                      <p className="text-sm text-[#1E9BD9] font-bold">
                        <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-[#475569]'}`}>{language === 'ar' ? 'عرض العميل:' : 'Customer Offer:'}</span> {formatPrice(neg.offered_price)}
                      </p>
                      {neg.message && (
                        <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-300' : 'text-[#475569]'}`}>
                          <span className="font-medium">{language === 'ar' ? 'رسالة:' : 'Message:'}</span> {neg.message}
                        </p>
                      )}
                    </div>
                    {neg.status === 'pending' && (
                      <div className="mt-3 flex gap-2">
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={async () => {
                            const token = localStorage.getItem('business_token');
                            try {
                              await axios.put(`${BACKEND_URL}/api/negotiations/${neg.id}/accept`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success(language === 'ar' ? 'تم قبول العرض' : 'Offer accepted');
                              fetchNegotiations();
                            } catch (error) {
                              toast.error(language === 'ar' ? 'فشل قبول العرض' : 'Failed to accept offer');
                            }
                          }}
                        >
                          <CheckCircle className="h-4 w-4 me-1" />
                          {language === 'ar' ? 'قبول' : 'Accept'}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={async () => {
                            const token = localStorage.getItem('business_token');
                            try {
                              await axios.put(`${BACKEND_URL}/api/negotiations/${neg.id}/reject`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              toast.success(language === 'ar' ? 'تم رفض العرض' : 'Offer rejected');
                              fetchNegotiations();
                            } catch (error) {
                              toast.error(language === 'ar' ? 'فشل رفض العرض' : 'Failed to reject offer');
                            }
                          }}
                        >
                          <XCircle className="h-4 w-4 me-1" />
                          {language === 'ar' ? 'رفض' : 'Reject'}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Archive Tab */}
        {activeTab === 'archive' && (
          <div className={`rounded-xl p-6 shadow-lg border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-[#E2E8F0]'}`}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                <Archive className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                {language === 'ar' ? 'الأرشيف' : 'Archive'}
              </h2>
              <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {archivedItems.length} {language === 'ar' ? 'عنصر' : 'items'}
              </span>
            </div>
            
            {loadingArchive ? (
              <div className="flex justify-center py-12">
                <Loader2 className={`h-8 w-8 animate-spin ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              </div>
            ) : archivedItems.length === 0 ? (
              <div className="text-center py-12">
                <Archive className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`} />
                <p className={`font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>
                  {language === 'ar' ? 'الأرشيف فارغ' : 'Archive is empty'}
                </p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>
                  {language === 'ar' 
                    ? 'العناصر المؤرشفة ستظهر هنا'
                    : 'Archived items will appear here'
                  }
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {archivedItems.map((item) => (
                  <div 
                    key={item.id} 
                    className={`p-4 border rounded-xl opacity-75 ${isDarkMode ? 'border-white/10 bg-[#0F172A]' : 'border-gray-200 bg-gray-50'}`}
                  >
                    {/* Phone Number Display (archive) — merchant sees REAL + masked preview */}
                    {(store.type === 'phone_numbers' || store.storeType === 'phone_numbers') && (
                      <div className="text-center">
                        <p className="text-xl font-bold text-gray-500 font-mono tracking-wider" dir="ltr">
                          {item.number}
                        </p>
                        {Array.isArray(item.hiddenDigits) && item.hiddenDigits.length > 0 && (
                          <p className="text-xs font-mono tracking-widest text-amber-500 mt-1" dir="ltr">
                            {applyMask(item.number, item.hiddenDigits)}
                          </p>
                        )}
                        <div className="flex items-center justify-center gap-2 mt-2">
                          {item.operator && (
                            <img 
                              src={telecomLogos[item.operator]} 
                              alt={item.operator} 
                              className="h-5 opacity-50"
                            />
                          )}
                          {item.category && (
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-600">
                              {item.category}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Car Plates Display (archive) — merchant sees REAL plate + masked preview */}
                    {(store.type === 'car_plates' || store.storeType === 'car_plates') && (
                      <div className="text-center">
                        {hasPlateDesign(item.emirate, item.plateType || 'private') ? (
                          <div className="flex justify-center">
                            <UAEPlate
                              emirate={item.emirate}
                              category={item.plateType || 'private'}
                              code={item.show_on_plate === false ? '' : item.plateCode}
                              number={item.plateNumber}
                              width={250}
                            />
                          </div>
                        ) : (
                          <UAEPlatePreview 
                            emirate={item.emirate}
                            plateCode={item.plateCode}
                            plateNumber={item.plateNumber}
                            plateType={item.plateType || 'private'}
                            language={language}
                          />
                        )}
                        {Array.isArray(item.hiddenDigits) && item.hiddenDigits.length > 0 && (
                          <p className="text-xs font-mono tracking-widest text-amber-500 mt-1" dir="ltr">
                            {(item.plateCode ? item.plateCode + ' ' : '') + applyMask(item.plateNumber, item.hiddenDigits)}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-2 justify-center">
                          {item.plateType && (
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-200 text-gray-600">
                              {item.plateType === 'private' ? (language === 'ar' ? 'خصوصي' : 'Private') :
                               item.plateType === 'motorcycle' ? (language === 'ar' ? 'دراجة' : 'Motorcycle') :
                               (language === 'ar' ? 'كلاسيك' : 'Classic')}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Other items */}
                    {(store.type !== 'phone_numbers' && store.storeType !== 'phone_numbers') && (store.type !== 'car_plates' && store.storeType !== 'car_plates') && (
                      <div className="text-center">
                        <p className="text-lg font-bold text-gray-500">
                          {item.name || item.title || item.plateNumber || 'Item'}
                        </p>
                      </div>
                    )}
                    
                    <p className="text-gray-500 font-medium text-center mt-2">
                      {item.priceOnRequest
                        ? (language === 'ar' ? 'اتصل لمعرفة السعر' : 'Call for price')
                        : formatPrice(item.price)}
                    </p>
                    
                    <div className="flex justify-center gap-2 mt-3">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-blue-600 border-blue-300 hover:bg-blue-50"
                        onClick={() => restoreFromArchive(item.id)}
                      >
                        <RotateCcw className="h-4 w-4 me-1" />
                        {language === 'ar' ? 'استعادة' : 'Restore'}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => deleteItem(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <p className="text-xs text-gray-400 text-center mt-2">
                      {language === 'ar' ? 'تم الأرشفة:' : 'Archived:'} {new Date(item.archived_at).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US')}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Team Tab */}
        {activeTab === 'team' && (
          <div className={`rounded-xl p-6 shadow-lg border ${isDarkMode ? 'bg-[#1E293B] border-white/10' : 'bg-white border-[#E2E8F0]'}`}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-[#1E9BD9]" />
                <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  {language === 'ar' ? 'فريق العمل' : 'Team Members'}
                </h2>
              </div>
              <Button 
                onClick={() => setShowAddTeamModal(true)}
                className="bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
              >
                <Plus className="h-4 w-4 me-2" />
                {language === 'ar' ? 'إضافة عضو' : 'Add Member'}
              </Button>
            </div>

            {/* Owner Card */}
            <div className="mb-6">
              <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>{language === 'ar' ? 'مالك المتجر' : 'Store Owner'}</p>
              <div className="bg-gradient-to-r from-[#1E9BD9]/5 to-[#0D7AB5]/5 rounded-xl p-4 border border-[#1E9BD9]/20">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-gradient-to-r from-[#1E9BD9] to-[#0D7AB5] flex items-center justify-center">
                    <UserCircle className="h-8 w-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{store.owner_name || (language === 'ar' ? 'صاحب المتجر' : 'Store Owner')}</h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>{store.owner_email || '-'}</p>
                  </div>
                  <span className="px-3 py-1 bg-[#1E9BD9] text-white text-xs rounded-full font-medium">
                    {language === 'ar' ? 'مالك' : 'Owner'}
                  </span>
                </div>
              </div>
            </div>

            {/* Team Members List */}
            <div>
              <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>{language === 'ar' ? 'أعضاء الفريق' : 'Team Members'}</p>
              {teamMembers.length === 0 ? (
                <div className={`text-center py-12 rounded-xl border-2 border-dashed ${isDarkMode ? 'bg-[#0F172A] border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                  <Users className={`h-12 w-12 mx-auto mb-3 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`} />
                  <p className={`mb-1 ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>
                    {language === 'ar' ? 'لا يوجد أعضاء في الفريق' : 'No team members yet'}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>
                    {language === 'ar' ? 'أضف أعضاء لمساعدتك في إدارة المتجر' : 'Add members to help manage your store'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {teamMembers.map((member, idx) => (
                    <div key={idx} className={`rounded-xl p-4 flex items-center gap-4 ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <UserCircle className={`h-6 w-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{member.name}</h3>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>{member.email}</p>
                      </div>
                      <span className={`px-3 py-1 text-xs rounded-full font-medium ${
                        member.role === 'admin' 
                          ? 'bg-[#1E9BD9]/20 text-[#1E9BD9]' 
                          : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                      }`}>
                        {member.role === 'admin' 
                          ? (language === 'ar' ? 'مدير' : 'Admin')
                          : (language === 'ar' ? 'عضو' : 'Member')
                        }
                      </span>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Permissions Info */}
            <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                <Eye className="h-4 w-4" />
                {language === 'ar' ? 'صلاحيات الأعضاء' : 'Member Permissions'}
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• {language === 'ar' ? 'مدير: إدارة كاملة للمتجر والمنتجات' : 'Admin: Full store and product management'}</li>
                <li>• {language === 'ar' ? 'عضو: عرض وإضافة المنتجات فقط' : 'Member: View and add products only'}</li>
              </ul>
            </div>
          </div>
        )}
      </section>

      {/* Add Team Member Modal */}
      {showAddTeamModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl w-full max-w-md ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'}`}>
            <div className={`p-6 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : ''}`}>
              <h2 className={`text-xl font-bold font-heading ${isDarkMode ? 'text-white' : ''}`}>
                {language === 'ar' ? 'إضافة عضو جديد' : 'Add New Member'}
              </h2>
              <button onClick={() => setShowAddTeamModal(false)} className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
                <X className={`h-5 w-5 ${isDarkMode ? 'text-white' : ''}`} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <Label>{language === 'ar' ? 'الاسم' : 'Name'}</Label>
                <Input
                  value={newTeamMember.name}
                  onChange={(e) => setNewTeamMember({...newTeamMember, name: e.target.value})}
                  placeholder={language === 'ar' ? 'أدخل اسم العضو' : 'Enter member name'}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label>
                <Input
                  type="email"
                  value={newTeamMember.email}
                  onChange={(e) => setNewTeamMember({...newTeamMember, email: e.target.value})}
                  placeholder="email@example.com"
                  className="mt-2"
                  dir="ltr"
                />
              </div>
              <div>
                <Label>{language === 'ar' ? 'الصلاحية' : 'Role'}</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    onClick={() => setNewTeamMember({...newTeamMember, role: 'admin'})}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      newTeamMember.role === 'admin'
                        ? 'border-[#1E9BD9] bg-[#1E9BD9]/10 text-[#1E9BD9]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="font-medium">{language === 'ar' ? 'مدير' : 'Admin'}</span>
                  </button>
                  <button
                    onClick={() => setNewTeamMember({...newTeamMember, role: 'member'})}
                    className={`p-3 rounded-xl border-2 text-center transition-all ${
                      newTeamMember.role === 'member'
                        ? 'border-[#1E9BD9] bg-[#1E9BD9]/5 text-[#1E9BD9]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="font-medium">{language === 'ar' ? 'عضو' : 'Member'}</span>
                  </button>
                </div>
              </div>
            </div>
            <div className={`p-6 border-t flex gap-3 justify-end ${isDarkMode ? 'bg-[#0F172A] border-white/10' : ''}`}>
              <Button 
                variant="outline" 
                className={isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white' : ''}
                onClick={() => setShowAddTeamModal(false)}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button 
                className="bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
                disabled={!newTeamMember.name || !newTeamMember.email}
                onClick={() => {
                  setTeamMembers([...teamMembers, {...newTeamMember, id: Date.now()}]);
                  setNewTeamMember({ name: '', email: '', role: 'member' });
                  setShowAddTeamModal(false);
                  toast.success(language === 'ar' ? 'تمت إضافة العضو بنجاح' : 'Member added successfully');
                }}
              >
                {language === 'ar' ? 'إضافة' : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Store Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'}`}>
            <div className={`p-6 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : ''}`}>
              <h2 className={`text-xl font-bold font-heading ${isDarkMode ? 'text-white' : ''}`}>
                {language === 'ar' ? 'تعديل المتجر' : 'Edit Store'}
              </h2>
              <button onClick={() => setShowEditModal(false)} className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
                <X className={`h-5 w-5 ${isDarkMode ? 'text-white' : ''}`} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Store names — LOCKED after creation (per user directive iter-231). */}
              <div>
                <Label className={`flex items-center gap-1 ${isDarkMode ? 'text-white' : ''}`}>
                  {language === 'ar' ? 'اسم المتجر (عربي)' : 'Store Name (Arabic)'}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-md ${isDarkMode ? 'bg-white/10 text-white/50' : 'bg-slate-200 text-slate-500'}`}>
                    {language === 'ar' ? 'مقفول' : 'Locked'}
                  </span>
                </Label>
                <Input
                  value={editData.name}
                  readOnly
                  disabled
                  className={`mt-2 cursor-not-allowed opacity-70 ${isDarkMode ? 'bg-[#0F172A]/60' : 'bg-slate-100'}`}
                  data-testid="store-name-ar-input"
                />
              </div>
              <div>
                <Label className={`flex items-center gap-1 ${isDarkMode ? 'text-white' : ''}`}>
                  {language === 'ar' ? 'اسم المتجر (إنجليزي)' : 'Store Name (English)'}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-md ${isDarkMode ? 'bg-white/10 text-white/50' : 'bg-slate-200 text-slate-500'}`}>
                    {language === 'ar' ? 'مقفول' : 'Locked'}
                  </span>
                </Label>
                <Input
                  value={editData.nameEn}
                  readOnly
                  disabled
                  className={`mt-2 cursor-not-allowed opacity-70 ${isDarkMode ? 'bg-[#0F172A]/60' : 'bg-slate-100'}`}
                  dir="ltr"
                  data-testid="store-name-en-input"
                />
                <p className={`text-[11px] mt-1 ${isDarkMode ? 'text-white/40' : 'text-slate-500'}`}>
                  {language === 'ar'
                    ? 'الاسم لا يمكن تعديله بعد إنشاء المتجر. للتغيير، تواصل مع الدعم.'
                    : 'Store names cannot be changed after creation. Contact support if needed.'}
                </p>
              </div>

              {/* Arabic description */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className={isDarkMode ? 'text-white' : ''}>
                    {language === 'ar' ? 'الوصف (عربي)' : 'Description (Arabic)'}
                  </Label>
                  <button
                    type="button"
                    onClick={() => translateStoreDescription('ar', 'en')}
                    disabled={translatingDesc || !editData.description}
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border transition-colors disabled:opacity-40 ${
                      isDarkMode
                        ? 'text-[#38BDF8] border-[#38BDF8]/40 bg-[#38BDF8]/10 hover:bg-[#38BDF8]/20'
                        : 'text-[#0EA5E9] border-[#0EA5E9]/40 bg-[#0EA5E9]/10 hover:bg-[#0EA5E9]/20'
                    }`}
                    data-testid="translate-desc-ar-to-en"
                    title={language === 'ar' ? 'ترجم إلى الإنجليزية' : 'Translate to English'}
                  >
                    {translatingDesc === 'ar-en'
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Languages className="h-3 w-3" />}
                    <span>AR → EN</span>
                  </button>
                </div>
                <textarea
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg h-24 focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/20 ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                  data-testid="store-description-ar-input"
                  placeholder={language === 'ar' ? 'اكتب وصف المتجر بالعربية...' : 'Write Arabic description...'}
                />
              </div>

              {/* English description */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className={isDarkMode ? 'text-white' : ''}>
                    {language === 'ar' ? 'الوصف (إنجليزي)' : 'Description (English)'}
                  </Label>
                  <button
                    type="button"
                    onClick={() => translateStoreDescription('en', 'ar')}
                    disabled={translatingDesc || !editData.descriptionEn}
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border transition-colors disabled:opacity-40 ${
                      isDarkMode
                        ? 'text-[#38BDF8] border-[#38BDF8]/40 bg-[#38BDF8]/10 hover:bg-[#38BDF8]/20'
                        : 'text-[#0EA5E9] border-[#0EA5E9]/40 bg-[#0EA5E9]/10 hover:bg-[#0EA5E9]/20'
                    }`}
                    data-testid="translate-desc-en-to-ar"
                    title={language === 'ar' ? 'ترجم إلى العربية' : 'Translate to Arabic'}
                  >
                    {translatingDesc === 'en-ar'
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Languages className="h-3 w-3" />}
                    <span>EN → AR</span>
                  </button>
                </div>
                <textarea
                  value={editData.descriptionEn}
                  onChange={(e) => setEditData({ ...editData, descriptionEn: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-lg h-24 focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/20 ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                  dir="ltr"
                  data-testid="store-description-en-input"
                  placeholder="Write English description..."
                />
              </div>
            </div>

            <div className={`p-6 border-t flex gap-3 ${isDarkMode ? 'bg-[#0F172A] border-white/10' : 'bg-gray-50'}`}>
              <Button variant="outline" className="flex-1" onClick={() => setShowEditModal(false)}>
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button onClick={updateStore} disabled={saving} className="flex-1 bg-[#1E9BD9]">
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : (language === 'ar' ? 'حفظ' : 'Save')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Item Modal */}
      {showAddItemModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'}`}>
            <div className={`p-6 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : ''}`}>
              <h2 className={`text-xl font-bold font-heading ${isDarkMode ? 'text-white' : ''}`}>
                {language === 'ar' ? `إضافة ${storeConfig.itemName}` : `Add ${storeConfig.itemNameEn}`}
              </h2>
              <button 
                onClick={() => {
                  setShowAddItemModal(false);
                  setNewItem({});
                  setItemImage(null);
                  setItemImagePreview(null);
                  // Reset phone number form
                  setPhonePrefix('');
                  setPhoneNumber('');
                  setPhoneCategory('');
                  setPhonePrice('');
                  setPhoneDescription('');
                  setPhoneNegotiable(null);
                  setPhonePaymentType('');
                  setPhoneContract(null);
                }} 
                className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              >
                <X className={`h-5 w-5 ${isDarkMode ? 'text-white' : ''}`} />
              </button>
            </div>

            <div className={`p-6 space-y-4 ${isDarkMode ? 'bg-[#1E293B]' : ''}`}>
              {/* Custom Phone Number Form */}
              {(store.type === 'phone_numbers' || store.storeType === 'phone_numbers') ? (
                <>
                  {/* Prefix Selection */}
                  <div>
                    <Label className={`mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                      {language === 'ar' ? 'بادئة الرقم *' : 'Number Prefix *'}
                    </Label>
                    <select
                      value={phonePrefix}
                      onChange={(e) => setPhonePrefix(e.target.value)}
                      className={`w-full px-3 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/20 focus:border-[#1E9BD9] text-lg font-bold ${
                        isDarkMode ? 'bg-[#0F172A] border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'
                      }`}
                      data-testid="phone-prefix-select"
                    >
                      <option value="">{language === 'ar' ? 'اختر البادئة...' : 'Select prefix...'}</option>
                      {phonePrefixes.map((prefix) => (
                        <option key={prefix.value} value={prefix.value}>
                          {prefix.value} - {language === 'ar' ? prefix.operatorName : prefix.operatorNameEn}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Phone Number Input (7 digits only) */}
                  <div>
                    <Label className={`mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                      {language === 'ar' ? 'الرقم (7 أرقام) *' : 'Number (7 digits) *'}
                    </Label>
                    <Input
                      type="text"
                      value={phoneNumber}
                      onChange={(e) => {
                        // Only allow digits and max 7 characters
                        const value = e.target.value.replace(/\D/g, '').slice(0, 7);
                        setPhoneNumber(value);
                      }}
                      placeholder="1234567"
                      className={`text-lg font-bold tracking-wider ${
                        isDarkMode ? 'bg-[#0F172A] border-gray-600 text-white placeholder:text-gray-500' : ''
                      }`}
                      dir="ltr"
                      maxLength={7}
                      data-testid="phone-number-input"
                    />
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {language === 'ar' ? `${phoneNumber.length}/7 أرقام` : `${phoneNumber.length}/7 digits`}
                    </p>
                  </div>

                  {/* Number Preview with Telecom Logo - IMPROVED */}
                  {phonePrefix && phoneNumber && (
                    <div className="mt-4" data-testid="phone-preview-container">
                      <Label className={`mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                        {language === 'ar' ? 'معاينة الرقم' : 'Number Preview'}
                      </Label>
                      <div className="flex items-center p-4 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 shadow-lg">
                        {/* Telecom Logo - Always on the left */}
                        <div className="flex-shrink-0 order-first">
                          <img 
                            src={telecomLogos[getCurrentOperator()]} 
                            alt={getCurrentOperator() === 'etisalat' ? 'Etisalat' : 'Du'}
                            className="h-14 w-14 object-contain rounded-lg bg-white/10 p-1"
                            data-testid="telecom-logo"
                          />
                        </div>
                        {/* Number Display - Centered and larger */}
                        <div className="flex-1 text-center">
                          <span 
                            className="font-bold text-2xl md:text-3xl tracking-widest text-white"
                            dir="ltr"
                            data-testid="phone-number-display"
                          >
                            {phonePrefix}-{phoneNumber.padEnd(7, '_')}
                          </span>
                        </div>
                      </div>
                      {/* Operator Badge */}
                      <div className="mt-2 flex justify-center">
                        <span 
                          className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                            getCurrentOperator() === 'etisalat' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {language === 'ar' 
                            ? (getCurrentOperator() === 'etisalat' ? 'اتصالات' : 'دو')
                            : (getCurrentOperator() === 'etisalat' ? 'Etisalat by e&' : 'Du')
                          }
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Category */}
                  <div>
                    <Label className={`mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                      {language === 'ar' ? 'الفئة *' : 'Category *'}
                    </Label>
                    <select
                      value={phoneCategory}
                      onChange={(e) => setPhoneCategory(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/20 ${
                        isDarkMode ? 'bg-[#0F172A] border-gray-600 text-white' : 'bg-white border-gray-200'
                      }`}
                      data-testid="phone-category-select"
                      required
                    >
                      <option value="">{language === 'ar' ? 'اختر الفئة...' : 'Select category...'}</option>
                      <option value="vip">VIP</option>
                      <option value="golden">{language === 'ar' ? 'ذهبي' : 'Golden'}</option>
                      <option value="silver">{language === 'ar' ? 'فضي' : 'Silver'}</option>
                      <option value="normal">{language === 'ar' ? 'عادي' : 'Normal'}</option>
                    </select>
                  </div>

                  {/* Payment Type - Prepaid/Postpaid */}
                  <div>
                    <Label className={`mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                      {language === 'ar' ? 'نوع الفاتورة *' : 'Invoice Type *'}
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setPhonePaymentType('prepaid')}
                        className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          phonePaymentType === 'prepaid'
                            ? 'border-green-500 bg-green-500/20 text-green-500'
                            : isDarkMode 
                              ? 'border-gray-600 text-gray-400 hover:border-gray-500' 
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {language === 'ar' ? 'مسبق الدفع' : 'Prepaid'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPhonePaymentType('postpaid')}
                        className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          phonePaymentType === 'postpaid'
                            ? 'border-blue-500 bg-blue-500/20 text-blue-500'
                            : isDarkMode 
                              ? 'border-gray-600 text-gray-400 hover:border-gray-500' 
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {language === 'ar' ? 'آجل الدفع' : 'Postpaid'}
                      </button>
                    </div>
                  </div>

                  {/* Contract - Yes/No */}
                  <div>
                    <Label className={`mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                      {language === 'ar' ? 'العقد *' : 'Contract *'}
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setPhoneContract(true)}
                        className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          phoneContract === true
                            ? 'border-[#1E9BD9] bg-[#1E9BD9]/20 text-[#1E9BD9]'
                            : isDarkMode 
                              ? 'border-gray-600 text-gray-400 hover:border-gray-500' 
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {language === 'ar' ? 'عقد' : 'With Contract'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPhoneContract(false)}
                        className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          phoneContract === false
                            ? 'border-orange-500 bg-orange-500/20 text-orange-500'
                            : isDarkMode 
                              ? 'border-gray-600 text-gray-400 hover:border-gray-500' 
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {language === 'ar' ? 'بدون عقد' : 'No Contract'}
                      </button>
                    </div>
                  </div>

                  {/* Negotiable - Yes/No */}
                  <div>
                    <Label className={`mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                      {language === 'ar' ? 'قابل للتفاوض *' : 'Negotiable *'}
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setPhoneNegotiable(true)}
                        className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          phoneNegotiable === true
                            ? 'border-green-500 bg-green-500/20 text-green-500'
                            : isDarkMode 
                              ? 'border-gray-600 text-gray-400 hover:border-gray-500' 
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {language === 'ar' ? 'قابل للتفاوض' : 'Negotiable'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setPhoneNegotiable(false)}
                        className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                          phoneNegotiable === false
                            ? 'border-[#1E9BD9] bg-[#1E9BD9]/20 text-[#1E9BD9]'
                            : isDarkMode 
                              ? 'border-gray-600 text-gray-400 hover:border-gray-500' 
                              : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {language === 'ar' ? 'غير قابل للتفاوض' : 'Non-negotiable'}
                      </button>
                    </div>
                  </div>

                  {/* Price with AED image */}
                  <div>
                    <Label className={`mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                      {language === 'ar' ? 'السعر *' : 'Price *'}
                    </Label>
                    <div className="relative">
                      <Input
                        type="number" inputMode="decimal"
                        value={phonePrice}
                        onChange={(e) => setPhonePrice(e.target.value)}
                        placeholder="0"
                        className={`text-lg pe-16 ${
                          isDarkMode ? 'bg-[#0F172A] border-gray-600 text-white placeholder:text-gray-500' : ''
                        }`}
                        data-testid="phone-price-input"
                      />
                      <div className="absolute inset-y-0 end-3 flex items-center pointer-events-none">
                        <img 
                          src={isDarkMode ? '/currency-aed-white.png' : '/currency-aed-black.png'} 
                          alt="AED" 
                          className="h-5 w-auto"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <Label className={`mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                      {language === 'ar' ? 'ملاحظات' : 'Notes'}
                    </Label>
                    <textarea
                      value={phoneDescription}
                      onChange={(e) => setPhoneDescription(e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg h-20 focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/20 ${
                        isDarkMode ? 'bg-[#0F172A] border-gray-600 text-white placeholder:text-gray-500' : ''
                      }`}
                      data-testid="phone-description-input"
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Image Upload - Only for types that allow it */}
                  {!storeConfig.noImageUpload && (
                    storeConfig.multipleImages ? (
                      // Multiple images for vehicles (5 images: 1 main + 4 extra)
                      <div>
                        <Label className="mb-3 block font-bold">
                          {language === 'ar' ? 'صور السيارة (5 صور كحد أقصى)' : 'Vehicle Images (up to 5)'}
                        </Label>
                        <div className="grid grid-cols-5 gap-3">
                          {[0, 1, 2, 3, 4].map((idx) => (
                            <div key={idx} className="relative">
                              <div 
                                onClick={() => vehicleImageRefs[idx].current?.click()}
                                className={`aspect-square rounded-xl border-2 border-dashed flex items-center justify-center cursor-pointer transition-all overflow-hidden ${
                                  idx === 0 
                                    ? 'border-[#1E9BD9] bg-[#1E9BD9]/5' 
                                    : 'border-gray-300 hover:border-gray-400'
                                }`}
                              >
                                {vehicleImagePreviews[idx] ? (
                                  <img src={vehicleImagePreviews[idx]} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                                ) : (
                                  <div className="text-center p-2">
                                    <Upload className={`h-5 w-5 mx-auto ${idx === 0 ? 'text-[#1E9BD9]' : 'text-gray-400'}`} />
                                    <p className={`text-[10px] mt-1 ${idx === 0 ? 'text-[#1E9BD9] font-medium' : 'text-gray-400'}`}>
                                      {idx === 0 
                                        ? (language === 'ar' ? 'رئيسية' : 'Main')
                                        : (language === 'ar' ? `صورة ${idx}` : `Image ${idx}`)
                                      }
                                    </p>
                                  </div>
                                )}
                              </div>
                              {vehicleImagePreviews[idx] && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); removeVehicleImage(idx); }}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-lg hover:bg-red-600"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                              <input
                                ref={vehicleImageRefs[idx]}
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleVehicleImageUpload(idx, e)}
                                className="hidden"
                              />
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {language === 'ar' 
                            ? 'الصورة الأولى هي الصورة الرئيسية التي ستظهر في الإعلان' 
                            : 'The first image is the main image that will appear in the listing'}
                        </p>
                      </div>
                    ) : (
                      // Single image upload for other store types
                      <div>
                        <Label className="mb-2 block">{language === 'ar' ? 'صورة' : 'Image'}</Label>
                        <div 
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full h-32 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-[#1E9BD9] transition-colors overflow-hidden"
                        >
                          {itemImagePreview ? (
                            <img src={itemImagePreview} alt="Preview" className="w-full h-full object-cover" />
                          ) : (
                            <div className="text-center">
                              <Upload className="h-8 w-8 mx-auto text-gray-400" />
                              <p className="text-xs text-gray-400 mt-1">{language === 'ar' ? 'رفع صورة' : 'Upload image'}</p>
                            </div>
                          )}
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </div>
                    )
                  )}

                  {/* Dynamic Fields */}
                  {storeConfig.fields.map((field, fieldIndex) => {
                    // Check if field should be shown based on listingType (for real estate)
                    if (field.showFor) {
                      const currentListingType = newItem.listingType;
                      if (currentListingType && field.showFor !== currentListingType) {
                        return null; // Hide field if it doesn't match the listing type
                      }
                    }
                    
                    return (
                    <div key={field.id}>
                      <Label className={isDarkMode ? 'text-white' : ''}>
                        {language === 'ar' ? field.label : field.labelEn}
                        {field.required && ' *'}
                      </Label>
                      {field.type === 'select' ? (
                        <select
                          value={newItem[field.id] || ''}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            const updatedItem = { ...newItem, [field.id]: newValue };
                            
                            // Special handling for car plates
                            if (store.type === 'car_plates') {
                              // If plate type changed to classic, auto-set code to 'classic'
                              if (field.id === 'plateType' && newValue === 'classic') {
                                updatedItem.plateCode = 'classic';
                                setPlateCodeError('');
                              }
                              // If plate type changed from classic, reset code if it was 'classic'
                              else if (field.id === 'plateType' && newItem.plateCode === 'classic') {
                                updatedItem.plateCode = '';
                              }
                              // If emirate or plateType changed, validate current code
                              if (field.id === 'emirate' || field.id === 'plateType') {
                                const validation = validatePlateCodeCompatibility(
                                  updatedItem.plateCode, 
                                  updatedItem.plateType, 
                                  language
                                );
                                setPlateCodeError(validation.valid ? '' : validation.message);
                              }
                            }
                            
                            setNewItem(updatedItem);
                          }}
                          className={`mt-2 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/20 ${
                            isDarkMode 
                              ? 'bg-[#0F172A] border-white/10 text-white' 
                              : 'bg-white border-gray-200 text-gray-900'
                          }`}
                        >
                          <option value="">{language === 'ar' ? 'اختر...' : 'Select...'}</option>
                          {field.options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {language === 'ar' ? opt.label : opt.labelEn}
                            </option>
                          ))}
                        </select>
                      ) : field.type === 'dynamic_select' ? (
                        // Dynamic select for plate codes based on emirate and plateType
                        (() => {
                          const availableCodes = getAvailablePlateCodes(newItem.emirate, newItem.plateType);
                          const isDisabled = !newItem.emirate || !newItem.plateType;
                          const isClassicLocked = newItem.plateType === 'classic';
                          
                          return (
                            <div>
                              <select
                                value={newItem[field.id] || ''}
                                disabled={isDisabled}
                                onChange={(e) => {
                                  const newCode = e.target.value;
                                  const validation = validatePlateCodeCompatibility(newCode, newItem.plateType, language);
                                  setPlateCodeError(validation.valid ? '' : validation.message);
                                  setNewItem({ ...newItem, [field.id]: newCode });
                                }}
                                className={`mt-2 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/20 ${
                                  isDisabled 
                                    ? isDarkMode ? 'bg-[#1E293B] cursor-not-allowed text-gray-500' : 'bg-gray-100 cursor-not-allowed' 
                                    : isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : 'bg-white border-gray-200'
                                } ${isClassicLocked ? isDarkMode ? 'bg-amber-900/30' : 'bg-amber-50' : ''}`}
                              >
                                <option value="">
                                  {isDisabled 
                                    ? (language === 'ar' ? 'اختر الإمارة والصنف أولاً' : 'Select emirate and type first')
                                    : (language === 'ar' ? 'اختر...' : 'Select...')}
                                </option>
                                {availableCodes.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {language === 'ar' ? opt.label : opt.labelEn}
                                  </option>
                                ))}
                              </select>
                              {plateCodeError && (
                                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                                  <XCircle className="h-4 w-4" />
                                  {plateCodeError}
                                </p>
                              )}
                              {isClassicLocked && (
                                <p className={`mt-1 text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                  {language === 'ar' ? 'لوحات كلاسيك تستخدم كود كلاسيك فقط' : 'Classic plates use Classic code only'}
                                </p>
                              )}
                            </div>
                          );
                        })()
                      ) : field.type === 'negotiable' ? (
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => setNewItem({ ...newItem, [field.id]: 'yes' })}
                            className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                              newItem[field.id] === 'yes'
                                ? 'border-green-500 bg-green-50 text-green-700'
                                : isDarkMode 
                                  ? 'border-white/10 text-white hover:border-white/20' 
                                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            {language === 'ar' ? 'قابل للتفاوض' : 'Negotiable'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setNewItem({ ...newItem, [field.id]: 'no' })}
                            className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                              newItem[field.id] === 'no'
                                ? 'border-red-500 bg-red-50 text-red-700'
                                : isDarkMode 
                                  ? 'border-white/10 text-white hover:border-white/20' 
                                  : 'border-gray-200 text-gray-600 hover:border-gray-300'
                            }`}
                          >
                            {language === 'ar' ? 'غير قابل للتفاوض' : 'Non-negotiable'}
                          </button>
                        </div>
                      ) : field.type === 'year_select' ? (
                        <select
                          value={newItem[field.id] || ''}
                          onChange={(e) => setNewItem({ ...newItem, [field.id]: e.target.value })}
                          className={`mt-2 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/20 ${
                            isDarkMode 
                              ? 'bg-[#0F172A] border-white/10 text-white' 
                              : 'bg-white border-gray-200 text-gray-900'
                          }`}
                        >
                          <option value="">{language === 'ar' ? 'اختر سنة الصنع...' : 'Select year...'}</option>
                          {Array.from({ length: 2027 - 1920 + 1 }, (_, i) => 2027 - i).map((year) => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                      ) : field.type === 'radio' ? (
                        <div className="mt-2 flex flex-wrap gap-3">
                          {field.options.map((opt) => (
                            <label 
                              key={opt.value} 
                              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 cursor-pointer transition-all ${
                                newItem[field.id] === opt.value
                                  ? 'border-[#1E9BD9] bg-[#1E9BD9]/10 text-[#1E9BD9]'
                                  : isDarkMode
                                    ? 'border-white/10 text-white hover:border-white/20'
                                    : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name={field.id}
                                value={opt.value}
                                checked={newItem[field.id] === opt.value}
                                onChange={(e) => setNewItem({ ...newItem, [field.id]: e.target.value })}
                                className="w-4 h-4 text-[#1E9BD9] focus:ring-[#1E9BD9]"
                              />
                              <span className="font-medium">{language === 'ar' ? opt.label : opt.labelEn}</span>
                            </label>
                          ))}
                        </div>
                      ) : field.type === 'multiselect' ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {field.options.map(option => {
                            const isSelected = (newItem[field.id] || []).includes(option.value);
                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() => {
                                  const currentValues = newItem[field.id] || [];
                                  const newValues = isSelected
                                    ? currentValues.filter(v => v !== option.value)
                                    : [...currentValues, option.value];
                                  setNewItem({ ...newItem, [field.id]: newValues });
                                }}
                                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                                  isSelected
                                    ? 'bg-[#1E9BD9] text-white'
                                    : isDarkMode
                                      ? 'bg-[#1E293B] text-gray-300 hover:bg-[#334155]'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {language === 'ar' ? option.label : option.labelEn}
                              </button>
                            );
                          })}
                        </div>
                      ) : field.type === 'date' ? (
                        <Input
                          type="date"
                          value={newItem[field.id] || ''}
                          onChange={(e) => setNewItem({ ...newItem, [field.id]: e.target.value })}
                          className={`mt-2 ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                        />
                      ) : field.type === 'textarea' ? (
                        <textarea
                          value={newItem[field.id] || ''}
                          onChange={(e) => setNewItem({ ...newItem, [field.id]: e.target.value })}
                          className={`mt-2 w-full px-3 py-2 border rounded-lg h-20 focus:outline-none focus:ring-2 focus:ring-[#1E9BD9]/20 ${
                            isDarkMode 
                              ? 'bg-[#0F172A] border-white/10 text-white placeholder-gray-500' 
                              : 'bg-white border-gray-200 text-gray-900'
                          }`}
                        />
                      ) : (
                        <Input
                          type={field.type}
                          value={newItem[field.id] || ''}
                          onChange={(e) => setNewItem({ ...newItem, [field.id]: e.target.value })}
                          className={`mt-2 ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                        />
                      )}
                      
                      {/* Show plate preview after plateCode field when all required fields are filled */}
                      {store.type === 'car_plates' && field.id === 'plateCode' && 
                       newItem.plateNumber && newItem.emirate && newItem.plateType && newItem.plateCode && (
                        <div className="mt-4 p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
                          <Label className="mb-3 block font-bold text-center text-gray-700">
                            {language === 'ar' ? 'معاينة اللوحة' : 'Plate Preview'}
                          </Label>
                          <UAEPlatePreview 
                            emirate={newItem.emirate}
                            plateCode={newItem.plateCode}
                            plateNumber={newItem.plateNumber}
                            plateType={newItem.plateType}
                            language={language}
                          />
                        </div>
                      )}
                    </div>
                  );
                  })}
                </>
              )}
            </div>

            <div className={`p-6 border-t flex gap-3 ${isDarkMode ? 'bg-[#0F172A] border-white/10' : 'bg-gray-50'}`}>
              <Button 
                variant="outline" 
                className={`flex-1 ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white' : ''}`}
                onClick={() => {
                  setShowAddItemModal(false);
                  setNewItem({});
                  setItemImage(null);
                  setItemImagePreview(null);
                  // Reset phone number form
                  setPhonePrefix('');
                  setPhoneNumber('');
                  setPhoneCategory('');
                  setPhonePrice('');
                  setPhoneDescription('');
                  setPhoneNegotiable(null);
                  setPhonePaymentType('');
                  setPhoneContract(null);
                }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button 
                onClick={() => {
                  if (store.type === 'phone_numbers' || store.storeType === 'phone_numbers') {
                    // Validate phone number form
                    if (!phonePrefix || phoneNumber.length !== 7 || !phonePrice) {
                      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة (البادئة، 7 أرقام، السعر)' : 'Please fill all required fields (prefix, 7 digits, price)');
                      return;
                    }
                    // Build the full phone number
                    const fullNumber = `${phonePrefix}-${phoneNumber}`;
                    const operator = getCurrentOperator();
                    // Set newItem and call addItem
                    setNewItem({
                      number: fullNumber,
                      prefix: phonePrefix,
                      digits: phoneNumber,
                      operator: operator,
                      category: phoneCategory,
                      price: phonePrice,
                      description: phoneDescription
                    });
                    // Call addItem after state update
                    setTimeout(() => addItem(), 0);
                  } else {
                    addItem();
                  }
                }} 
                disabled={saving || ((store.type === 'phone_numbers' || store.storeType === 'phone_numbers') && (phoneNumber.length !== 7 || !phonePrefix || !phonePrice))} 
                className={`flex-1 bg-gradient-to-r ${storeConfig.color}`}
              >
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : (language === 'ar' ? 'إضافة' : 'Add')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload CSV/Excel Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl w-full max-w-lg ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'}`}>
            <div className={`p-6 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : ''}`}>
              <h2 className={`text-xl font-bold font-heading flex items-center gap-2 ${isDarkMode ? 'text-white' : ''}`}>
                <FileSpreadsheet className="h-5 w-5 text-[#1E9BD9]" />
                {language === 'ar' ? 'رفع ملف الأرقام' : 'Upload Numbers File'}
              </h2>
              <button 
                onClick={() => {
                  setShowBulkUploadModal(false);
                  setBulkUploadFile(null);
                }} 
                className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              >
                <X className={`h-5 w-5 ${isDarkMode ? 'text-white' : ''}`} />
              </button>
            </div>

            <div className={`p-6 space-y-4 ${isDarkMode ? 'bg-[#1E293B]' : ''}`}>
              {/* Instructions */}
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[#0F172A] border border-[#1E9BD9]/30' : 'bg-blue-50 border border-blue-200'}`}>
                <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-[#1E9BD9]' : 'text-blue-700'}`}>
                  {language === 'ar' ? 'الأعمدة المطلوبة في الملف:' : 'Required columns in file:'}
                </p>
                <ul className={`text-xs space-y-1 list-disc list-inside ${isDarkMode ? 'text-gray-300' : 'text-blue-600'}`}>
                  <li><strong>phone_number</strong> - {language === 'ar' ? 'رقم الهاتف (مثال: 050-1234567)' : 'Phone number (e.g. 050-1234567)'}</li>
                  <li><strong>category</strong> - {language === 'ar' ? 'الفئة (vip, golden, silver, normal)' : 'Category (vip, golden, silver, normal)'}</li>
                  <li><strong>price</strong> - {language === 'ar' ? 'السعر بالدرهم' : 'Price in AED'}</li>
                  <li><strong>payment_type</strong> - {language === 'ar' ? 'نوع الفاتورة (prepaid, postpaid)' : 'Invoice type (prepaid, postpaid)'}</li>
                  <li><strong>contract</strong> - {language === 'ar' ? 'العقد (yes, no)' : 'Contract (yes, no)'}</li>
                  <li><strong>negotiable</strong> - {language === 'ar' ? 'قابل للتفاوض (yes, no)' : 'Negotiable (yes, no)'}</li>
                  <li><strong>description</strong> - {language === 'ar' ? 'الوصف/الملاحظات' : 'Description/Notes'}</li>
                </ul>
              </div>

              {/* Sample Template Download */}
              <div className="flex items-center justify-center gap-4">
                <a 
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    // Generate sample CSV with all fields
                    const csvContent = `phone_number,category,price,payment_type,contract,negotiable,description
050-1234567,vip,5000,prepaid,yes,yes,رقم مميز VIP سهل الحفظ
055-7654321,golden,3000,postpaid,no,yes,رقم ذهبي مميز
052-1111111,silver,1500,prepaid,yes,no,رقم فضي
058-9999999,normal,500,prepaid,no,no,رقم عادي`;
                    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = 'phone_numbers_template.csv';
                    link.click();
                  }}
                  className={`text-sm flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'bg-[#1E9BD9]/20 text-[#1E9BD9] hover:bg-[#1E9BD9]/30' 
                      : 'bg-[#1E9BD9]/10 text-[#1E9BD9] hover:bg-[#1E9BD9]/20'
                  }`}
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  {language === 'ar' ? 'تحميل نموذج CSV' : 'Download CSV Template'}
                </a>
                
                <a 
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    // Generate Excel-compatible CSV
                    const csvContent = `phone_number,category,price,payment_type,contract,negotiable,description
050-1234567,vip,5000,prepaid,yes,yes,رقم مميز VIP سهل الحفظ
055-7654321,golden,3000,postpaid,no,yes,رقم ذهبي مميز
052-1111111,silver,1500,prepaid,yes,no,رقم فضي
058-9999999,normal,500,prepaid,no,no,رقم عادي`;
                    const blob = new Blob(['\ufeff' + csvContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = 'phone_numbers_template.xls';
                    link.click();
                  }}
                  className={`text-sm flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isDarkMode 
                      ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                      : 'bg-green-500/10 text-green-600 hover:bg-green-500/20'
                  }`}
                >
                  <FileSpreadsheet className="h-4 w-4" />
                  {language === 'ar' ? 'تحميل نموذج Excel' : 'Download Excel Template'}
                </a>
              </div>

              {/* File Upload Area */}
              <div 
                onClick={() => csvInputRef.current?.click()}
                className={`w-full h-40 rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-colors ${
                  bulkUploadFile 
                    ? isDarkMode 
                      ? 'border-green-400 bg-green-500/10' 
                      : 'border-green-400 bg-green-50'
                    : isDarkMode
                      ? 'border-gray-600 hover:border-[#1E9BD9] bg-[#0F172A]'
                      : 'border-gray-300 hover:border-[#1E9BD9]'
                }`}
              >
                {bulkUploadFile ? (
                  <div className="text-center">
                    <CheckCircle className="h-10 w-10 mx-auto text-green-500 mb-2" />
                    <p className={`font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>{bulkUploadFile.name}</p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {(bulkUploadFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload className={`h-10 w-10 mx-auto mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {language === 'ar' ? 'اضغط لرفع ملف CSV أو Excel' : 'Click to upload CSV or Excel file'}
                    </p>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      .csv, .xlsx, .xls
                    </p>
                  </div>
                )}
              </div>
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setBulkUploadFile(file);
                  }
                }}
                className="hidden"
              />

              {/* Progress Bar */}
              {bulkUploading && (
                <div className={`w-full rounded-full h-2.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div 
                    className="bg-[#1E9BD9] h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${bulkUploadProgress}%` }}
                  />
                </div>
              )}
            </div>

            <div className={`p-6 border-t flex gap-3 ${isDarkMode ? 'bg-[#0F172A] border-white/10' : 'bg-gray-50'}`}>
              <Button 
                variant="outline" 
                className={`flex-1 ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white' : ''}`}
                onClick={() => {
                  setShowBulkUploadModal(false);
                  setBulkUploadFile(null);
                }}
                disabled={bulkUploading}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button 
                onClick={handleBulkUpload}
                disabled={!bulkUploadFile || bulkUploading}
                className="flex-1 bg-[#1E9BD9]"
              >
                {bulkUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin me-2" />
                    {bulkUploadProgress}%
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'رفع وإضافة' : 'Upload & Add'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* AI-Powered Image Editor Modal */}
      {showImageEditor && (pendingImage || (editingImageIndex !== null && productImages[editingImageIndex])) && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`rounded-2xl w-full max-w-4xl shadow-2xl my-4 flex flex-col ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'}`} style={{ maxHeight: 'calc(100vh - 32px)' }}>
            {/* Header */}
            <div className={`p-4 border-b flex items-center justify-between rounded-t-2xl shrink-0 ${isDarkMode ? 'bg-[#0F172A] border-white/10' : 'bg-gray-50'}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-[#1E9BD9] to-[#0D7AB5] rounded-xl">
                  <Sparkles className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {language === 'ar' ? 'محرر الصور بالذكاء الاصطناعي' : 'AI Image Editor'}
                  </h2>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {language === 'ar' ? 'تحليل وتحسين الصور تلقائياً' : 'Automatic image analysis & enhancement'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => {
                  if (isNewImage && pendingImage) {
                    URL.revokeObjectURL(pendingImage.preview);
                    setPendingImage(null);
                  }
                  setShowImageEditor(false);
                  setEditingImageIndex(null);
                  setIsNewImage(false);
                  setImageEnhancements({ brightness: 100, contrast: 100, saturation: 100 });
                  setAiProductInfo(null);
                  setAiSuggestions([]);
                }}
                className={`p-2 rounded-full transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
              >
                <X className={`h-5 w-5 ${isDarkMode ? 'text-white' : ''}`} />
              </button>
            </div>
            
            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Image Preview */}
                <div className="space-y-4">
                  {/* Image Preview */}
                  <div className={`rounded-xl p-4 flex items-center justify-center ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-900'}`} style={{ minHeight: '280px', maxHeight: '320px' }}>
                    <img 
                      src={isNewImage && pendingImage ? pendingImage.preview : (productImages[editingImageIndex]?.preview || (productImages[editingImageIndex]?.file ? URL.createObjectURL(productImages[editingImageIndex].file) : ''))} 
                      alt="Preview" 
                      className="max-w-full max-h-[300px] object-contain rounded-lg"
                    />
                  </div>
                  
                  {/* Set as Main - Only show for existing images */}
                  {!isNewImage && (
                    <button
                      onClick={() => setMainImageIndex(editingImageIndex)}
                      className={`w-full p-3 rounded-xl border-2 flex items-center justify-center gap-2 transition-all ${
                        editingImageIndex === mainImageIndex 
                          ? 'border-[#1E9BD9] bg-[#1E9BD9]/10 text-[#1E9BD9]' 
                          : isDarkMode ? 'border-gray-600 hover:border-[#1E9BD9] text-gray-300' : 'border-gray-200 hover:border-[#1E9BD9]'
                      }`}
                    >
                      <CheckCircle className={`h-5 w-5 ${editingImageIndex === mainImageIndex ? 'text-[#1E9BD9]' : isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                      <span className="font-medium text-sm">
                        {editingImageIndex === mainImageIndex 
                          ? (language === 'ar' ? 'الصورة الرئيسية ✓' : 'Main Image ✓')
                          : (language === 'ar' ? 'تعيين كصورة رئيسية' : 'Set as Main Image')
                        }
                      </span>
                    </button>
                  )}
                  
                </div>
                
                {/* Right Column - AI Features */}
                <div className="space-y-4">
                  {/* AI Analysis Button - Analysis Only */}
                  <button
                    onClick={async () => {
                      setAiAnalyzing(true);
                      setAiProductInfo(null);
                      setAiSuggestions([]);
                      try {
                        const imageFile = isNewImage && pendingImage ? pendingImage.file : productImages[editingImageIndex]?.file;
                        
                        if (!imageFile) {
                          toast.error(language === 'ar' ? 'لا يوجد ملف صورة - حاول رفع الصورة مرة أخرى' : 'No image file - try uploading again');
                          setAiAnalyzing(false);
                          return;
                        }
                        
                        toast.info(language === 'ar' ? '🔍 جاري تحليل الصورة...' : '🔍 Analyzing image...');
                        const analyzeFormData = new FormData();
                        analyzeFormData.append('image', imageFile);
                        
                        const analyzeResponse = await fetch(`${BACKEND_URL}/api/ai/analyze-product-image`, {
                          method: 'POST',
                          headers: {
                            'Authorization': `Bearer ${localStorage.getItem('business_token')}`
                          },
                          body: analyzeFormData
                        });
                        
                        if (!analyzeResponse.ok) {
                          throw new Error('Analysis failed');
                        }
                        
                        const analyzeResult = await analyzeResponse.json();
                        
                        if (analyzeResult.success) {
                          setAiProductInfo({
                            name: analyzeResult.product_name,
                            nameEn: analyzeResult.product_name_en,
                            description: analyzeResult.product_description,
                            descriptionEn: analyzeResult.product_description_en,
                            category: analyzeResult.suggested_category,
                            type: analyzeResult.product_type
                          });
                          setAiSuggestions(analyzeResult.suggestions || []);
                          toast.success(language === 'ar' ? '✨ تم تحليل الصورة بنجاح!' : '✨ Image analyzed successfully!');
                        } else {
                          toast.error(language === 'ar' ? 'فشل في تحليل الصورة' : 'Failed to analyze image');
                        }
                      } catch (error) {
                        console.error('AI Analysis error:', error);
                        toast.error(language === 'ar' ? 'فشل في تحليل الصورة' : 'Failed to analyze image');
                      } finally {
                        setAiAnalyzing(false);
                      }
                    }}
                    disabled={aiAnalyzing}
                    className={`w-full p-4 rounded-xl flex items-center justify-center gap-3 transition-all text-white ${
                      aiAnalyzing
                        ? 'bg-gradient-to-r from-[#1E9BD9]/70 via-[#0D7AB5]/70 to-[#065A82]/70 cursor-wait'
                        : 'bg-gradient-to-r from-[#1E9BD9] via-[#0D7AB5] to-[#065A82] hover:opacity-90 shadow-lg hover:shadow-[#1E9BD9]/25'
                    }`}
                  >
                    {aiAnalyzing ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span className="font-medium">{language === 'ar' ? 'جاري التحليل...' : 'Analyzing...'}</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-5 w-5" />
                        <span className="font-medium">{language === 'ar' ? 'تحليل الصورة بالذكاء الاصطناعي' : 'AI Image Analysis'}</span>
                      </>
                    )}
                  </button>
                  
                  {/* AI Product Info Results */}
                  {aiProductInfo && (
                    <div className={`rounded-xl p-4 border ${isDarkMode ? 'bg-[#0F172A] border-[#1E9BD9]/30' : 'bg-[#1E9BD9]/10 border-[#1E9BD9]'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="h-4 w-4 text-[#1E9BD9]" />
                        <h4 className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-[#1E9BD9]'}`}>
                          {language === 'ar' ? 'نتائج التحليل' : 'Analysis Results'}
                        </h4>
                      </div>
                      
                      <div className="space-y-3">
                        {/* Product Name */}
                        <div>
                          <Label className={`text-xs mb-1 block ${isDarkMode ? 'text-gray-400' : 'text-[#1E9BD9]'}`}>
                            {language === 'ar' ? 'اسم المنتج المقترح' : 'Suggested Product Name'}
                          </Label>
                          <div className="flex items-center gap-2">
                            <p className={`flex-1 text-sm font-medium ${isDarkMode ? 'text-white' : 'text-[#1E9BD9]'}`}>
                              {language === 'ar' ? aiProductInfo.name : aiProductInfo.nameEn}
                            </p>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs px-2 py-1 h-7 border-[#1E9BD9] text-[#1E9BD9] hover:bg-[#1E9BD9]/20"
                              onClick={() => {
                                setItemName(language === 'ar' ? aiProductInfo.name : aiProductInfo.nameEn);
                                toast.success(language === 'ar' ? 'تم نسخ الاسم' : 'Name applied');
                              }}
                            >
                              {language === 'ar' ? 'استخدام' : 'Use'}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Product Description */}
                        <div>
                          <Label className={`text-xs mb-1 block ${isDarkMode ? 'text-gray-400' : 'text-[#1E9BD9]'}`}>
                            {language === 'ar' ? 'الوصف المقترح' : 'Suggested Description'}
                          </Label>
                          <div className="flex items-start gap-2">
                            <p className={`flex-1 text-xs ${isDarkMode ? 'text-gray-300' : 'text-[#1E9BD9]'}`}>
                              {language === 'ar' ? aiProductInfo.description : aiProductInfo.descriptionEn}
                            </p>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-xs px-2 py-1 h-7 border-[#1E9BD9] text-[#1E9BD9] hover:bg-[#1E9BD9]/20"
                              onClick={() => {
                                setItemDescription(language === 'ar' ? aiProductInfo.description : aiProductInfo.descriptionEn);
                                toast.success(language === 'ar' ? 'تم نسخ الوصف' : 'Description applied');
                              }}
                            >
                              {language === 'ar' ? 'استخدام' : 'Use'}
                            </Button>
                          </div>
                        </div>
                        
                        {/* Category */}
                        {aiProductInfo.category && (
                          <div className={`text-xs px-2 py-1 rounded-full inline-block ${isDarkMode ? 'bg-[#1E9BD9]/100/20 text-[#1E9BD9]' : 'bg-[#1E9BD9]/20 text-[#1E9BD9]'}`}>
                            {language === 'ar' ? 'التصنيف: ' : 'Category: '}{aiProductInfo.category}
                          </div>
                        )}
                        
                        {/* Apply All Button */}
                        <Button
                          size="sm"
                          className="w-full bg-gradient-to-r from-[#1E9BD9] to-[#0D7AB5] text-white hover:opacity-90"
                          onClick={() => {
                            setItemName(language === 'ar' ? aiProductInfo.name : aiProductInfo.nameEn);
                            setItemDescription(language === 'ar' ? aiProductInfo.description : aiProductInfo.descriptionEn);
                            toast.success(language === 'ar' ? 'تم تطبيق جميع البيانات' : 'All data applied');
                          }}
                        >
                          <CheckCircle className="h-4 w-4 me-2" />
                          {language === 'ar' ? 'تطبيق جميع البيانات' : 'Apply All Data'}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {/* AI Suggestions - Click to Apply Enhancement */}
                  {aiSuggestions.length > 0 && (
                    <div className={`rounded-xl p-4 border ${isDarkMode ? 'bg-[#0F172A] border-blue-500/30' : 'bg-blue-50 border-blue-200'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Wand2 className="h-4 w-4 text-blue-500" />
                        <h4 className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-blue-800'}`}>
                          {language === 'ar' ? 'اقتراحات تحسين الصورة - اضغط للتطبيق' : 'Image Enhancement - Click to Apply'}
                        </h4>
                      </div>
                      
                      <div className="space-y-2">
                        {aiSuggestions.map((suggestion) => (
                          <button
                            key={suggestion.id}
                            onClick={async () => {
                              setApplyingSuggestion(suggestion.id);
                              try {
                                const imageFile = isNewImage && pendingImage ? pendingImage.file : productImages[editingImageIndex]?.file;
                                
                                if (!imageFile) {
                                  toast.error(language === 'ar' ? 'لا يوجد ملف صورة' : 'No image file');
                                  setApplyingSuggestion(null);
                                  return;
                                }
                                
                                toast.info(language === 'ar' ? '🎨 جاري تطبيق التحسين...' : '🎨 Applying enhancement...');
                                
                                const formData = new FormData();
                                formData.append('image', imageFile);
                                formData.append('suggestion_type', suggestion.action_type);
                                formData.append('product_type', aiProductInfo?.type || 'general');
                                
                                const response = await fetch(`${BACKEND_URL}/api/ai/apply-image-suggestion`, {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('business_token')}`
                                  },
                                  body: formData
                                });
                                
                                const result = await response.json();
                                
                                if (response.ok && result.success && result.enhanced_image) {
                                  // Convert base64 to blob
                                  const base64Data = result.enhanced_image.data;
                                  const mimeType = result.enhanced_image.mime_type || 'image/png';
                                  const byteCharacters = atob(base64Data);
                                  const byteNumbers = new Array(byteCharacters.length);
                                  for (let i = 0; i < byteCharacters.length; i++) {
                                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                                  }
                                  const byteArray = new Uint8Array(byteNumbers);
                                  const blob = new Blob([byteArray], { type: mimeType });
                                  
                                  const enhancedFile = new File([blob], `enhanced_${Date.now()}.png`, { type: mimeType });
                                  const enhancedPreview = URL.createObjectURL(blob);
                                  
                                  // Update the image
                                  if (isNewImage && pendingImage) {
                                    setPendingImage({
                                      file: enhancedFile,
                                      preview: enhancedPreview,
                                      enhanced: true
                                    });
                                  } else if (editingImageIndex !== null) {
                                    const updated = [...productImages];
                                    updated[editingImageIndex] = {
                                      ...updated[editingImageIndex],
                                      file: enhancedFile,
                                      preview: enhancedPreview,
                                      enhanced: true
                                    };
                                    setProductImages(updated);
                                  }
                                  
                                  toast.success(language === 'ar' ? '✨ تم تحسين الصورة بنجاح!' : '✨ Image enhanced successfully!');
                                } else {
                                  toast.error(language === 'ar' ? 'فشل في تحسين الصورة' : 'Failed to enhance image');
                                }
                              } catch (error) {
                                console.error('Enhancement error:', error);
                                toast.error(language === 'ar' ? 'فشل في تحسين الصورة' : 'Failed to enhance image');
                              } finally {
                                setApplyingSuggestion(null);
                              }
                            }}
                            disabled={applyingSuggestion !== null}
                            className={`w-full p-3 rounded-lg border text-start flex items-center gap-3 transition-all ${
                              applyingSuggestion === suggestion.id
                                ? 'bg-blue-500/20 border-blue-500'
                                : isDarkMode 
                                  ? 'bg-[#1E293B] border-white/10 hover:border-blue-500/50 hover:bg-blue-500/10' 
                                  : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                            }`}
                          >
                            <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                              {suggestion.action_type === 'white_background' && <Image className="h-4 w-4 text-blue-500" />}
                              {suggestion.action_type === 'on_model' && <UserCircle className="h-4 w-4 text-blue-500" />}
                              {suggestion.action_type === 'remove_background' && <Eraser className="h-4 w-4 text-blue-500" />}
                              {suggestion.action_type === 'enhance_lighting' && <Sun className="h-4 w-4 text-blue-500" />}
                              {suggestion.action_type === 'crop_center' && <Crop className="h-4 w-4 text-blue-500" />}
                              {!['white_background', 'on_model', 'remove_background', 'enhance_lighting', 'crop_center'].includes(suggestion.action_type) && <Sparkles className="h-4 w-4 text-blue-500" />}
                            </div>
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-blue-900'}`}>
                                {language === 'ar' ? suggestion.text_ar : suggestion.text_en}
                              </p>
                            </div>
                            {applyingSuggestion === suggestion.id ? (
                              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                            ) : (
                              <Wand2 className={`h-4 w-4 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                </div>
              </div>
            </div>
            
            {/* Footer - Fixed at bottom */}
            <div className={`p-4 border-t flex justify-end gap-3 rounded-b-2xl shrink-0 ${isDarkMode ? 'bg-[#0F172A] border-white/10' : 'bg-gray-50'}`}>
              <Button 
                variant="outline" 
                onClick={() => {
                  if (isNewImage && pendingImage) {
                    URL.revokeObjectURL(pendingImage.preview);
                    setPendingImage(null);
                  }
                  setShowImageEditor(false);
                  setEditingImageIndex(null);
                  setIsNewImage(false);
                  setImageEnhancements({ brightness: 100, contrast: 100, saturation: 100 });
                  setAiProductInfo(null);
                  setAiSuggestions([]);
                }}
                className="px-6"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button 
                onClick={() => {
                  if (isNewImage && pendingImage) {
                    const newImage = { 
                      ...pendingImage, 
                      brightness: imageEnhancements.brightness,
                      contrast: imageEnhancements.contrast,
                      saturation: imageEnhancements.saturation,
                      enhanced: imageEnhancements.brightness !== 100 || imageEnhancements.contrast !== 100 || imageEnhancements.saturation !== 100
                    };
                    setProductImages([...productImages, newImage]);
                    setPendingImage(null);
                    toast.success(language === 'ar' ? 'تم إضافة الصورة' : 'Image added');
                  } else {
                    const updated = [...productImages];
                    updated[editingImageIndex] = { 
                      ...updated[editingImageIndex], 
                      brightness: imageEnhancements.brightness,
                      contrast: imageEnhancements.contrast,
                      saturation: imageEnhancements.saturation
                    };
                    setProductImages(updated);
                    toast.success(language === 'ar' ? 'تم حفظ التعديلات' : 'Changes saved');
                  }
                  setShowImageEditor(false);
                  setEditingImageIndex(null);
                  setIsNewImage(false);
                  setImageEnhancements({ brightness: 100, contrast: 100, saturation: 100 });
                  setAiProductInfo(null);
                  setAiSuggestions([]);
                }}
                className="bg-[#1E9BD9] hover:bg-[#1E9BD9]/90 px-6"
              >
                {language === 'ar' ? 'موافق' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Product Options Modal */}
      {showAddOptionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`rounded-2xl w-full max-w-lg shadow-2xl my-4 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'}`}>
            {/* Header */}
            <div className={`p-4 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : ''}`}>
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {language === 'ar' ? 'إضافة خيارات المنتج' : 'Add Product Options'}
              </h2>
              <button 
                onClick={() => {
                  setShowAddOptionModal(false);
                  setSelectedOptionType(null);
                  setCustomOptionValues('');
                }}
                className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              >
                <X className={`h-5 w-5 ${isDarkMode ? 'text-white' : ''}`} />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4">
              {!selectedOptionType ? (
                // Option Type Selection
                <div className="space-y-3">
                  <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {language === 'ar' ? 'اختر نوع الخيار الذي تريد إضافته:' : 'Select the type of option you want to add:'}
                  </p>
                  
                  {/* Colors */}
                  <button
                    onClick={() => setSelectedOptionType('color')}
                    className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${isDarkMode ? 'border-white/10 hover:border-cyan-500/50 bg-[#0F172A]' : 'border-gray-200 hover:border-cyan-300 hover:bg-cyan-50'}`}
                  >
                    <div className="flex -space-x-1 rtl:space-x-reverse">
                      <div className="w-6 h-6 rounded-full bg-red-500 border-2 border-white"></div>
                      <div className="w-6 h-6 rounded-full bg-blue-500 border-2 border-white"></div>
                      <div className="w-6 h-6 rounded-full bg-green-500 border-2 border-white"></div>
                    </div>
                    <div className="flex-1 text-start">
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {language === 'ar' ? 'الألوان' : 'Colors'}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {language === 'ar' ? 'أحمر، أزرق، أخضر...' : 'Red, Blue, Green...'}
                      </p>
                    </div>
                    <ChevronLeft className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} ${language === 'ar' ? '' : 'rotate-180'}`} />
                  </button>
                  
                  {/* Sizes */}
                  <button
                    onClick={() => setSelectedOptionType('size')}
                    className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${isDarkMode ? 'border-white/10 hover:border-blue-500/50 bg-[#0F172A]' : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'}`}
                  >
                    <div className="flex items-center gap-1">
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200'}`}>S</span>
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200'}`}>M</span>
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200'}`}>L</span>
                    </div>
                    <div className="flex-1 text-start">
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {language === 'ar' ? 'المقاسات' : 'Sizes'}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {language === 'ar' ? 'S, M, L, XL, XXL...' : 'S, M, L, XL, XXL...'}
                      </p>
                    </div>
                    <ChevronLeft className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} ${language === 'ar' ? '' : 'rotate-180'}`} />
                  </button>
                  
                  {/* Weight */}
                  <button
                    onClick={() => setSelectedOptionType('weight')}
                    className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${isDarkMode ? 'border-white/10 hover:border-orange-500/50 bg-[#0F172A]' : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'}`}
                  >
                    <Weight className={`h-6 w-6 ${isDarkMode ? 'text-orange-400' : 'text-orange-500'}`} />
                    <div className="flex-1 text-start">
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {language === 'ar' ? 'الوزن' : 'Weight'}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {language === 'ar' ? '250 جرام، 500 جرام، 1 كيلو...' : '250g, 500g, 1kg...'}
                      </p>
                    </div>
                    <ChevronLeft className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} ${language === 'ar' ? '' : 'rotate-180'}`} />
                  </button>
                  
                  {/* Length/Dimensions */}
                  <button
                    onClick={() => setSelectedOptionType('length')}
                    className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${isDarkMode ? 'border-white/10 hover:border-green-500/50 bg-[#0F172A]' : 'border-gray-200 hover:border-green-300 hover:bg-green-50'}`}
                  >
                    <svg className={`h-6 w-6 ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                    <div className="flex-1 text-start">
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {language === 'ar' ? 'الطول / الأبعاد' : 'Length / Dimensions'}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {language === 'ar' ? '10 سم، 20 سم، متر...' : '10cm, 20cm, 1m...'}
                      </p>
                    </div>
                    <ChevronLeft className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} ${language === 'ar' ? '' : 'rotate-180'}`} />
                  </button>
                  
                  {/* Material */}
                  <button
                    onClick={() => setSelectedOptionType('material')}
                    className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${isDarkMode ? 'border-white/10 hover:border-[#1E9BD9]/50 bg-[#0F172A]' : 'border-gray-200 hover:border-[#1E9BD9] hover:bg-[#1E9BD9]/10'}`}
                  >
                    <svg className={`h-6 w-6 ${isDarkMode ? 'text-[#1E9BD9]' : 'text-[#1E9BD9]'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                    </svg>
                    <div className="flex-1 text-start">
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {language === 'ar' ? 'الخامة / المادة' : 'Material'}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {language === 'ar' ? 'قطن، بوليستر، جلد...' : 'Cotton, Polyester, Leather...'}
                      </p>
                    </div>
                    <ChevronLeft className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} ${language === 'ar' ? '' : 'rotate-180'}`} />
                  </button>
                  
                  {/* Custom */}
                  <button
                    onClick={() => setSelectedOptionType('custom')}
                    className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${isDarkMode ? 'border-white/10 hover:border-gray-500/50 bg-[#0F172A]' : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'}`}
                  >
                    <Plus className={`h-6 w-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                    <div className="flex-1 text-start">
                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {language === 'ar' ? 'خيار مخصص' : 'Custom Option'}
                      </p>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {language === 'ar' ? 'أضف أي خيار آخر تريده' : 'Add any other option you need'}
                      </p>
                    </div>
                    <ChevronLeft className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} ${language === 'ar' ? '' : 'rotate-180'}`} />
                  </button>
                </div>
              ) : (
                // Option Values Selection
                <div className="space-y-4">
                  {/* Back Button */}
                  <button
                    onClick={() => {
                      setSelectedOptionType(null);
                      setCustomOptionValues('');
                    }}
                    className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <ArrowRight className={`h-4 w-4 ${language === 'ar' ? '' : 'rotate-180'}`} />
                    {language === 'ar' ? 'رجوع' : 'Back'}
                  </button>
                  
                  {/* Option Title */}
                  <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {selectedOptionType === 'color' && (language === 'ar' ? 'اختر الألوان' : 'Select Colors')}
                    {selectedOptionType === 'size' && (language === 'ar' ? 'اختر المقاسات' : 'Select Sizes')}
                    {selectedOptionType === 'weight' && (language === 'ar' ? 'اختر الأوزان' : 'Select Weights')}
                    {selectedOptionType === 'length' && (language === 'ar' ? 'اختر الأطوال' : 'Select Lengths')}
                    {selectedOptionType === 'material' && (language === 'ar' ? 'اختر الخامات' : 'Select Materials')}
                    {selectedOptionType === 'custom' && (language === 'ar' ? 'خيار مخصص' : 'Custom Option')}
                  </h3>
                  
                  {/* Color Options */}
                  {selectedOptionType === 'color' && (
                    <div className="space-y-3">
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {language === 'ar' ? 'اضغط على الألوان المتوفرة:' : 'Click on available colors:'}
                      </p>
                      <div className="grid grid-cols-5 gap-3">
                        {[
                          { name: language === 'ar' ? 'أحمر' : 'Red', color: '#EF4444' },
                          { name: language === 'ar' ? 'أزرق' : 'Blue', color: '#3B82F6' },
                          { name: language === 'ar' ? 'أخضر' : 'Green', color: '#22C55E' },
                          { name: language === 'ar' ? 'أصفر' : 'Yellow', color: '#EAB308' },
                          { name: language === 'ar' ? 'برتقالي' : 'Orange', color: '#F97316' },
                          { name: language === 'ar' ? 'بنفسجي' : 'Purple', color: '#A855F7' },
                          { name: language === 'ar' ? 'وردي' : 'Pink', color: '#EC4899' },
                          { name: language === 'ar' ? 'أسود' : 'Black', color: '#000000' },
                          { name: language === 'ar' ? 'أبيض' : 'White', color: '#FFFFFF' },
                          { name: language === 'ar' ? 'رمادي' : 'Gray', color: '#6B7280' },
                          { name: language === 'ar' ? 'بني' : 'Brown', color: '#92400E' },
                          { name: language === 'ar' ? 'بيج' : 'Beige', color: '#D4B896' },
                          { name: language === 'ar' ? 'كحلي' : 'Navy', color: '#1E3A5F' },
                          { name: language === 'ar' ? 'سماوي' : 'Cyan', color: '#06B6D4' },
                          { name: language === 'ar' ? 'ذهبي' : 'Gold', color: '#D4AF37' },
                        ].map((colorObj) => {
                          const isSelected = customOptionValues.split(',').map(v => v.trim()).includes(colorObj.name);
                          return (
                            <button
                              key={colorObj.name}
                              onClick={() => {
                                const values = customOptionValues ? customOptionValues.split(',').map(v => v.trim()).filter(v => v) : [];
                                if (isSelected) {
                                  setCustomOptionValues(values.filter(v => v !== colorObj.name).join(', '));
                                } else {
                                  setCustomOptionValues([...values, colorObj.name].join(', '));
                                }
                              }}
                              className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all ${isSelected ? 'ring-2 ring-[#1E9BD9] ring-offset-2' : ''} ${isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}
                            >
                              <div 
                                className={`w-8 h-8 rounded-full border-2 ${colorObj.color === '#FFFFFF' ? 'border-gray-300' : 'border-transparent'}`}
                                style={{ backgroundColor: colorObj.color }}
                              ></div>
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{colorObj.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Size Options - Separated by Category */}
                  {selectedOptionType === 'size' && (
                    <div className="space-y-4">
                      {/* Clothing Sizes */}
                      <div>
                        <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {language === 'ar' ? 'مقاسات الملابس:' : 'Clothing Sizes:'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'].map((size) => {
                            const isSelected = customOptionValues.split(',').map(v => v.trim()).includes(size);
                            return (
                              <button
                                key={size}
                                onClick={() => {
                                  const values = customOptionValues ? customOptionValues.split(',').map(v => v.trim()).filter(v => v) : [];
                                  if (isSelected) {
                                    setCustomOptionValues(values.filter(v => v !== size).join(', '));
                                  } else {
                                    setCustomOptionValues([...values, size].join(', '));
                                  }
                                }}
                                className={`px-4 py-2 rounded-xl font-medium transition-all ${
                                  isSelected 
                                    ? 'bg-[#1E9BD9] text-white' 
                                    : isDarkMode ? 'bg-[#0F172A] text-gray-300 hover:bg-[#1E293B]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {size}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Shoe Sizes - EU */}
                      <div>
                        <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {language === 'ar' ? 'مقاسات الأحذية (EU):' : 'Shoe Sizes (EU):'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {['36', '37', '38', '39', '40', '41', '42', '43', '44', '45', '46', '47', '48'].map((size) => {
                            const euSize = `EU ${size}`;
                            const isSelected = customOptionValues.split(',').map(v => v.trim()).includes(euSize);
                            return (
                              <button
                                key={size}
                                onClick={() => {
                                  const values = customOptionValues ? customOptionValues.split(',').map(v => v.trim()).filter(v => v) : [];
                                  if (isSelected) {
                                    setCustomOptionValues(values.filter(v => v !== euSize).join(', '));
                                  } else {
                                    setCustomOptionValues([...values, euSize].join(', '));
                                  }
                                }}
                                className={`px-3 py-2 rounded-xl font-medium transition-all text-sm ${
                                  isSelected 
                                    ? 'bg-orange-500 text-white' 
                                    : isDarkMode ? 'bg-[#0F172A] text-gray-300 hover:bg-[#1E293B]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                EU {size}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Shoe Sizes - US */}
                      <div>
                        <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {language === 'ar' ? 'مقاسات الأحذية (US):' : 'Shoe Sizes (US):'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {['5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '13'].map((size) => {
                            const usSize = `US ${size}`;
                            const isSelected = customOptionValues.split(',').map(v => v.trim()).includes(usSize);
                            return (
                              <button
                                key={size}
                                onClick={() => {
                                  const values = customOptionValues ? customOptionValues.split(',').map(v => v.trim()).filter(v => v) : [];
                                  if (isSelected) {
                                    setCustomOptionValues(values.filter(v => v !== usSize).join(', '));
                                  } else {
                                    setCustomOptionValues([...values, usSize].join(', '));
                                  }
                                }}
                                className={`px-3 py-2 rounded-xl font-medium transition-all text-sm ${
                                  isSelected 
                                    ? 'bg-green-500 text-white' 
                                    : isDarkMode ? 'bg-[#0F172A] text-gray-300 hover:bg-[#1E293B]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                US {size}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Shoe Sizes - UK */}
                      <div>
                        <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                          {language === 'ar' ? 'مقاسات الأحذية (UK):' : 'Shoe Sizes (UK):'}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {['3', '3.5', '4', '4.5', '5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '11', '12'].map((size) => {
                            const ukSize = `UK ${size}`;
                            const isSelected = customOptionValues.split(',').map(v => v.trim()).includes(ukSize);
                            return (
                              <button
                                key={size}
                                onClick={() => {
                                  const values = customOptionValues ? customOptionValues.split(',').map(v => v.trim()).filter(v => v) : [];
                                  if (isSelected) {
                                    setCustomOptionValues(values.filter(v => v !== ukSize).join(', '));
                                  } else {
                                    setCustomOptionValues([...values, ukSize].join(', '));
                                  }
                                }}
                                className={`px-3 py-2 rounded-xl font-medium transition-all text-sm ${
                                  isSelected 
                                    ? 'bg-red-500 text-white' 
                                    : isDarkMode ? 'bg-[#0F172A] text-gray-300 hover:bg-[#1E293B]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                UK {size}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Weight Options */}
                  {selectedOptionType === 'weight' && (
                    <div className="space-y-3">
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {language === 'ar' ? 'اضغط على الأوزان المتوفرة أو أضف وزن مخصص:' : 'Click on available weights or add custom:'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {['100g', '250g', '500g', '750g', '1kg', '1.5kg', '2kg', '5kg', '10kg'].map((weight) => {
                          const isSelected = customOptionValues.split(',').map(v => v.trim()).includes(weight);
                          return (
                            <button
                              key={weight}
                              onClick={() => {
                                const values = customOptionValues ? customOptionValues.split(',').map(v => v.trim()).filter(v => v) : [];
                                if (isSelected) {
                                  setCustomOptionValues(values.filter(v => v !== weight).join(', '));
                                } else {
                                  setCustomOptionValues([...values, weight].join(', '));
                                }
                              }}
                              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                                isSelected 
                                  ? 'bg-orange-500 text-white' 
                                  : isDarkMode ? 'bg-[#0F172A] text-gray-300 hover:bg-[#1E293B]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {weight}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Length Options */}
                  {selectedOptionType === 'length' && (
                    <div className="space-y-3">
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {language === 'ar' ? 'اضغط على الأطوال المتوفرة أو أضف طول مخصص:' : 'Click on available lengths or add custom:'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {['10cm', '20cm', '30cm', '50cm', '75cm', '1m', '1.5m', '2m', '3m', '5m'].map((length) => {
                          const isSelected = customOptionValues.split(',').map(v => v.trim()).includes(length);
                          return (
                            <button
                              key={length}
                              onClick={() => {
                                const values = customOptionValues ? customOptionValues.split(',').map(v => v.trim()).filter(v => v) : [];
                                if (isSelected) {
                                  setCustomOptionValues(values.filter(v => v !== length).join(', '));
                                } else {
                                  setCustomOptionValues([...values, length].join(', '));
                                }
                              }}
                              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                                isSelected 
                                  ? 'bg-green-500 text-white' 
                                  : isDarkMode ? 'bg-[#0F172A] text-gray-300 hover:bg-[#1E293B]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {length}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Material Options */}
                  {selectedOptionType === 'material' && (
                    <div className="space-y-3">
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {language === 'ar' ? 'اضغط على الخامات المتوفرة:' : 'Click on available materials:'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {[
                          language === 'ar' ? 'قطن' : 'Cotton',
                          language === 'ar' ? 'بوليستر' : 'Polyester',
                          language === 'ar' ? 'جلد طبيعي' : 'Genuine Leather',
                          language === 'ar' ? 'جلد صناعي' : 'Faux Leather',
                          language === 'ar' ? 'حرير' : 'Silk',
                          language === 'ar' ? 'صوف' : 'Wool',
                          language === 'ar' ? 'كتان' : 'Linen',
                          language === 'ar' ? 'نايلون' : 'Nylon',
                          language === 'ar' ? 'ستانلس ستيل' : 'Stainless Steel',
                          language === 'ar' ? 'خشب' : 'Wood',
                          language === 'ar' ? 'بلاستيك' : 'Plastic',
                          language === 'ar' ? 'زجاج' : 'Glass',
                        ].map((material) => {
                          const isSelected = customOptionValues.split(',').map(v => v.trim()).includes(material);
                          return (
                            <button
                              key={material}
                              onClick={() => {
                                const values = customOptionValues ? customOptionValues.split(',').map(v => v.trim()).filter(v => v) : [];
                                if (isSelected) {
                                  setCustomOptionValues(values.filter(v => v !== material).join(', '));
                                } else {
                                  setCustomOptionValues([...values, material].join(', '));
                                }
                              }}
                              className={`px-4 py-2 rounded-xl font-medium transition-all ${
                                isSelected 
                                  ? 'bg-[#1E9BD9]/100 text-white' 
                                  : isDarkMode ? 'bg-[#0F172A] text-gray-300 hover:bg-[#1E293B]' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {material}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Custom Input */}
                  <div className="mt-4">
                    <Label className={`text-sm mb-2 block ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {selectedOptionType === 'custom' 
                        ? (language === 'ar' ? 'أدخل اسم الخيار والقيم (مفصولة بفواصل):' : 'Enter option name and values (comma separated):')
                        : (language === 'ar' ? 'أو أضف قيم مخصصة (مفصولة بفواصل):' : 'Or add custom values (comma separated):')}
                    </Label>
                    {selectedOptionType === 'custom' && (
                      <Input
                        placeholder={language === 'ar' ? 'اسم الخيار (مثال: النكهة)' : 'Option name (e.g., Flavor)'}
                        className={`mb-2 ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white placeholder:text-gray-500' : ''}`}
                        id="customOptionName"
                      />
                    )}
                    <Input
                      value={customOptionValues}
                      onChange={(e) => setCustomOptionValues(e.target.value)}
                      placeholder={language === 'ar' ? 'القيم (مثال: فانيلا، شوكولاتة، فراولة)' : 'Values (e.g., Vanilla, Chocolate, Strawberry)'}
                      className={`${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white placeholder:text-gray-500' : ''}`}
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className={`p-4 border-t flex justify-end gap-3 ${isDarkMode ? 'border-white/10' : ''}`}>
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAddOptionModal(false);
                  setSelectedOptionType(null);
                  setCustomOptionValues('');
                }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              {selectedOptionType && (
                <Button 
                  onClick={() => {
                    if (!customOptionValues.trim()) {
                      toast.error(language === 'ar' ? 'الرجاء اختيار قيمة واحدة على الأقل' : 'Please select at least one value');
                      return;
                    }
                    
                    const optionNames = {
                      color: language === 'ar' ? 'اللون' : 'Color',
                      size: language === 'ar' ? 'المقاس' : 'Size',
                      weight: language === 'ar' ? 'الوزن' : 'Weight',
                      length: language === 'ar' ? 'الطول' : 'Length',
                      material: language === 'ar' ? 'الخامة' : 'Material',
                      custom: document.getElementById('customOptionName')?.value || (language === 'ar' ? 'خيار مخصص' : 'Custom'),
                    };
                    
                    const newOption = {
                      type: selectedOptionType,
                      name: optionNames[selectedOptionType],
                      choices: customOptionValues.split(',').map(v => v.trim()).filter(v => v)
                    };
                    
                    setProductOptions([...productOptions, newOption]);
                    setShowAddOptionModal(false);
                    setSelectedOptionType(null);
                    setCustomOptionValues('');
                    toast.success(language === 'ar' ? 'تم إضافة الخيار بنجاح' : 'Option added successfully');
                  }}
                  className="bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
                >
                  {language === 'ar' ? 'إضافة' : 'Add'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Phone Details Modal */}
      {showPhoneDetailsModal && selectedPhoneItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-2xl shadow-2xl ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'}`}>
            {/* Header */}
            <div className={`p-4 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : ''}`}>
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {language === 'ar' ? 'تفاصيل الرقم' : 'Number Details'}
              </h3>
              <button 
                onClick={() => {
                  setShowPhoneDetailsModal(false);
                  setSelectedPhoneItem(null);
                }}
                className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              >
                <X className={`h-5 w-5 ${isDarkMode ? 'text-white' : ''}`} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Number Display */}
              <div className="flex items-center p-4 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 shadow-lg">
                <div className="flex-shrink-0 order-first">
                  <img 
                    src={telecomLogos[selectedPhoneItem.operator]} 
                    alt={selectedPhoneItem.operator}
                    className="h-14 w-14 object-contain rounded-lg bg-white/10 p-1"
                  />
                </div>
                <div className="flex-1 text-center">
                  <span className="font-bold text-2xl md:text-3xl tracking-widest text-white" dir="ltr">
                    {selectedPhoneItem.number}
                  </span>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-3">
                {/* Price */}
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {language === 'ar' ? 'السعر' : 'Price'}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <span className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {selectedPhoneItem.price?.toLocaleString('en-US')}
                    </span>
                    <img 
                      src={isDarkMode ? '/currency-aed-white.png' : '/currency-aed-black.png'} 
                      alt="AED" 
                      className="h-4 w-auto"
                    />
                  </div>
                </div>

                {/* Category */}
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {language === 'ar' ? 'الفئة' : 'Category'}
                  </p>
                  <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {selectedPhoneItem.category === 'vip' ? 'VIP' : 
                     selectedPhoneItem.category === 'golden' ? (language === 'ar' ? 'ذهبي' : 'Golden') :
                     selectedPhoneItem.category === 'silver' ? (language === 'ar' ? 'فضي' : 'Silver') :
                     (language === 'ar' ? 'عادي' : 'Normal')}
                  </p>
                </div>

                {/* Operator */}
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {language === 'ar' ? 'المشغل' : 'Operator'}
                  </p>
                  <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {language === 'ar' 
                      ? (selectedPhoneItem.operator === 'etisalat' ? 'اتصالات' : 'دو')
                      : (selectedPhoneItem.operator === 'etisalat' ? 'Etisalat' : 'Du')
                    }
                  </p>
                </div>

                {/* Payment Type */}
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {language === 'ar' ? 'نوع الفاتورة' : 'Invoice Type'}
                  </p>
                  <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {(selectedPhoneItem.paymentType || selectedPhoneItem.planType) === 'prepaid' 
                      ? (language === 'ar' ? 'مسبق الدفع' : 'Prepaid')
                      : (selectedPhoneItem.paymentType || selectedPhoneItem.planType) === 'postpaid'
                        ? (language === 'ar' ? 'آجل الدفع' : 'Postpaid')
                        : '-'
                    }
                  </p>
                </div>

                {/* Contract */}
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {language === 'ar' ? 'العقد' : 'Contract'}
                  </p>
                  <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {(selectedPhoneItem.contract === true || selectedPhoneItem.hasContract === true)
                      ? (language === 'ar' ? 'نعم' : 'Yes')
                      : (selectedPhoneItem.contract === false || selectedPhoneItem.hasContract === false)
                        ? (language === 'ar' ? 'لا' : 'No')
                        : '-'
                    }
                  </p>
                </div>

                {/* Negotiable */}
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {language === 'ar' ? 'قابل للتفاوض' : 'Negotiable'}
                  </p>
                  <p className={`font-bold mt-1 ${
                    selectedPhoneItem.negotiable 
                      ? 'text-green-500' 
                      : isDarkMode ? 'text-white' : 'text-[#0F172A]'
                  }`}>
                    {selectedPhoneItem.negotiable 
                      ? (language === 'ar' ? 'نعم' : 'Yes')
                      : (language === 'ar' ? 'لا' : 'No')
                    }
                  </p>
                </div>
              </div>

              {/* Description */}
              {selectedPhoneItem.description && (
                <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {language === 'ar' ? 'ملاحظات' : 'Notes'}
                  </p>
                  <p className={`mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {selectedPhoneItem.description}
                  </p>
                </div>
              )}

              {/* Status */}
              <div className="flex items-center justify-between">
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'الحالة' : 'Status'}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  selectedPhoneItem.status === 'sold' 
                    ? 'bg-red-100 text-red-700' 
                    : 'bg-green-100 text-green-700'
                }`}>
                  {selectedPhoneItem.status === 'sold' 
                    ? (language === 'ar' ? 'مباع' : 'Sold')
                    : (language === 'ar' ? 'متاح' : 'Available')
                  }
                </span>
              </div>
            </div>

            {/* Footer */}
            <div className={`p-4 border-t ${isDarkMode ? 'border-white/10' : ''}`}>
              <Button 
                onClick={() => {
                  setShowPhoneDetailsModal(false);
                  setSelectedPhoneItem(null);
                }}
                className="w-full bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
              >
                {language === 'ar' ? 'إغلاق' : 'Close'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Car Details/Edit Modal */}
      {showCarDetailsModal && selectedCarItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`w-full max-w-2xl rounded-2xl shadow-2xl my-8 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'}`}>
            {/* Header */}
            <div className={`p-4 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : ''}`}>
              <h3 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                <Car className="h-5 w-5 text-[#1E9BD9]" />
                {isEditingCar 
                  ? (language === 'ar' ? 'تعديل السيارة' : 'Edit Car')
                  : (language === 'ar' ? 'تفاصيل السيارة' : 'Car Details')
                }
              </h3>
              <div className="flex items-center gap-2">
                {!isEditingCar && (
                  <button 
                    onClick={() => {
                      // Go to full-page edit mode
                      setEditingItem(selectedCarItem);
                      setShowEditCarPage(true);
                      setShowCarDetailsModal(false);
                    }}
                    className={`p-2 rounded-full text-[#1E9BD9] ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                    title={language === 'ar' ? 'تعديل' : 'Edit'}
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                )}
                <button 
                  onClick={() => {
                    setShowCarDetailsModal(false);
                    setSelectedCarItem(null);
                    setIsEditingCar(false);
                    setEditCarData({});
                  }}
                  className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {isEditingCar ? (
                /* Edit Mode */
                <>
                  {/* Edit Images Section */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'الصور' : 'Images'}
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {/* Existing Images */}
                      {(editCarData.images || []).map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img 
                            src={img} 
                            alt={`Image ${idx + 1}`}
                            className="w-full h-20 object-cover rounded-lg"
                          />
                          <button
                            onClick={() => {
                              const newImages = [...(editCarData.images || [])];
                              newImages.splice(idx, 1);
                              setEditCarData({...editCarData, images: newImages});
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      {/* Add New Image Button */}
                      {(editCarData.images?.length || 0) < 5 && (
                        <label className={`w-full h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
                          isDarkMode 
                            ? 'border-white/20 hover:border-white/40 text-gray-400' 
                            : 'border-gray-300 hover:border-gray-400 text-gray-500'
                        }`}>
                          <Plus className="h-5 w-5" />
                          <span className="text-xs mt-1">{language === 'ar' ? 'إضافة' : 'Add'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = () => {
                                  const newImages = [...(editCarData.images || []), reader.result];
                                  setEditCarData({...editCarData, images: newImages});
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </label>
                      )}
                    </div>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {language === 'ar' ? `${editCarData.images?.length || 0}/5 صور` : `${editCarData.images?.length || 0}/5 images`}
                    </p>
                  </div>

                  {/* Edit Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'الشركة المصنعة' : 'Make'}
                      </label>
                      <Input
                        value={editCarData.make || ''}
                        onChange={(e) => setEditCarData({...editCarData, make: e.target.value})}
                        className={isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'الموديل' : 'Model'}
                      </label>
                      <Input
                        value={editCarData.model || ''}
                        onChange={(e) => setEditCarData({...editCarData, model: e.target.value})}
                        className={isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'السعر' : 'Price'}
                      </label>
                      <Input
                        type="number" inputMode="decimal"
                        value={editCarData.price || ''}
                        onChange={(e) => setEditCarData({...editCarData, price: parseFloat(e.target.value)})}
                        className={isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'سنة الصنع' : 'Year'}
                      </label>
                      <select
                        value={editCarData.year || ''}
                        onChange={(e) => setEditCarData({...editCarData, year: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode 
                            ? 'bg-[#0F172A] border-white/10 text-white' 
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <option value="">{language === 'ar' ? 'اختر...' : 'Select...'}</option>
                        {Array.from({ length: 2027 - 1990 + 1 }, (_, i) => 2027 - i).map((year) => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'المسافة المقطوعة' : 'Mileage'}
                      </label>
                      <Input
                        type="number" inputMode="decimal"
                        value={editCarData.mileage || ''}
                        onChange={(e) => setEditCarData({...editCarData, mileage: parseInt(e.target.value)})}
                        className={isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}
                        placeholder="km"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'اللون' : 'Color'}
                      </label>
                      <Input
                        value={editCarData.color || ''}
                        onChange={(e) => setEditCarData({...editCarData, color: e.target.value})}
                        className={isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'الحالة' : 'Condition'}
                      </label>
                      <select
                        value={editCarData.condition || ''}
                        onChange={(e) => setEditCarData({...editCarData, condition: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode 
                            ? 'bg-[#0F172A] border-white/10 text-white' 
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <option value="">{language === 'ar' ? 'اختر...' : 'Select...'}</option>
                        <option value="new">{language === 'ar' ? 'جديد' : 'New'}</option>
                        <option value="used">{language === 'ar' ? 'مستعمل' : 'Used'}</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'المنشأ' : 'Origin'}
                      </label>
                      <select
                        value={editCarData.origin || ''}
                        onChange={(e) => setEditCarData({...editCarData, origin: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          isDarkMode 
                            ? 'bg-[#0F172A] border-white/10 text-white' 
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <option value="">{language === 'ar' ? 'اختر...' : 'Select...'}</option>
                        <option value="gcc">{language === 'ar' ? 'خليجي' : 'GCC'}</option>
                        <option value="imported">{language === 'ar' ? 'وارد' : 'Imported'}</option>
                      </select>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'الوصف' : 'Description'}
                    </label>
                    <textarea
                      value={editCarData.description || ''}
                      onChange={(e) => setEditCarData({...editCarData, description: e.target.value})}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDarkMode 
                          ? 'bg-[#0F172A] border-white/10 text-white' 
                          : 'bg-white border-gray-200'
                      }`}
                    />
                  </div>

                  {/* Warranty Section */}
                  <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-[#0F172A]/50 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                    <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'معلومات الضمان' : 'Warranty Information'}
                    </label>
                    
                    {/* Has Warranty Toggle */}
                    <div className="flex items-center gap-3 mb-4">
                      <label className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {language === 'ar' ? 'هل يوجد ضمان؟' : 'Has Warranty?'}
                      </label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setEditCarData({...editCarData, hasWarranty: true})}
                          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            editCarData.hasWarranty === true
                              ? 'bg-green-500 text-white'
                              : isDarkMode 
                                ? 'bg-[#0F172A] border border-white/10 text-gray-400 hover:border-green-500/50'
                                : 'bg-white border border-gray-200 text-gray-600 hover:border-green-500'
                          }`}
                        >
                          {language === 'ar' ? 'نعم' : 'Yes'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditCarData({...editCarData, hasWarranty: false, warrantyType: '', warrantyProvider: ''})}
                          className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                            editCarData.hasWarranty === false
                              ? 'bg-red-500 text-white'
                              : isDarkMode 
                                ? 'bg-[#0F172A] border border-white/10 text-gray-400 hover:border-red-500/50'
                                : 'bg-white border border-gray-200 text-gray-600 hover:border-red-500'
                          }`}
                        >
                          {language === 'ar' ? 'لا' : 'No'}
                        </button>
                      </div>
                    </div>

                    {/* Warranty Details - Show only if has warranty */}
                    {editCarData.hasWarranty && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {language === 'ar' ? 'نوع الضمان' : 'Warranty Type'}
                          </label>
                          <select
                            value={editCarData.warrantyType || ''}
                            onChange={(e) => setEditCarData({...editCarData, warrantyType: e.target.value})}
                            className={`w-full px-3 py-2 border rounded-lg text-sm ${
                              isDarkMode 
                                ? 'bg-[#0F172A] border-white/10 text-white' 
                                : 'bg-white border-gray-200'
                            }`}
                          >
                            <option value="">{language === 'ar' ? 'اختر...' : 'Select...'}</option>
                            <option value="full">{language === 'ar' ? 'ضمان شامل' : 'Full Warranty'}</option>
                            <option value="powertrain">{language === 'ar' ? 'ضمان المحرك وناقل الحركة' : 'Powertrain'}</option>
                            <option value="extended">{language === 'ar' ? 'ضمان ممتد' : 'Extended Warranty'}</option>
                            <option value="limited">{language === 'ar' ? 'ضمان محدود' : 'Limited Warranty'}</option>
                          </select>
                        </div>
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {language === 'ar' ? 'مزود الضمان' : 'Warranty Provider'}
                          </label>
                          <Input
                            value={editCarData.warrantyProvider || ''}
                            onChange={(e) => setEditCarData({...editCarData, warrantyProvider: e.target.value})}
                            placeholder={language === 'ar' ? 'مثال: وكالة، جراج...' : 'e.g., Dealer, Garage...'}
                            className={`text-sm ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                /* View Mode */
                <>
                  {/* Car Images */}
                  {(selectedCarItem.image || selectedCarItem.images?.length > 0) && (
                    <div className="grid grid-cols-3 gap-2">
                      {(selectedCarItem.images || [selectedCarItem.image]).filter(Boolean).map((img, idx) => (
                        <img 
                          key={idx}
                          src={img} 
                          alt={`${selectedCarItem.make} ${selectedCarItem.model}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  )}

                  {/* Car Name */}
                  <div className="text-center">
                    <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {selectedCarItem.make} {selectedCarItem.model}
                    </h2>
                    {selectedCarItem.year && (
                      <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedCarItem.year}</p>
                    )}
                  </div>

                  {/* Price */}
                  <div className={`text-center p-4 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                    {selectedCarItem.hasDiscount && selectedCarItem.salePrice ? (
                      <div className="space-y-1">
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                          -{selectedCarItem.discountPercentage}%
                        </span>
                        <p className={`text-sm line-through ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {selectedCarItem.price?.toLocaleString()} AED
                        </p>
                        <p className="text-2xl font-bold text-red-500">
                          {selectedCarItem.salePrice?.toLocaleString()} AED
                        </p>
                      </div>
                    ) : (
                      <p className="text-2xl font-bold text-[#1E9BD9]">
                        {selectedCarItem.price?.toLocaleString()} AED
                      </p>
                    )}
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {selectedCarItem.year && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'سنة الصنع' : 'Year'}
                        </p>
                        <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {selectedCarItem.year}
                        </p>
                      </div>
                    )}
                    {selectedCarItem.mileage && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'المسافة' : 'Mileage'}
                        </p>
                        <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {selectedCarItem.mileage?.toLocaleString()} km
                        </p>
                      </div>
                    )}
                    {selectedCarItem.color && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'اللون' : 'Color'}
                        </p>
                        <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {selectedCarItem.color}
                        </p>
                      </div>
                    )}
                    {selectedCarItem.condition && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'الحالة' : 'Condition'}
                        </p>
                        <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {selectedCarItem.condition === 'new' ? (language === 'ar' ? 'جديد' : 'New') : (language === 'ar' ? 'مستعمل' : 'Used')}
                        </p>
                      </div>
                    )}
                    {selectedCarItem.origin && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'المنشأ' : 'Origin'}
                        </p>
                        <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {selectedCarItem.origin === 'imported' ? (language === 'ar' ? 'وارد' : 'Imported') : (language === 'ar' ? 'خليجي' : 'GCC')}
                        </p>
                      </div>
                    )}
                    {selectedCarItem.fuelType && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'نوع الوقود' : 'Fuel Type'}
                        </p>
                        <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {selectedCarItem.fuelType === 'petrol' ? (language === 'ar' ? 'بنزين' : 'Petrol') : 
                           selectedCarItem.fuelType === 'diesel' ? (language === 'ar' ? 'ديزل' : 'Diesel') :
                           selectedCarItem.fuelType === 'electric' ? (language === 'ar' ? 'كهربائي' : 'Electric') :
                           (language === 'ar' ? 'هايبرد' : 'Hybrid')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {selectedCarItem.description && (
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {language === 'ar' ? 'الوصف' : 'Description'}
                      </p>
                      <p className={`mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {selectedCarItem.description}
                      </p>
                    </div>
                  )}

                  {/* Warranty Info - View Mode */}
                  {selectedCarItem.hasWarranty && (
                    <div className={`p-3 rounded-xl border ${isDarkMode ? 'bg-green-900/20 border-green-500/30' : 'bg-green-50 border-green-200'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-green-400' : 'text-green-700'}`}>
                          {language === 'ar' ? 'مشمول بالضمان' : 'Under Warranty'}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {selectedCarItem.warrantyType && (
                          <div>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {language === 'ar' ? 'نوع الضمان' : 'Type'}
                            </p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {selectedCarItem.warrantyType === 'full' ? (language === 'ar' ? 'ضمان شامل' : 'Full Warranty') :
                               selectedCarItem.warrantyType === 'powertrain' ? (language === 'ar' ? 'ضمان المحرك' : 'Powertrain') :
                               selectedCarItem.warrantyType === 'extended' ? (language === 'ar' ? 'ضمان ممتد' : 'Extended') :
                               (language === 'ar' ? 'ضمان محدود' : 'Limited')}
                            </p>
                          </div>
                        )}
                        {selectedCarItem.warrantyProvider && (
                          <div>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {language === 'ar' ? 'مزود الضمان' : 'Provider'}
                            </p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {selectedCarItem.warrantyProvider}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* No Warranty Badge */}
                  {selectedCarItem.hasWarranty === false && (
                    <div className={`p-3 rounded-xl border ${isDarkMode ? 'bg-red-900/20 border-red-500/30' : 'bg-red-50 border-red-200'}`}>
                      <div className="flex items-center gap-2">
                        <XCircle className="h-4 w-4 text-red-500" />
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>
                          {language === 'ar' ? 'بدون ضمان' : 'No Warranty'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {language === 'ar' ? 'الحالة' : 'Status'}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedCarItem.status === 'sold' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {selectedCarItem.status === 'sold' 
                        ? (language === 'ar' ? 'مباع' : 'Sold')
                        : (language === 'ar' ? 'متاح' : 'Available')
                      }
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Footer Actions */}
            <div className={`p-4 border-t flex gap-2 ${isDarkMode ? 'border-white/10' : ''}`}>
              {isEditingCar ? (
                /* Edit Mode Buttons */
                <>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setIsEditingCar(false);
                      setEditCarData({});
                    }}
                    className="flex-1"
                  >
                    {language === 'ar' ? 'إلغاء' : 'Cancel'}
                  </Button>
                  <Button 
                    onClick={async () => {
                      // Save edited car
                      const token = localStorage.getItem('business_token');
                      try {
                        const formData = new FormData();
                        formData.append('make', editCarData.make || '');
                        formData.append('model', editCarData.model || '');
                        formData.append('price', editCarData.price?.toString() || '0');
                        formData.append('year', editCarData.year || '');
                        formData.append('mileage', editCarData.mileage?.toString() || '');
                        formData.append('color', editCarData.color || '');
                        formData.append('condition', editCarData.condition || '');
                        formData.append('origin', editCarData.origin || '');
                        formData.append('description', editCarData.description || '');
                        
                        // Warranty fields
                        if (editCarData.hasWarranty !== undefined) {
                          formData.append('hasWarranty', editCarData.hasWarranty.toString());
                        }
                        if (editCarData.warrantyType) {
                          formData.append('warrantyType', editCarData.warrantyType);
                        }
                        if (editCarData.warrantyProvider) {
                          formData.append('warrantyProvider', editCarData.warrantyProvider);
                        }
                        
                        // Handle images
                        if (editCarData.images && editCarData.images.length > 0) {
                          editCarData.images.forEach((img, idx) => {
                            if (idx === 0) {
                              formData.append('mainImage', img);
                            } else {
                              formData.append(`extraImage${idx}`, img);
                            }
                          });
                        }
                        
                        const response = await axios.put(
                          `${BACKEND_URL}/api/stores/${store.id}/items/${selectedCarItem.id}`,
                          formData,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        
                        // Update local state
                        setItems(items.map(item => 
                          item.id === selectedCarItem.id 
                            ? { ...item, ...editCarData, image: editCarData.images?.[0], images: editCarData.images }
                            : item
                        ));
                        setSelectedCarItem({ ...selectedCarItem, ...editCarData, image: editCarData.images?.[0], images: editCarData.images });
                        setIsEditingCar(false);
                        setEditCarData({});
                        
                        // Show success toast
                        if (window.toast) {
                          window.toast.success(language === 'ar' ? 'تم تحديث السيارة بنجاح' : 'Car updated successfully');
                        }
                      } catch (error) {
                        console.error('Error updating car:', error);
                        if (window.toast) {
                          window.toast.error(language === 'ar' ? 'حدث خطأ أثناء التحديث' : 'Error updating car');
                        }
                      }
                    }}
                    className="flex-1 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
                  >
                    <Save className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                  </Button>
                </>
              ) : (
                /* View Mode Buttons */
                <>
                  {selectedCarItem.status === 'active' && (
                    <>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setIsEditingCar(true);
                          setEditCarData({
                            ...selectedCarItem,
                            images: selectedCarItem.images || (selectedCarItem.image ? [selectedCarItem.image] : [])
                          });
                        }}
                        className="flex-1 text-[#1E9BD9] border-[#1E9BD9]/50"
                      >
                        <Edit className="h-4 w-4 me-2" />
                        {language === 'ar' ? 'تعديل' : 'Edit'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setShowCarDetailsModal(false);
                          openDiscountModal(selectedCarItem);
                        }}
                        className="flex-1 text-orange-600 border-orange-300"
                      >
                        <Percent className="h-4 w-4 me-2" />
                        {language === 'ar' ? 'خصم' : 'Discount'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          markAsSold(selectedCarItem.id);
                          setShowCarDetailsModal(false);
                        }}
                        className="flex-1 text-green-600 border-green-300"
                      >
                        <CheckCircle className="h-4 w-4 me-2" />
                        {language === 'ar' ? 'تم البيع' : 'Sold'}
                      </Button>
                    </>
                  )}
                  {selectedCarItem.status === 'sold' && (
                    <Button 
                      variant="outline"
                      onClick={() => {
                        returnToSale(selectedCarItem.id);
                        setShowCarDetailsModal(false);
                      }}
                      className="flex-1 text-green-600 border-green-300"
                    >
                      <RotateCcw className="h-4 w-4 me-2" />
                      {language === 'ar' ? 'إعادة للبيع' : 'Return to Sale'}
                    </Button>
                  )}
                  <Button 
                    onClick={() => {
                      setShowCarDetailsModal(false);
                      setSelectedCarItem(null);
                    }}
                    className="flex-1 bg-gray-500 hover:bg-gray-600"
                  >
                    {language === 'ar' ? 'إغلاق' : 'Close'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Real Estate Details/Edit Modal */}
      {showRealEstateDetailsModal && selectedRealEstateItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`w-full max-w-2xl rounded-2xl shadow-2xl my-8 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'}`}>
            {/* Header */}
            <div className={`p-4 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : ''}`}>
              <h3 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                <Building2 className="h-5 w-5 text-[#1E9BD9]" />
                {isEditingRealEstate 
                  ? (language === 'ar' ? 'تعديل العقار' : 'Edit Property')
                  : (language === 'ar' ? 'تفاصيل العقار' : 'Property Details')
                }
              </h3>
              <div className="flex items-center gap-2">
                {!isEditingRealEstate && (
                  <button 
                    onClick={() => {
                      // Go to full-page edit mode
                      setEditingItem(selectedRealEstateItem);
                      setShowEditPropertyPage(true);
                      setShowRealEstateDetailsModal(false);
                    }}
                    className={`p-2 rounded-full text-[#1E9BD9] ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                    title={language === 'ar' ? 'تعديل' : 'Edit'}
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                )}
                <button 
                  onClick={() => {
                    setShowRealEstateDetailsModal(false);
                    setSelectedRealEstateItem(null);
                    setIsEditingRealEstate(false);
                    setEditRealEstateData({});
                  }}
                  className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {isEditingRealEstate ? (
                /* Edit Mode */
                <>
                  {/* Edit Images Section */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'الصور' : 'Images'}
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {(editRealEstateData.images || []).map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img 
                            src={img} 
                            alt={`Image ${idx + 1}`}
                            className="w-full h-20 object-cover rounded-lg"
                          />
                          <button
                            onClick={() => {
                              const newImages = [...(editRealEstateData.images || [])];
                              newImages.splice(idx, 1);
                              setEditRealEstateData({...editRealEstateData, images: newImages});
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      {(editRealEstateData.images?.length || 0) < 10 && (
                        <label className={`w-full h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors ${
                          isDarkMode 
                            ? 'border-white/20 hover:border-white/40 text-gray-400' 
                            : 'border-gray-300 hover:border-gray-400 text-gray-500'
                        }`}>
                          <Plus className="h-5 w-5" />
                          <span className="text-xs mt-1">{language === 'ar' ? 'إضافة' : 'Add'}</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = () => {
                                  const newImages = [...(editRealEstateData.images || []), reader.result];
                                  setEditRealEstateData({...editRealEstateData, images: newImages});
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Edit Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'عنوان العقار' : 'Property Title'}
                      </label>
                      <Input
                        value={editRealEstateData.title || ''}
                        onChange={(e) => setEditRealEstateData({...editRealEstateData, title: e.target.value})}
                        className={isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'السعر' : 'Price'}
                      </label>
                      <Input
                        type="number" inputMode="decimal"
                        value={editRealEstateData.price || ''}
                        onChange={(e) => setEditRealEstateData({...editRealEstateData, price: parseFloat(e.target.value)})}
                        className={isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'نوع العرض' : 'Listing Type'}
                      </label>
                      <select
                        value={editRealEstateData.listingType || ''}
                        onChange={(e) => setEditRealEstateData({...editRealEstateData, listingType: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                      >
                        <option value="for_sale">{language === 'ar' ? 'للبيع' : 'For Sale'}</option>
                        <option value="for_rent">{language === 'ar' ? 'للإيجار' : 'For Rent'}</option>
                        <option value="for_investment">{language === 'ar' ? 'للاستثمار' : 'For Investment'}</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'نوع العقار' : 'Property Type'}
                      </label>
                      <select
                        value={editRealEstateData.propertyType || ''}
                        onChange={(e) => setEditRealEstateData({...editRealEstateData, propertyType: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                      >
                        <option value="apartment">{language === 'ar' ? 'شقة' : 'Apartment'}</option>
                        <option value="villa">{language === 'ar' ? 'فيلا' : 'Villa'}</option>
                        <option value="townhouse">{language === 'ar' ? 'تاون هاوس' : 'Townhouse'}</option>
                        <option value="penthouse">{language === 'ar' ? 'بنتهاوس' : 'Penthouse'}</option>
                        <option value="office">{language === 'ar' ? 'مكتب' : 'Office'}</option>
                        <option value="shop">{language === 'ar' ? 'محل' : 'Shop'}</option>
                        <option value="warehouse">{language === 'ar' ? 'مستودع' : 'Warehouse'}</option>
                        <option value="land">{language === 'ar' ? 'أرض' : 'Land'}</option>
                      </select>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'عدد الغرف' : 'Bedrooms'}
                      </label>
                      <Input
                        type="number" inputMode="decimal"
                        value={editRealEstateData.bedrooms || ''}
                        onChange={(e) => setEditRealEstateData({...editRealEstateData, bedrooms: parseInt(e.target.value)})}
                        className={isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'الحمامات' : 'Bathrooms'}
                      </label>
                      <Input
                        type="number" inputMode="decimal"
                        value={editRealEstateData.bathrooms || ''}
                        onChange={(e) => setEditRealEstateData({...editRealEstateData, bathrooms: parseInt(e.target.value)})}
                        className={isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'المساحة (sqft)' : 'Area (sqft)'}
                      </label>
                      <Input
                        type="number" inputMode="decimal"
                        value={editRealEstateData.area || ''}
                        onChange={(e) => setEditRealEstateData({...editRealEstateData, area: parseInt(e.target.value)})}
                        className={isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'الموقع' : 'Location'}
                      </label>
                      <Input
                        value={editRealEstateData.location || ''}
                        onChange={(e) => setEditRealEstateData({...editRealEstateData, location: e.target.value})}
                        className={isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}
                      />
                    </div>
                  </div>

                  {/* Google Map Picker */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'تحديد الموقع على الخريطة' : 'Select Location on Map'}
                    </label>
                    <GoogleMapPicker
                      isDarkMode={isDarkMode}
                      language={language}
                      initialLocation={editRealEstateData.latitude && editRealEstateData.longitude ? {
                        lat: parseFloat(editRealEstateData.latitude),
                        lng: parseFloat(editRealEstateData.longitude)
                      } : null}
                      onLocationSelect={(locationData) => {
                        if (locationData) {
                          setEditRealEstateData({
                            ...editRealEstateData,
                            latitude: locationData.lat,
                            longitude: locationData.lng,
                            location: locationData.address || editRealEstateData.location
                          });
                        } else {
                          setEditRealEstateData({
                            ...editRealEstateData,
                            latitude: null,
                            longitude: null
                          });
                        }
                      }}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'الوصف' : 'Description'}
                    </label>
                    <textarea
                      value={editRealEstateData.description || ''}
                      onChange={(e) => setEditRealEstateData({...editRealEstateData, description: e.target.value})}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                    />
                  </div>
                </>
              ) : (
                /* View Mode */
                <>
                  {/* Property Images */}
                  {(selectedRealEstateItem.image || selectedRealEstateItem.images?.length > 0) && (
                    <div className="grid grid-cols-3 gap-2">
                      {(selectedRealEstateItem.images || [selectedRealEstateItem.image]).filter(Boolean).slice(0, 6).map((img, idx) => (
                        <img 
                          key={idx}
                          src={img} 
                          alt={selectedRealEstateItem.title}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  )}

                  {/* Property Title */}
                  <div className="text-center">
                    <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {selectedRealEstateItem.title}
                    </h2>
                    {selectedRealEstateItem.location && (
                      <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>📍 {selectedRealEstateItem.location}</p>
                    )}
                  </div>

                  {/* Listing Type Badge */}
                  <div className="flex justify-center">
                    <span className={`px-4 py-1 rounded-full text-sm font-bold ${
                      selectedRealEstateItem.listingType === 'for_sale' 
                        ? 'bg-green-100 text-green-700' 
                        : selectedRealEstateItem.listingType === 'for_rent'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {selectedRealEstateItem.listingType === 'for_sale' 
                        ? (language === 'ar' ? 'للبيع' : 'For Sale') 
                        : selectedRealEstateItem.listingType === 'for_rent'
                        ? (language === 'ar' ? 'للإيجار' : 'For Rent')
                        : (language === 'ar' ? 'للاستثمار' : 'For Investment')}
                    </span>
                  </div>

                  {/* Price */}
                  <div className={`text-center p-4 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                    {selectedRealEstateItem.hasDiscount && selectedRealEstateItem.salePrice ? (
                      <div className="space-y-1">
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                          -{selectedRealEstateItem.discountPercentage}%
                        </span>
                        <p className={`text-sm line-through ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {selectedRealEstateItem.price?.toLocaleString()} AED
                        </p>
                        <p className="text-2xl font-bold text-red-500">
                          {selectedRealEstateItem.salePrice?.toLocaleString()} AED
                          {selectedRealEstateItem.listingType === 'for_rent' && <span className="text-sm font-normal"> / {language === 'ar' ? 'سنوياً' : 'year'}</span>}
                        </p>
                      </div>
                    ) : (
                      <p className="text-2xl font-bold text-[#1E9BD9]">
                        {selectedRealEstateItem.price?.toLocaleString()} AED
                        {selectedRealEstateItem.listingType === 'for_rent' && <span className="text-sm font-normal"> / {language === 'ar' ? 'سنوياً' : 'year'}</span>}
                      </p>
                    )}
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {selectedRealEstateItem.propertyType && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'نوع العقار' : 'Property Type'}
                        </p>
                        <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {selectedRealEstateItem.propertyType === 'apartment' ? (language === 'ar' ? 'شقة' : 'Apartment') :
                           selectedRealEstateItem.propertyType === 'villa' ? (language === 'ar' ? 'فيلا' : 'Villa') :
                           selectedRealEstateItem.propertyType === 'townhouse' ? (language === 'ar' ? 'تاون هاوس' : 'Townhouse') :
                           selectedRealEstateItem.propertyType === 'penthouse' ? (language === 'ar' ? 'بنتهاوس' : 'Penthouse') :
                           selectedRealEstateItem.propertyType === 'office' ? (language === 'ar' ? 'مكتب' : 'Office') :
                           selectedRealEstateItem.propertyType === 'shop' ? (language === 'ar' ? 'محل' : 'Shop') :
                           selectedRealEstateItem.propertyType === 'warehouse' ? (language === 'ar' ? 'مستودع' : 'Warehouse') :
                           selectedRealEstateItem.propertyType === 'land' ? (language === 'ar' ? 'أرض' : 'Land') :
                           selectedRealEstateItem.propertyType}
                        </p>
                      </div>
                    )}
                    {selectedRealEstateItem.bedrooms && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'غرف النوم' : 'Bedrooms'}
                        </p>
                        <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {selectedRealEstateItem.bedrooms}
                        </p>
                      </div>
                    )}
                    {selectedRealEstateItem.bathrooms && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'الحمامات' : 'Bathrooms'}
                        </p>
                        <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {selectedRealEstateItem.bathrooms}
                        </p>
                      </div>
                    )}
                    {selectedRealEstateItem.area && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'المساحة' : 'Area'}
                        </p>
                        <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {selectedRealEstateItem.area} sqft
                        </p>
                      </div>
                    )}
                    {selectedRealEstateItem.furnished && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'التأثيث' : 'Furnished'}
                        </p>
                        <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {selectedRealEstateItem.furnished === 'furnished' ? (language === 'ar' ? 'مفروش' : 'Furnished') :
                           selectedRealEstateItem.furnished === 'semi_furnished' ? (language === 'ar' ? 'نصف مفروش' : 'Semi-Furnished') :
                           (language === 'ar' ? 'غير مفروش' : 'Unfurnished')}
                        </p>
                      </div>
                    )}
                    {selectedRealEstateItem.parking && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'مواقف السيارات' : 'Parking'}
                        </p>
                        <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {selectedRealEstateItem.parking}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {selectedRealEstateItem.description && (
                    <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {language === 'ar' ? 'الوصف' : 'Description'}
                      </p>
                      <p className={`mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {selectedRealEstateItem.description}
                      </p>
                    </div>
                  )}

                  {/* Map Display - Show if coordinates exist */}
                  {selectedRealEstateItem.latitude && selectedRealEstateItem.longitude && (
                    <div>
                      <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                        {language === 'ar' ? 'الموقع على الخريطة' : 'Location on Map'}
                      </p>
                      <GoogleMapPicker
                        isDarkMode={isDarkMode}
                        language={language}
                        readOnly={true}
                        initialLocation={{
                          lat: parseFloat(selectedRealEstateItem.latitude),
                          lng: parseFloat(selectedRealEstateItem.longitude)
                        }}
                      />
                    </div>
                  )}

                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {language === 'ar' ? 'الحالة' : 'Status'}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      selectedRealEstateItem.status === 'sold' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {selectedRealEstateItem.status === 'sold' 
                        ? (language === 'ar' ? 'مباع' : 'Sold')
                        : (language === 'ar' ? 'متاح' : 'Available')
                      }
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Footer Actions */}
            <div className={`p-4 border-t flex gap-2 ${isDarkMode ? 'border-white/10' : ''}`}>
              {isEditingRealEstate ? (
                /* Edit Mode Buttons */
                <>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setIsEditingRealEstate(false);
                      setEditRealEstateData({});
                    }}
                    className="flex-1"
                  >
                    {language === 'ar' ? 'إلغاء' : 'Cancel'}
                  </Button>
                  <Button 
                    onClick={async () => {
                      const token = localStorage.getItem('business_token');
                      try {
                        const formData = new FormData();
                        formData.append('title', editRealEstateData.title || '');
                        formData.append('price', editRealEstateData.price?.toString() || '0');
                        formData.append('listingType', editRealEstateData.listingType || '');
                        formData.append('propertyType', editRealEstateData.propertyType || '');
                        formData.append('bedrooms', editRealEstateData.bedrooms?.toString() || '');
                        formData.append('bathrooms', editRealEstateData.bathrooms?.toString() || '');
                        formData.append('area', editRealEstateData.area?.toString() || '');
                        formData.append('location', editRealEstateData.location || '');
                        formData.append('description', editRealEstateData.description || '');
                        
                        // Add map coordinates
                        if (editRealEstateData.latitude) {
                          formData.append('latitude', editRealEstateData.latitude.toString());
                        }
                        if (editRealEstateData.longitude) {
                          formData.append('longitude', editRealEstateData.longitude.toString());
                        }
                        
                        if (editRealEstateData.images && editRealEstateData.images.length > 0) {
                          editRealEstateData.images.forEach((img, idx) => {
                            if (idx === 0) {
                              formData.append('mainImage', img);
                            } else {
                              formData.append(`extraImage${idx}`, img);
                            }
                          });
                        }
                        
                        await axios.put(
                          `${BACKEND_URL}/api/stores/${store.id}/items/${selectedRealEstateItem.id}`,
                          formData,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        
                        setItems(items.map(item => 
                          item.id === selectedRealEstateItem.id 
                            ? { ...item, ...editRealEstateData, image: editRealEstateData.images?.[0], images: editRealEstateData.images }
                            : item
                        ));
                        setSelectedRealEstateItem({ ...selectedRealEstateItem, ...editRealEstateData, image: editRealEstateData.images?.[0], images: editRealEstateData.images });
                        setIsEditingRealEstate(false);
                        setEditRealEstateData({});
                        
                        if (window.toast) {
                          window.toast.success(language === 'ar' ? 'تم تحديث العقار بنجاح' : 'Property updated successfully');
                        }
                      } catch (error) {
                        console.error('Error updating property:', error);
                        if (window.toast) {
                          window.toast.error(language === 'ar' ? 'حدث خطأ أثناء التحديث' : 'Error updating property');
                        }
                      }
                    }}
                    className="flex-1 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
                  >
                    <Save className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                  </Button>
                </>
              ) : (
                /* View Mode Buttons */
                <>
                  {selectedRealEstateItem.status === 'active' && (
                    <>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setIsEditingRealEstate(true);
                          setEditRealEstateData({
                            ...selectedRealEstateItem,
                            images: selectedRealEstateItem.images || (selectedRealEstateItem.image ? [selectedRealEstateItem.image] : [])
                          });
                        }}
                        className="flex-1 text-[#1E9BD9] border-[#1E9BD9]/50"
                      >
                        <Edit className="h-4 w-4 me-2" />
                        {language === 'ar' ? 'تعديل' : 'Edit'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setShowRealEstateDetailsModal(false);
                          openDiscountModal(selectedRealEstateItem);
                        }}
                        className="flex-1 text-orange-600 border-orange-300"
                      >
                        <Percent className="h-4 w-4 me-2" />
                        {language === 'ar' ? 'خصم' : 'Discount'}
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          markAsSold(selectedRealEstateItem.id);
                          setShowRealEstateDetailsModal(false);
                        }}
                        className="flex-1 text-green-600 border-green-300"
                      >
                        <CheckCircle className="h-4 w-4 me-2" />
                        {language === 'ar' ? 'تم البيع' : 'Sold'}
                      </Button>
                    </>
                  )}
                  {selectedRealEstateItem.status === 'sold' && (
                    <Button 
                      variant="outline"
                      onClick={() => {
                        returnToSale(selectedRealEstateItem.id);
                        setShowRealEstateDetailsModal(false);
                      }}
                      className="flex-1 text-green-600 border-green-300"
                    >
                      <RotateCcw className="h-4 w-4 me-2" />
                      {language === 'ar' ? 'إعادة للبيع' : 'Return to Sale'}
                    </Button>
                  )}
                  <Button 
                    onClick={() => {
                      setShowRealEstateDetailsModal(false);
                      setSelectedRealEstateItem(null);
                    }}
                    className="flex-1 bg-gray-500 hover:bg-gray-600"
                  >
                    {language === 'ar' ? 'إغلاق' : 'Close'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* E-commerce Product Details/Edit Modal */}
      {showProductDetailsModal && selectedProductItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`w-full max-w-2xl rounded-2xl shadow-2xl my-8 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'}`}>
            {/* Header */}
            <div className={`p-4 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : ''}`}>
              <h3 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                <Package className="h-5 w-5 text-[#1E9BD9]" />
                {isEditingProduct 
                  ? (language === 'ar' ? 'تعديل المنتج' : 'Edit Product')
                  : (language === 'ar' ? 'تفاصيل المنتج' : 'Product Details')
                }
              </h3>
              <button 
                onClick={() => {
                  setShowProductDetailsModal(false);
                  setSelectedProductItem(null);
                  setIsEditingProduct(false);
                  setEditProductData({});
                  setEditProductImages([]);
                }}
                className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              >
                <X className={`h-5 w-5 ${isDarkMode ? 'text-white' : ''}`} />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
              {isEditingProduct ? (
                /* Edit Mode */
                <>
                  {/* Images Section */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'صور المنتج' : 'Product Images'}
                    </label>
                    <div className="grid grid-cols-4 gap-3">
                      {editProductImages.map((img, idx) => (
                        <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border">
                          <img 
                            src={typeof img === 'string' ? img : URL.createObjectURL(img)} 
                            alt="" 
                            className="w-full h-full object-cover" 
                          />
                          <button
                            onClick={() => setEditProductImages(editProductImages.filter((_, i) => i !== idx))}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                      {editProductImages.length < 4 && (
                        <label className={`aspect-square rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer ${isDarkMode ? 'border-white/20 hover:border-[#1E9BD9]' : 'border-gray-200 hover:border-[#1E9BD9]'}`}>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = (ev) => {
                                  setEditProductImages([...editProductImages, ev.target.result]);
                                };
                                reader.readAsDataURL(file);
                              }
                              e.target.value = '';
                            }}
                          />
                          <Plus className={`h-6 w-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Name */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'اسم المنتج' : 'Product Name'} *
                    </label>
                    <input
                      type="text"
                      value={editProductData.name || ''}
                      onChange={(e) => setEditProductData({...editProductData, name: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                    />
                  </div>

                  {/* Price */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'السعر (درهم)' : 'Price (AED)'} *
                    </label>
                    <input
                      type="number" inputMode="decimal"
                      value={editProductData.price || ''}
                      onChange={(e) => setEditProductData({...editProductData, price: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                    />
                  </div>

                  {/* Quantity */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'الكمية' : 'Quantity'}
                    </label>
                    <input
                      type="number" inputMode="decimal"
                      value={editProductData.quantity || ''}
                      onChange={(e) => setEditProductData({...editProductData, quantity: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'الوصف' : 'Description'}
                    </label>
                    <textarea
                      value={editProductData.description || ''}
                      onChange={(e) => setEditProductData({...editProductData, description: e.target.value})}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                    />
                  </div>

                  {/* Category Selection - Only show if categories exist */}
                  {categories.length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {language === 'ar' ? 'الفئة الرئيسية' : 'Main Category'}
                        </label>
                        <select
                          value={editProductData.category_id || ''}
                          onChange={(e) => setEditProductData({...editProductData, category_id: e.target.value, subcategory_id: ''})}
                          className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                        >
                          <option value="">{language === 'ar' ? 'اختر الفئة...' : 'Select category...'}</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {language === 'ar' ? cat.name : cat.nameEn}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {language === 'ar' ? 'الفئة الفرعية' : 'Sub Category'}
                        </label>
                        <select
                          value={editProductData.subcategory_id || ''}
                          onChange={(e) => setEditProductData({...editProductData, subcategory_id: e.target.value})}
                          className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                          disabled={!editProductData.category_id}
                        >
                          <option value="">{language === 'ar' ? 'اختر الفئة الفرعية...' : 'Select subcategory...'}</option>
                          {editProductData.category_id && categories.find(c => c.id === editProductData.category_id)?.subcategories?.map((sub) => (
                            <option key={sub.id} value={sub.id}>
                              {language === 'ar' ? sub.name : sub.nameEn}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {/* Status Selection */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'الحالة' : 'Status'}
                    </label>
                    <select
                      value={editProductData.status || 'active'}
                      onChange={(e) => setEditProductData({...editProductData, status: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg ${isDarkMode ? 'bg-[#0F172A] border-white/10 text-white' : ''}`}
                    >
                      <option value="active">{language === 'ar' ? 'متوفر' : 'In Stock'}</option>
                      <option value="out_of_stock">{language === 'ar' ? 'نفذت الكمية' : 'Out of Stock'}</option>
                      <option value="hidden">{language === 'ar' ? 'مخفي' : 'Hidden'}</option>
                    </select>
                  </div>
                </>
              ) : (
                /* View Mode */
                <>
                  {/* Main Image */}
                  <div className="relative rounded-xl overflow-hidden">
                    {selectedProductItem.image || (selectedProductItem.images && selectedProductItem.images.length > 0) ? (
                      <img 
                        src={selectedProductItem.images?.[0] || selectedProductItem.image} 
                        alt={selectedProductItem.name} 
                        className="w-full h-64 object-cover"
                      />
                    ) : (
                      <div className={`w-full h-64 flex items-center justify-center ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-100'}`}>
                        <Package className={`h-20 w-20 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                      </div>
                    )}
                    {selectedProductItem.hasDiscount && (
                      <div className="absolute top-3 left-3 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        -{selectedProductItem.discountPercentage}%
                      </div>
                    )}
                  </div>

                  {/* Image Gallery */}
                  {selectedProductItem.images && selectedProductItem.images.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {selectedProductItem.images.map((img, idx) => (
                        <img key={idx} src={img} alt="" className="h-16 w-16 object-cover rounded-lg flex-shrink-0" />
                      ))}
                    </div>
                  )}

                  {/* Title & Category */}
                  <div>
                    <h4 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {selectedProductItem.name}
                    </h4>
                    {selectedProductItem.category_name && (
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {selectedProductItem.category_name}
                        {selectedProductItem.subcategory_name && ` > ${selectedProductItem.subcategory_name}`}
                      </p>
                    )}
                  </div>

                  {/* Price */}
                  <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                    {selectedProductItem.hasDiscount ? (
                      <div className="flex items-center gap-3">
                        <span className={`text-lg line-through ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          AED {selectedProductItem.price?.toLocaleString()}
                        </span>
                        <span className="text-2xl font-bold text-red-500">
                          AED {selectedProductItem.salePrice?.toLocaleString()}
                        </span>
                      </div>
                    ) : (
                      <span className="text-2xl font-bold text-[#1E9BD9]">
                        AED {selectedProductItem.price?.toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-2 gap-3">
                    {selectedProductItem.quantity !== undefined && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {language === 'ar' ? 'الكمية المتوفرة' : 'Available Quantity'}
                        </p>
                        <p className={`font-bold mt-1 ${selectedProductItem.quantity > 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {selectedProductItem.quantity > 0 ? selectedProductItem.quantity : (language === 'ar' ? 'نفذت الكمية' : 'Out of Stock')}
                        </p>
                      </div>
                    )}
                    {selectedProductItem.sku && (
                      <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>SKU</p>
                        <p className={`font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{selectedProductItem.sku}</p>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {selectedProductItem.description && (
                    <div>
                      <h5 className={`text-sm font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                        {language === 'ar' ? 'الوصف' : 'Description'}
                      </h5>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {selectedProductItem.description}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer Buttons */}
            <div className={`p-4 border-t flex gap-2 ${isDarkMode ? 'border-white/10' : ''}`}>
              {isEditingProduct ? (
                <>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setIsEditingProduct(false);
                      setEditProductData({
                        name: selectedProductItem.name || '',
                        nameEn: selectedProductItem.nameEn || '',
                        price: selectedProductItem.price || '',
                        description: selectedProductItem.description || '',
                        quantity: selectedProductItem.quantity || '',
                        category_id: selectedProductItem.category_id || '',
                        subcategory_id: selectedProductItem.subcategory_id || '',
                        status: selectedProductItem.status || 'active'
                      });
                      setEditProductImages(selectedProductItem.images || (selectedProductItem.image ? [selectedProductItem.image] : []));
                    }}
                    className="flex-1"
                  >
                    {language === 'ar' ? 'إلغاء' : 'Cancel'}
                  </Button>
                  <Button 
                    disabled={savingProduct || !editProductData.name || !editProductData.price}
                    onClick={async () => {
                      try {
                        setSavingProduct(true);
                        const token = localStorage.getItem('business_token');
                        
                        const formData = new FormData();
                        formData.append('name', editProductData.name);
                        formData.append('price', editProductData.price);
                        formData.append('description', editProductData.description || '');
                        formData.append('quantity', editProductData.quantity || '0');
                        
                        // Add category and status fields
                        if (editProductData.category_id) {
                          formData.append('category_id', editProductData.category_id);
                        }
                        if (editProductData.subcategory_id) {
                          formData.append('subcategory_id', editProductData.subcategory_id);
                        }
                        if (editProductData.status) {
                          formData.append('status', editProductData.status);
                        }
                        
                        // Separate existing URLs from new base64 images
                        const existingUrls = [];
                        const newImages = [];
                        
                        editProductImages.forEach((img) => {
                          if (typeof img === 'string') {
                            if (img.startsWith('data:')) {
                              // New base64 image
                              newImages.push(img);
                            } else {
                              // Existing URL
                              existingUrls.push(img);
                            }
                          }
                        });
                        
                        // Send existing URLs as JSON array
                        if (existingUrls.length > 0) {
                          formData.append('existingImages', JSON.stringify(existingUrls));
                        }
                        
                        // Send new images
                        newImages.forEach((img, idx) => {
                          if (idx === 0 && existingUrls.length === 0) {
                            formData.append('mainImage', img);
                          } else {
                            formData.append(`extraImage${idx + 1}`, img);
                          }
                        });
                        
                        await axios.put(
                          `${BACKEND_URL}/api/stores/${store.id}/items/${selectedProductItem.id}`,
                          formData,
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        
                        // Update local state
                        const updatedItem = {
                          ...selectedProductItem,
                          ...editProductData,
                          image: editProductImages[0] || null,
                          images: editProductImages.length > 0 ? editProductImages : null
                        };
                        
                        setItems(items.map(item => 
                          item.id === selectedProductItem.id ? updatedItem : item
                        ));
                        setSelectedProductItem(updatedItem);
                        setIsEditingProduct(false);
                        
                        toast.success(language === 'ar' ? 'تم تحديث المنتج بنجاح' : 'Product updated successfully');
                      } catch (error) {
                        console.error('Error updating product:', error);
                        toast.error(language === 'ar' ? 'حدث خطأ أثناء التحديث' : 'Error updating product');
                      } finally {
                        setSavingProduct(false);
                      }
                    }}
                    className="flex-1 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
                  >
                    {savingProduct ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : <Save className="h-4 w-4 me-2" />}
                    {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      // Go to full-page edit mode
                      setEditingItem(selectedProductItem);
                      setShowEditProductPage(true);
                      setShowProductDetailsModal(false);
                      setSelectedProductItem(null);
                    }}
                    className="flex-1 text-[#1E9BD9] border-[#1E9BD9]"
                  >
                    <Edit2 className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'تعديل' : 'Edit'}
                  </Button>
                  {selectedProductItem.status !== 'sold' && (
                    <>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setShowProductDetailsModal(false);
                          openDiscountModal(selectedProductItem);
                        }}
                        className="flex-1 text-orange-600 border-orange-300"
                      >
                        <Percent className="h-4 w-4 me-2" />
                        {language === 'ar' ? 'خصم' : 'Discount'}
                      </Button>
                    </>
                  )}
                  <Button 
                    onClick={() => {
                      setShowProductDetailsModal(false);
                      setSelectedProductItem(null);
                    }}
                    className="flex-1 bg-gray-500 hover:bg-gray-600"
                  >
                    {language === 'ar' ? 'إغلاق' : 'Close'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Discount Modal */}
      {showDiscountModal && discountItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-2xl shadow-2xl ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'}`}>
            {/* Header */}
            <div className={`p-4 border-b flex items-center justify-between ${isDarkMode ? 'border-white/10' : ''}`}>
              <h3 className={`text-lg font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                <Percent className="h-5 w-5 text-[#1E9BD9]" />
                {language === 'ar' ? 'إضافة خصم' : 'Add Discount'}
              </h3>
              <button 
                onClick={() => {
                  setShowDiscountModal(false);
                  setDiscountItem(null);
                  setDiscountError('');
                }}
                className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
              >
                <X className={`h-5 w-5 ${isDarkMode ? 'text-white' : ''}`} />
              </button>
            </div>

            {/* Content */}
            <div className={`p-6 space-y-4 ${isDarkMode ? 'bg-[#1E293B]' : ''}`}>
              {/* Original Price Display */}
              <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-[#0F172A]' : 'bg-gray-50'}`}>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {language === 'ar' ? 'السعر الأصلي' : 'Original Price'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {discountItem.price?.toLocaleString('en-US')}
                  </span>
                  <img 
                    src={isDarkMode ? '/currency-aed-white.png' : '/currency-aed-black.png'} 
                    alt="AED" 
                    className="h-6 w-auto"
                  />
                </div>
              </div>

              {/* Discount Type Selection */}
              <div>
                <Label className={`mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                  {language === 'ar' ? 'طريقة الخصم' : 'Discount Method'}
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setDiscountType('percentage');
                      setDiscountNewPrice('');
                      setDiscountError('');
                    }}
                    className={`py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all ${
                      discountType === 'percentage'
                        ? 'border-[#1E9BD9] bg-[#1E9BD9]/20 text-[#1E9BD9]'
                        : isDarkMode 
                          ? 'border-gray-600 text-gray-400 hover:border-gray-500' 
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <Percent className="h-5 w-5 mx-auto mb-1" />
                    {language === 'ar' ? 'نسبة الخصم' : 'Percentage'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDiscountType('fixed');
                      setDiscountPercentage('');
                      setDiscountError('');
                    }}
                    className={`py-3 px-4 rounded-xl border-2 text-sm font-medium transition-all ${
                      discountType === 'fixed'
                        ? 'border-[#1E9BD9] bg-[#1E9BD9]/20 text-[#1E9BD9]'
                        : isDarkMode 
                          ? 'border-gray-600 text-gray-400 hover:border-gray-500' 
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <DollarSign className="h-5 w-5 mx-auto mb-1" />
                    {language === 'ar' ? 'سعر جديد' : 'New Price'}
                  </button>
                </div>
              </div>

              {/* Input Fields */}
              {discountType === 'percentage' ? (
                <div>
                  <Label className={`mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                    {language === 'ar' ? 'نسبة الخصم (%)' : 'Discount Percentage (%)'}
                  </Label>
                  <div className="relative">
                    <Input
                      type="number" inputMode="decimal"
                      value={discountPercentage}
                      onChange={(e) => handleDiscountPercentageChange(e.target.value)}
                      placeholder="10"
                      min="1"
                      max="99"
                      className={`text-lg pe-12 ${isDarkMode ? 'bg-[#0F172A] border-gray-600 text-white' : ''}`}
                    />
                    <div className="absolute inset-y-0 end-3 flex items-center pointer-events-none">
                      <Percent className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <Label className={`mb-2 block ${isDarkMode ? 'text-gray-300' : ''}`}>
                    {language === 'ar' ? 'السعر الجديد' : 'New Price'}
                  </Label>
                  <div className="relative">
                    <Input
                      type="number" inputMode="decimal"
                      value={discountNewPrice}
                      onChange={(e) => handleDiscountNewPriceChange(e.target.value)}
                      placeholder="0"
                      min="1"
                      className={`text-lg pe-12 ${isDarkMode ? 'bg-[#0F172A] border-gray-600 text-white' : ''}`}
                    />
                    <div className="absolute inset-y-0 end-3 flex items-center pointer-events-none">
                      <img 
                        src={isDarkMode ? '/currency-aed-white.png' : '/currency-aed-black.png'} 
                        alt="AED" 
                        className="h-5 w-auto"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {discountError && (
                <div className="p-3 bg-red-100 border border-red-300 rounded-xl">
                  <p className="text-sm text-red-700 text-center font-medium">{discountError}</p>
                </div>
              )}

              {/* Result Preview */}
              {!discountError && discountNewPrice && discountPercentage && (
                <div className={`p-4 rounded-xl border-2 border-dashed ${isDarkMode ? 'border-green-500/50 bg-green-500/10' : 'border-green-400 bg-green-50'}`}>
                  <p className={`text-sm text-center mb-2 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                    {language === 'ar' ? 'النتيجة بعد الخصم' : 'Result After Discount'}
                  </p>
                  <div className="flex items-center justify-center gap-4">
                    {/* Original Price */}
                    <div className="text-center">
                      <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {language === 'ar' ? 'السعر الأصلي' : 'Original'}
                      </p>
                      <p className={`line-through ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {discountItem.price?.toLocaleString()}
                      </p>
                    </div>
                    <ArrowRight className={`h-5 w-5 ${isDarkMode ? 'text-green-400' : 'text-green-500'}`} />
                    {/* New Price */}
                    <div className="text-center">
                      <p className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                        {language === 'ar' ? 'السعر الجديد' : 'New Price'}
                      </p>
                      <p className={`text-xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                        {parseFloat(discountNewPrice).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <p className={`text-center mt-2 text-sm font-medium ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>
                    {language === 'ar' ? `خصم ${discountPercentage}%` : `${discountPercentage}% OFF`}
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`p-4 border-t flex gap-3 ${isDarkMode ? 'border-white/10 bg-[#0F172A]' : 'bg-gray-50'}`}>
              <Button 
                variant="outline"
                className={`flex-1 ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : ''}`}
                onClick={() => {
                  setShowDiscountModal(false);
                  setDiscountItem(null);
                  setDiscountError('');
                }}
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button 
                className="flex-1 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
                onClick={saveDiscount}
                disabled={!discountNewPrice || !discountPercentage || !!discountError || savingDiscount}
              >
                {savingDiscount ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 me-2" />
                    {language === 'ar' ? 'حفظ الخصم' : 'Save Discount'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
